import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { parsePrice as parsePizzaPrice } from "../components/pizza/PizzaStepEditor";
import PizzaStepEditor, { PriceInput as PizzaPriceInput } from "../components/pizza/PizzaStepEditor";
import PizzaPersonalizationDialog from "../components/pizza/PizzaPersonalizationDialog";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit, ChevronDown, ChevronUp, ChevronRight, ImageOff, Copy, Camera, FileText, Search, X, ChefHat, Package, GripVertical, Settings2, Eye, EyeOff, Layers, ArrowUp, ArrowDown, ArrowUpDown, ArrowRight, SortAsc, SortDesc, Check, RefreshCw, ArrowLeft, Printer, LayoutGrid, LayoutList, SlidersHorizontal, Filter, Sparkles, Store, Info, DollarSign, TrendingUp, Scale, Link2, Circle, BookOpen, MoreVertical, Archive, ArchiveRestore, AlertTriangle, Clock, Pizza, Tag, Receipt, ClipboardList, QrCode, Smartphone, CupSoda, Hamburger } from "lucide-react";
import { CaixaRegistradoraIcon, MesaIcon, MotoEntregaIcon, TotemIcon } from "../components/icons/ModulosIcons";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SecoesUnidade, Secao, SecaoEstoque, ResumoUnidade, paraPayload, minMaxValido } from "../features/estoque-unidade";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { sanitizeCurrencyInput } from "../lib/utils";
import TablePagination from "../components/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { FiscalLock, ProdutoNotaFiscalTab, parseFiscalConfig, produtoPronto } from "../features/fiscal";
import { PizzaFichaTab, adicionarIngrediente as adicionarNaFichaPizza } from "../features/pizza-ficha";
import { CampoTempoProducao, rotuloTempoSetor, tempoParaSalvar } from "../features/setor-tempo";
import { CampoCodigoBarras, limparCodigo } from "../features/produto-codigo-barras";
import { ProdutosCarregando } from "../features/cartoes-carregando";
import { CarregandoLista } from "../components/carregando";
import { Skeleton } from "../components/ui/skeleton";
import { InputDecimal } from "../components/ui/input-decimal";
import { PlanilhaCardapio } from "../features/cardapio-planilha";
import { CardPrecificacao } from "../features/precificacao-ficha";
import { FOTO_ACEITA, MIDIAS_VAZIAS, MidiaVazia, MidiasExtras, NumeroDaMidia, SEM_GIF, VIDEO_ACEITO, ehGif, ehVideo, enviarVideo } from "../features/produto-midias";
import { FotosProntas } from "../features/fotos-padrao";
import { ExcluirCategoriaDialog, ExcluirProdutoDialog } from "../features/excluir-cardapio";
import { FILTROS_VAZIOS, Ordenacao, PainelFiltros, TODOS, aplicarFiltros, criarGrupos, ehDoCardapio, filtrosAtivos } from "../features/produtos-filtros";

// URL relativa - funciona em qualquer domínio
const API = '/api';

// O `detail` de um 422 do FastAPI é uma LISTA de objetos; entregue direto ao
// toast, o React tenta desenhar objeto e a página inteira cai em "Algo deu
// errado" (era o que acontecia ao digitar a posição de uma categoria).
function mensagemDeErro(error, padrao) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && typeof detail[0]?.msg === "string") return `${padrao}: ${detail[0].msg}`;
  return padrao;
}

// Slug derivado do nome do produto pra link de divulgacao do cardapio publico.
// Mantem em sync com makeProductSlug em CardapioPublico.js.
const makeProductSlug = (name) => {
  if (!name) return "";
  return String(name)
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, "-e-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
};

// Monta a URL de divulgacao do produto baseado no host atual (subdominio do tenant).
// Em dev/localhost, usa o host como esta; em prod, formato e <slug>.usenucleo.com.br/#/cardapio/p/<slug>.
const buildProductPublicUrl = (productName) => {
  const slug = makeProductSlug(productName);
  if (!slug) return "";
  const host = (typeof window !== 'undefined' && window.location?.host) || "";
  const protocol = (typeof window !== 'undefined' && window.location?.protocol) || "https:";
  return `${protocol}//${host}/#/cardapio/p/${slug}`;
};

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Quilo/litro (30/09/2026): o produto é vendido por peso quando a unidade é
// kg/L E "Divisível" está ligado (regra única de lib/unidadeVenda). Escolher
// Quilo ou Litro liga o Divisível sozinho; o lojista ainda pode desligar
// ("Guaraná 2 L" vendido inteiro).
const ehUnidadePeso = (u) => u === "kg" || u === "L";
const AVISO_PESO_SEM_DIVISIVEL = (u) =>
  `${u === "kg" ? "Quilo" : "Litro"} sem "Divisível": será vendido por unidade inteira.`;

function ExplicacaoUnidadeVenda({ unidade, divisivel, className = "" }) {
  if (!ehUnidadePeso(unidade)) return null;
  const medida = unidade === "kg" ? "kg" : "litros";
  return (
    <p className={`text-[11px] leading-tight ${divisivel ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"} ${className}`}>
      {divisivel
        ? `Vendido por peso: o cliente escolhe a quantidade em ${medida}.`
        : `Sem "Divisível": será vendido por unidade inteira (ex.: Guaraná 2 L).`}
    </p>
  );
}

// === Cardápio Profiles (Turnos) — admin section ===
const WEEKDAY_OPTIONS = [
  { value: 'mon', label: 'Seg' },
  { value: 'tue', label: 'Ter' },
  { value: 'wed', label: 'Qua' },
  { value: 'thu', label: 'Qui' },
  { value: 'fri', label: 'Sex' },
  { value: 'sat', label: 'Sáb' },
  { value: 'sun', label: 'Dom' },
];

// Helpers visuais do editor de turno
function SectionHeader({ number, title, subtitle }) {
  return (
    <div className="flex items-start gap-2.5 mb-2.5">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold shrink-0 shadow-sm">
        {number}
      </span>
      <div className="min-w-0 flex-1">
        <h4 className="text-sm font-bold leading-tight">{title}</h4>
        {subtitle && <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function TimeBlock({ label, value, onChange }) {
  // Input nativo de tempo, mas estilizado num bloco maior pra ficar visível
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 text-center">{label}</p>
      <div className="relative">
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-12 text-center text-lg font-bold tabular-nums bg-background border-2 rounded-lg px-2 focus:border-primary focus:outline-none transition-colors"
        />
      </div>
    </div>
  );
}

function PresetChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 h-7 rounded-md text-[11px] font-semibold transition-all ${
        active
          ? 'bg-primary/15 text-primary border border-primary/40'
          : 'bg-muted text-muted-foreground border border-transparent hover:text-foreground hover:bg-muted/80'
      }`}
    >
      {children}
    </button>
  );
}

function CardapioProfilesSection({ categories, tenantApi, authHeader, semCategoriaCount = 0 }) {
  const [profiles, setProfiles] = useState([]);
  const [turnoAtivo, setTurnoAtivo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = novo, ou objeto perfil
  const [confirmDelete, setConfirmDelete] = useState(null);

  const refresh = async () => {
    try {
      const [allRes, activeRes] = await Promise.all([
        axios.get(`${tenantApi}/cardapio-profiles`, authHeader()),
        axios.get(`${tenantApi}/cardapio-profiles/active`, authHeader()).catch(() => ({ data: null })),
      ]);
      setProfiles(allRes.data || []);
      setTurnoAtivo(activeRes.data || null);
    } catch (e) {
      // silencioso — pode ser primeiro acesso
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // O turno vira no relógio, não num clique. Sem este intervalo o badge
  // "ATIVO AGORA" ficaria apontando o turno errado até alguém recarregar a tela.
  useEffect(() => {
    const id = setInterval(() => { refresh(); }, 30000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCreate = () => { setEditing({ name: '', days: ['mon','tue','wed','thu','fri','sat','sun'], from_time: '11:00', to_time: '15:00', categories: [], enabled: true }); setEditorOpen(true); };
  const startEdit = (p) => { setEditing({ ...p }); setEditorOpen(true); };

  const handleSave = async () => {
    // Duplo clique criava DOIS turnos idênticos — que, sobrepostos, somam as
    // mesmas categorias e ainda geram o aviso de sobreposição contra si mesmos.
    if (!editing || saving) return;
    if (!editing.name?.trim()) { toast.error('Nome é obrigatório'); return; }
    if (!editing.days || editing.days.length === 0) { toast.error('Escolha pelo menos um dia'); return; }
    // Turno sem categoria nao faz efeito nenhum (o backend tambem barra com 400).
    if (!editing.categories || editing.categories.length === 0) {
      toast.error('Escolha as categorias que aparecem neste turno');
      return;
    }
    if ((editing.from_time || '00:00') === (editing.to_time || '23:59')) {
      toast.error('Início e fim iguais — o turno nunca valeria. Use 00:00 às 23:59 para o dia inteiro.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        days: editing.days,
        from_time: editing.from_time || '00:00',
        to_time: editing.to_time || '23:59',
        categories: editing.categories || [],
        enabled: editing.enabled !== false,
      };
      let resp;
      if (editing.id) {
        resp = await axios.put(`${tenantApi}/cardapio-profiles/${editing.id}`, payload, authHeader());
        toast.success(`Turno "${payload.name}" atualizado`);
      } else {
        resp = await axios.post(`${tenantApi}/cardapio-profiles`, payload, authHeader());
        toast.success(`Turno "${payload.name}" criado`);
      }
      // Sobreposicao nao bloqueia (turnos sobrepostos somam categorias), mas o
      // lojista precisa saber que somam em vez de trocar.
      if (resp?.data?.aviso) toast(resp.data.aviso, { icon: '⚠️', duration: 6000 });
      setEditorOpen(false);
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(mensagemDeErro(e, 'Erro ao salvar turno'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${tenantApi}/cardapio-profiles/${confirmDelete.id}`, authHeader());
      toast.success(`Turno "${confirmDelete.name}" removido`);
      setConfirmDelete(null);
      refresh();
    } catch (e) {
      toast.error('Erro ao remover turno');
    }
  };

  const handleToggleEnabled = async (p) => {
    try {
      await axios.put(`${tenantApi}/cardapio-profiles/${p.id}`, {
        ...p,
        enabled: !p.enabled,
      }, authHeader());
      refresh();
    } catch (e) {
      toast.error('Erro ao alternar perfil');
    }
  };

  const formatDays = (days) => {
    if (!days || days.length === 0) return '—';
    if (days.length === 7) return 'Todos os dias';
    return WEEKDAY_OPTIONS.filter(d => days.includes(d.value)).map(d => d.label).join(' · ');
  };

  return (
    <div className="bg-card rounded-xl border shadow-sm p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Turnos do Cardápio
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Defina horários e dias para mostrar categorias específicas. Sem turnos ativos, o cardápio padrão (todas as categorias na ordem global) é exibido.
          </p>
          {turnoAtivo && (
            <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1">
              Agora o cliente vê {(turnoAtivo.categorias_nomes || []).length} categoria
              {(turnoAtivo.categorias_nomes || []).length === 1 ? '' : 's'}
              {(turnoAtivo.categorias_nomes || []).length > 0 && `: ${(turnoAtivo.categorias_nomes || []).join(', ')}`}
              {turnoAtivo.volta_em ? ` — muda ${turnoAtivo.volta_em}` : ''}
            </p>
          )}
          {semCategoriaCount > 0 && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
              {semCategoriaCount} produto{semCategoriaCount === 1 ? '' : 's'} sem categoria
              {semCategoriaCount === 1 ? ' aparece' : ' aparecem'} em TODOS os turnos —
              o turno recorta por categoria. Defina uma categoria para que{semCategoriaCount === 1 ? ' ele siga' : ' sigam'} os horários.
            </p>
          )}
        </div>
        <Button size="sm" onClick={startCreate} className="self-start sm:self-auto flex-shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Novo Turno
        </Button>
      </div>

      {loading ? (
        <CarregandoLista linhas={2} avatar={false} colunas={1} className="rounded-lg border overflow-hidden" />
      ) : profiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum turno configurado. O cardápio mostra todas as categorias na ordem global.
        </p>
      ) : (
        <div className="space-y-2">
          {profiles.map(p => {
            const isActive = (turnoAtivo?.ids_vigentes || []).includes(p.id);
            return (
              <div
                key={p.id}
                className={`flex items-center gap-2 sm:gap-3 p-3 rounded-lg border ${
                  isActive
                    ? 'border-emerald-500/50 bg-emerald-500/5'
                    : p.enabled
                      ? 'border-border bg-muted/30'
                      : 'border-border/50 bg-muted/10 opacity-60'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{p.name}</span>
                    {isActive && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500 text-white inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> ATIVO AGORA
                      </span>
                    )}
                    {!p.enabled && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300">desativado</span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.from_time} → {p.to_time}</span>
                    <span>{formatDays(p.days)}</span>
                    <span>{(p.categories || []).length} categoria{(p.categories || []).length === 1 ? '' : 's'}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="h-9 w-9 sm:h-8 sm:w-8 p-0 flex-shrink-0" onClick={() => handleToggleEnabled(p)} title={p.enabled ? 'Desativar' : 'Ativar'} aria-label={p.enabled ? 'Desativar turno' : 'Ativar turno'}>
                  {p.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                </Button>
                <Button variant="ghost" size="sm" className="h-9 w-9 sm:h-8 sm:w-8 p-0 flex-shrink-0" onClick={() => startEdit(p)} title="Editar" aria-label="Editar turno">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-9 w-9 sm:h-8 sm:w-8 p-0 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(p)} title="Excluir" aria-label="Excluir turno">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor — refinado: 3 seções numeradas, presets, resume em tempo real */}
      <Dialog open={editorOpen} onOpenChange={(o) => { setEditorOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0">
          {/* Header com ícone + subtítulo */}
          <DialogHeader className="px-4 sm:px-6 pt-5 pb-4 border-b bg-gradient-to-r from-primary/5 to-transparent text-left">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-lg leading-tight">{editing?.id ? 'Editar Turno' : 'Novo Turno'}</DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Defina nome, horário, dias e quais categorias mostrar quando ele estiver ativo.
                </p>
              </div>
            </div>
          </DialogHeader>

          {editing && (() => {
            // Helpers / derivados
            const days = editing.days || [];
            const cats = editing.categories || [];
            const allCatsAvailable = categories.filter(c => !cats.includes(c.id));
            const dayOptions = WEEKDAY_OPTIONS;
            // Presets de dias
            const setDays = (newDays) => setEditing({ ...editing, days: newDays });
            const setAllDays = () => setDays(dayOptions.map(d => d.value));
            const setWeekdays = () => setDays(['mon','tue','wed','thu','fri']);
            const setWeekend = () => setDays(['sat','sun']);
            const clearDays = () => setDays([]);
            const isAll = days.length === 7;
            const isWeekdays = days.length === 5 && ['mon','tue','wed','thu','fri'].every(d => days.includes(d));
            const isWeekend = days.length === 2 && days.includes('sat') && days.includes('sun');
            // Resume textual pro footer
            const formatDays = () => {
              if (isAll) return 'Todos os dias';
              if (isWeekdays) return 'Seg-Sex';
              if (isWeekend) return 'Sáb-Dom';
              if (days.length === 0) return '—';
              return dayOptions.filter(d => days.includes(d.value)).map(d => d.label).join('·');
            };

            return (
            <div className="px-4 sm:px-6 py-5 space-y-5">

              {/* === ① IDENTIFICAÇÃO === */}
              <section>
                <SectionHeader number="1" title="Identificação" />
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Almoço, Janta, Madrugada…"
                  className="h-11 text-base"
                  autoFocus
                />
              </section>

              {/* === ② QUANDO === */}
              <section>
                <SectionHeader number="2" title="Quando" subtitle="Horário e dias da semana" />

                {/* Horário com visual destacado */}
                <div className="rounded-xl border bg-muted/30 p-4 mb-3">
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <TimeBlock
                      label="Início"
                      value={editing.from_time}
                      onChange={(v) => setEditing({ ...editing, from_time: v })}
                    />
                    <ArrowRight className="w-5 h-5 text-muted-foreground mt-5" />
                    <TimeBlock
                      label="Fim"
                      value={editing.to_time}
                      onChange={(v) => setEditing({ ...editing, to_time: v })}
                    />
                  </div>
                  {editing.from_time > editing.to_time && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Janela cruza meia-noite (ex: 22:00 → 02:00). Suportado.
                    </p>
                  )}
                </div>

                {/* Presets de dias */}
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mr-1">Atalhos:</span>
                  <PresetChip active={isAll}      onClick={setAllDays}>Todos</PresetChip>
                  <PresetChip active={isWeekdays} onClick={setWeekdays}>Seg-Sex</PresetChip>
                  <PresetChip active={isWeekend}  onClick={setWeekend}>Fim de semana</PresetChip>
                  {days.length > 0 && (
                    <button type="button" onClick={clearDays} className="text-[10px] text-muted-foreground hover:text-destructive ml-auto">
                      Limpar
                    </button>
                  )}
                </div>

                {/* Toggles individuais */}
                <div className="grid grid-cols-7 gap-1.5">
                  {dayOptions.map(d => {
                    const active = days.includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => {
                          setEditing({ ...editing, days: active ? days.filter(x => x !== d.value) : [...days, d.value] });
                        }}
                        className={`h-10 rounded-lg text-xs font-bold transition-all ${
                          active
                            ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30 scale-105'
                            : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 hover:scale-105'
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* === ③ CATEGORIAS === */}
              <section>
                <SectionHeader
                  number="3"
                  title="Categorias e ordem"
                  subtitle="Apenas estas aparecem quando o turno está ativo, na ordem definida"
                />

                {/* Lista selecionada */}
                {cats.length === 0 ? (
                  <div className="rounded-xl border-2 border-dashed border-muted-foreground/20 px-4 py-6 text-center">
                    <Layers className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma categoria selecionada</p>
                    <p className="text-[11px] text-muted-foreground/70 mt-0.5">Clique em uma categoria abaixo para adicionar</p>
                  </div>
                ) : (
                  <div className="space-y-1.5 mb-3">
                    {cats.map((catId, idx) => {
                      const cat = categories.find(c => c.id === catId);
                      if (!cat) return null;
                      const total = cats.length;
                      return (
                        <div key={catId} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-primary/5 border border-primary/20 group">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary text-primary-foreground text-xs font-bold shrink-0 shadow-sm">
                            {idx + 1}
                          </span>
                          <span className="font-medium text-sm flex-1 truncate">{cat.name}</span>
                          <div className="flex items-center bg-background rounded-md border shadow-sm">
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 w-8 p-0 rounded-r-none disabled:opacity-25"
                              disabled={idx === 0}
                              onClick={() => {
                                const arr = [...cats];
                                [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]];
                                setEditing({ ...editing, categories: arr });
                              }}
                              title="Subir"
                            >
                              <ChevronUp className="w-4 h-4" strokeWidth={2.5} />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 w-8 p-0 rounded-l-none border-l disabled:opacity-25"
                              disabled={idx === total - 1}
                              onClick={() => {
                                const arr = [...cats];
                                [arr[idx], arr[idx+1]] = [arr[idx+1], arr[idx]];
                                setEditing({ ...editing, categories: arr });
                              }}
                              title="Descer"
                            >
                              <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
                            </Button>
                          </div>
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => {
                              setEditing({ ...editing, categories: cats.filter(id => id !== catId) });
                            }}
                            title="Remover deste turno"
                          >
                            <X className="w-3.5 h-3.5" strokeWidth={2.2} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pool de disponíveis */}
                {allCatsAvailable.length > 0 ? (
                  <div className="rounded-lg border bg-muted/20 p-3">
                    <p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-2 font-semibold">
                      {cats.length > 0 ? 'Adicionar mais' : 'Categorias disponíveis'}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {allCatsAvailable.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setEditing({ ...editing, categories: [...cats, c.id] })}
                          className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-[12px] bg-background border hover:border-primary hover:bg-primary/5 hover:text-primary transition-all active:scale-95"
                        >
                          <Plus className="w-3 h-3" strokeWidth={2.5} />
                          {c.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground italic text-center py-1">
                    ✓ Todas as categorias adicionadas
                  </p>
                )}
              </section>

              {/* Toggle Ativo */}
              <div className="rounded-lg border bg-muted/20 px-3 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="turn-enabled"
                    checked={editing.enabled !== false}
                    onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                    className="rounded h-4 w-4 cursor-pointer"
                  />
                  <label htmlFor="turn-enabled" className="text-sm font-medium cursor-pointer">
                    Turno ativo
                  </label>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {editing.enabled !== false ? 'Será aplicado quando bater o horário' : 'Pausado — não será aplicado'}
                </span>
              </div>
            </div>
            );
          })()}

          {/* Footer com resume + ações */}
          {editing && (() => {
            const days = editing.days || [];
            const cats = editing.categories || [];
            const isAll = days.length === 7;
            const isWeekdays = days.length === 5 && ['mon','tue','wed','thu','fri'].every(d => days.includes(d));
            const isWeekend = days.length === 2 && days.includes('sat') && days.includes('sun');
            const formatDays = () => {
              if (isAll) return 'Todos os dias';
              if (isWeekdays) return 'Seg-Sex';
              if (isWeekend) return 'Sáb-Dom';
              if (days.length === 0) return '—';
              return WEEKDAY_OPTIONS.filter(d => days.includes(d.value)).map(d => d.label).join('·');
            };
            // Categoria entrou no canSave porque o backend rejeita turno sem
            // categoria com 400 — melhor o botão ficar desabilitado do que o
            // lojista clicar e levar erro.
            const canSave = editing.name?.trim() && days.length > 0
              && (editing.categories || []).length > 0
              && (editing.from_time || '00:00') !== (editing.to_time || '23:59');
            return (
              <div className="border-t bg-card px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-[11px] text-muted-foreground min-w-0 flex-1">
                  <span className="font-bold text-foreground">{editing.name?.trim() || 'Sem nome'}</span>
                  <span className="mx-1.5 opacity-50">·</span>
                  <span>{formatDays()}</span>
                  <span className="mx-1.5 opacity-50">·</span>
                  <span>{editing.from_time}→{editing.to_time}</span>
                  <span className="mx-1.5 opacity-50">·</span>
                  <span>{cats.length} categoria{cats.length === 1 ? '' : 's'}</span>
                </div>
                <div className="grid grid-cols-2 sm:flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => { setEditorOpen(false); setEditing(null); }}>Cancelar</Button>
                  <Button size="sm" onClick={handleSave} disabled={!canSave || saving} className="min-w-[90px]">
                    {saving ? 'Salvando…' : (editing.id ? 'Atualizar' : 'Criar Turno')}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Confirmação de delete */}
      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir turno "{confirmDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove o turno e o cardápio voltará ao comportamento padrão nos horários cobertos por ele. Não pode ser desfeito.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Componente de lista de categorias com numeração editável
function CategoryList({ categories, onReorder, onMoveUp, onMoveDown, onEdit, onDelete, setores, onSetorChange, onToggleHidden }) {
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderValue, setOrderValue] = useState("");
  const [movingCategoryId, setMovingCategoryId] = useState(null);

  // Função para reorganizar categorias quando o número é alterado
  const handleOrderChange = (categoryId, newOrder) => {
    const fecharEdicao = () => { setEditingOrder(null); setOrderValue(""); };
    const numOrder = parseInt(newOrder);
    // Antes o return deixava o campo aberto para sempre (o blur caía aqui de novo).
    if (isNaN(numOrder) || numOrder < 1 || numOrder > categories.length) {
      toast.error(`Digite uma posição de 1 a ${categories.length}`);
      fecharEdicao();
      return;
    }

    const currentIndex = categories.findIndex(c => c.id === categoryId);
    if (currentIndex === -1) { fecharEdicao(); return; }

    const targetIndex = numOrder - 1; // Converter para índice (1-based para 0-based)

    if (currentIndex === targetIndex) {
      fecharEdicao(); // Já está na posição correta
      return;
    }

    // Ativar animação de highlight
    setMovingCategoryId(categoryId);
    setTimeout(() => setMovingCategoryId(null), 600);

    // Criar nova ordem
    const newCategories = [...categories];
    const [movedCategory] = newCategories.splice(currentIndex, 1);
    newCategories.splice(targetIndex, 0, movedCategory);

    // Reorganizar display_order de todas as categorias
    const reorderedCategories = newCategories.map((cat, idx) => ({
      ...cat,
      display_order: idx
    }));

    onReorder(reorderedCategories);
    setEditingOrder(null);
  };

  const handleOrderInputKeyDown = (e, categoryId, currentOrder) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOrderChange(categoryId, orderValue);
    } else if (e.key === 'Escape') {
      setEditingOrder(null);
      setOrderValue("");
    }
  };

  const handleOrderInputBlur = (categoryId, currentOrder) => {
    // Se o valor não mudou ou está vazio, apenas cancela a edição
    if (!orderValue || orderValue === currentOrder.toString()) {
      setEditingOrder(null);
      setOrderValue("");
      return;
    }
    // Se o valor mudou, aplica a mudança
    handleOrderChange(categoryId, orderValue);
  };

  return (
    <div className="divide-y divide-border">
      {categories.map((category, index) => {
        const currentOrder = index + 1;
        const isEditing = editingOrder === category.id;
        
        const isMoving = movingCategoryId === category.id;
        
        const isHidden = category.hidden === true;
        return (
          // Celular: ordem | nome | ações na 1ª linha e o setor embaixo. Com as
          // quatro colunas lado a lado, o setor (160px) e as ações ocupavam a
          // largura toda e o NOME da categoria sumia.
          <div
            key={category.id}
            className={`grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_auto_auto] items-center px-3 sm:px-4 py-3 hover:bg-muted/30 transition-all duration-500 ease-in-out gap-x-3 gap-y-2 ${
              isMoving ? 'category-item-moving' : ''
            } ${
              isHidden ? 'opacity-70 bg-zinc-100/40 dark:bg-zinc-800/40 border-l-2 border-l-zinc-400' : ''
            }`}
            style={{ animation: 'slideIn 0.5s ease-out' }}
          >
            {/* Coluna 1: Ordem + Setas */}
            <div className="flex items-center gap-1.5">
              {isEditing ? (
                <Input
                  type="number"
                  min={1}
                  max={categories.length}
                  value={orderValue}
                  onChange={(e) => setOrderValue(e.target.value)}
                  onKeyDown={(e) => handleOrderInputKeyDown(e, category.id, currentOrder)}
                  onBlur={() => handleOrderInputBlur(category.id, currentOrder)}
                  className="w-14 h-10 text-center font-semibold"
                  autoFocus
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-lg border-2 border-primary/20 bg-primary/5 flex items-center justify-center cursor-pointer hover:bg-primary/10 transition-colors shrink-0"
                  onClick={() => {
                    setEditingOrder(category.id);
                    setOrderValue(currentOrder.toString());
                  }}
                  title="Clique para editar a ordem"
                >
                  <span className="text-base font-bold text-primary">{currentOrder}</span>
                </div>
              )}
              <div className="flex flex-col">
                <Button
                  onClick={() => { setMovingCategoryId(category.id); setTimeout(() => setMovingCategoryId(null), 600); onMoveUp(category.id); }}
                  variant="ghost" size="sm" className="h-7 w-7 sm:h-5 sm:w-5 p-0 hover:bg-muted" disabled={index === 0} title="Mover para cima" aria-label="Mover para cima"
                >
                  <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
                <Button
                  onClick={() => { setMovingCategoryId(category.id); setTimeout(() => setMovingCategoryId(null), 600); onMoveDown(category.id); }}
                  variant="ghost" size="sm" className="h-7 w-7 sm:h-5 sm:w-5 p-0 hover:bg-muted" disabled={index === categories.length - 1} title="Mover para baixo" aria-label="Mover para baixo"
                >
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            {/* Coluna 2: Nome + Contagem */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-semibold text-sm sm:text-base break-words sm:truncate min-w-0 ${isHidden ? 'text-muted-foreground line-through decoration-zinc-400/50' : ''}`}>{category.name}</span>
                {isHidden && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300">
                    <EyeOff className="w-3 h-3" /> Escondida
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {category.products_count || 0} produto{(category.products_count || 0) !== 1 ? 's' : ''}
                {(category.archived_count || 0) > 0 && (
                  <span className="text-amber-600 dark:text-amber-400"> · {category.archived_count} arquivado{category.archived_count !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>

            {/* Coluna 3: Setor — no celular vira a 2ª linha, e some quando não há setores */}
            <div className={`col-span-3 sm:col-span-1 order-last sm:order-none w-full sm:w-[160px] ${setores && setores.length > 0 ? '' : 'hidden sm:block'}`}>
              {setores && setores.length > 0 ? (
                <Select
                  value={category.setor_id || "none"}
                  onValueChange={(val) => onSetorChange && onSetorChange(category.id, val === "none" ? null : val)}
                >
                  <SelectTrigger className="h-9 w-full text-xs">
                    <SelectValue placeholder="Sem setor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem setor</SelectItem>
                    {setores.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs text-muted-foreground italic">Sem setores</span>
              )}
            </div>

            {/* Coluna 4: Ações */}
            <div className="flex gap-0.5 sm:gap-1">
              <Button
                onClick={() => onToggleHidden && onToggleHidden(category)}
                variant="ghost" size="sm"
                className={`h-9 w-9 sm:h-8 sm:w-8 p-0 ${isHidden ? 'text-zinc-500 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                title={isHidden ? "Mostrar categoria no cardápio" : "Esconder categoria do cardápio"}
                aria-label={isHidden ? "Mostrar categoria no cardápio" : "Esconder categoria do cardápio"}
              >
                {isHidden ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </Button>
              <Button onClick={() => onEdit(category)} variant="ghost" size="sm" className="h-9 w-9 sm:h-8 sm:w-8 p-0 hover:bg-muted" aria-label="Editar categoria">
                <Edit className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button
                onClick={() => onDelete(category)} variant="ghost" size="sm" aria-label="Excluir categoria"
                className="h-9 w-9 sm:h-8 sm:w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                title={category.products_count > 0
                  ? `Excluir a categoria e os ${category.products_count} produto(s) dela (mostra a lista antes)`
                  : (category.archived_count > 0
                      ? `Excluir (${category.archived_count} produto(s) arquivado(s) ficarão sem categoria)`
                      : "Excluir")}
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Componente Combobox com busca e opção de cadastrar novo ingrediente
function IngredientCombobox({
  ingredients,
  value,
  onChange,
  placeholder = "Buscar ingrediente...",
  onCreateNew,
  getIngredientUnit,
  excludeIds = []
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Encontrar ingrediente selecionado
  const selectedIngredient = ingredients.find(i => i.id === value);

  // Filtrar ingredientes baseado na busca, excluindo os já adicionados
  const excludeSet = useMemo(() => new Set((excludeIds || []).filter(Boolean)), [excludeIds]);
  const filteredIngredients = useMemo(() => {
    // Arquivado nao entra em ficha nova; se ja esta na ficha (value), continua aparecendo
    let list = ingredients.filter(ing => (ing.is_active !== false && !excludeSet.has(ing.id)) || ing.id === value);
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(ing =>
      ing.name.toLowerCase().includes(term) ||
      (ing.code && ing.code.includes(term))
    );
  }, [ingredients, searchTerm, excludeSet, value]);

  // Verificar se a busca não encontrou resultados
  const noResults = searchTerm && filteredIngredients.length === 0;

  // Calcular posição do dropdown baseado no input
  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Atualizar posição ao abrir e no scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    const scrollParent = containerRef.current?.closest('[class*="overflow"]');
    const handleUpdate = () => updateDropdownPosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    if (scrollParent) scrollParent.addEventListener('scroll', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      if (scrollParent) scrollParent.removeEventListener('scroll', handleUpdate);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleSelect = (ingredientId) => {
    onChange(ingredientId);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    onCreateNew(searchTerm);
    setSearchTerm("");
    setIsOpen(false);
  };

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      data-ingredient-dropdown=""
      className="bg-popover border rounded-xl shadow-xl max-h-72 overflow-auto"
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 9999,
        // O portal cai em document.body; dentro de um Radix Dialog modal o body
        // fica com pointer-events:none e o clique na opcao atravessava a lista.
        pointerEvents: 'auto',
      }}
    >
      {filteredIngredients.length > 0 && (
        <div className="py-1">
          {filteredIngredients.slice(0, 20).map((ing) => {
            const uw = parseFloat(ing.unit_weight) || 0;
            const baseUnit = ing.unit || "un";
            const fracText = (uw > 0 && (baseUnit === "kg" || baseUnit === "L"))
              ? `1 un = ${uw.toFixed(3).replace('.', ',')} ${baseUnit}`
              : "1 un = 1 un";
            return (
              <button
                key={ing.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(ing.id)}
                className={`w-full px-3 py-2 text-left hover:bg-muted transition-colors flex items-center justify-between gap-3 ${
                  ing.id === value ? 'bg-muted' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{ing.name}</p>
                  <p className="text-[10px] text-muted-foreground tabular-nums">{fracText}</p>
                </div>
                <span className="text-muted-foreground text-xs bg-muted px-1.5 py-0.5 rounded flex-shrink-0">
                  {getIngredientUnit(ing)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {noResults && (
        <div className="px-3 pt-3 pb-1">
          <p className="text-sm text-muted-foreground mb-2">
            Nenhum item encontrado para "<strong>{searchTerm}</strong>"
          </p>
        </div>
      )}

      {/* Sempre mostrar botão de cadastrar quando há texto de busca */}
      {searchTerm && (
        <div className="p-2 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full border-dashed border-emerald-300 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
            onMouseDown={(e) => e.preventDefault()}
                onClick={handleCreateNew}
          >
            <Plus className="w-4 h-4 mr-2" />
            Cadastrar "{searchTerm}" como novo item
          </Button>
        </div>
      )}

      {!searchTerm && ingredients.length === 0 && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Nenhum ingrediente cadastrado
        </div>
      )}
    </div>
  ) : null;

  return (
    <div ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={isOpen ? searchTerm : (selectedIngredient ? `${selectedIngredient.name} (${getIngredientUnit(selectedIngredient)})` : "")}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm("");
            updateDropdownPosition();
          }}
          placeholder={placeholder}
          className="h-11 pl-9 pr-8"
        />
        {(value || searchTerm) && (
          <button
            type="button"
            onClick={() => {
              onChange("");
              setSearchTerm("");
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {createPortal(dropdownContent, document.body)}
    </div>
  );
}

// Seletor de categoria com opção de criar nova (compatível com Radix Dialog)
function CategoryCombobox({ categories, value, onChange, onCategoryCreated }) {
  const [newCatName, setNewCatName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showNewInput, setShowNewInput] = useState(false);

  const handleCreateCategory = async () => {
    if (!newCatName.trim() || creating) return;
    setCreating(true);
    try {
      const response = await axios.post(`${API}/categories`, { name: newCatName.trim() }, getAuthHeader());
      toast.success(`Categoria "${newCatName.trim()}" criada!`);
      onCategoryCreated(response.data);
      onChange(response.data.name);
      setNewCatName("");
      setShowNewInput(false);
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao criar categoria"));
    } finally {
      setCreating(false);
    }
  };

  if (showNewInput) {
    return (
      <div className="flex gap-1.5">
        <Input
          value={newCatName}
          onChange={(e) => setNewCatName(e.target.value)}
          placeholder="Nome da categoria"
          className="h-11 flex-1"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); handleCreateCategory(); }
            if (e.key === "Escape") { setShowNewInput(false); setNewCatName(""); }
          }}
        />
        <Button
          type="button"
          size="icon"
          className="h-11 w-11 bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
          onClick={handleCreateCategory}
          disabled={creating || !newCatName.trim()}
        >
          {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-11 w-11 flex-shrink-0"
          onClick={() => { setShowNewInput(false); setNewCatName(""); }}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-1.5">
      <Select value={value || "none"} onValueChange={(val) => onChange(val === "none" ? "" : val)}>
        <SelectTrigger className="h-11 flex-1">
          <SelectValue placeholder="Selecione" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Sem categoria</SelectItem>
          {/* Vide comentário no Select principal: valor fora da lista não pode sumir. */}
          {value && !categories.some((c) => c.name === value) && (
            <SelectItem value={value}>{value} (categoria não encontrada)</SelectItem>
          )}
          {categories.map((cat) => (
            <SelectItem key={cat.id} value={cat.name}>
              {cat.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="h-11 w-11 flex-shrink-0 border-dashed border-violet-300 dark:border-violet-700 text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20"
        onClick={() => setShowNewInput(true)}
        title="Criar nova categoria"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
}

// Busca de categoria com autocomplete e criação inline
// Combo = dois itens juntos (dono, 26/10/2026): o hambúrguer sozinho não dizia
// o que é combo, então o ícone junta um lanche e uma bebida.
// Os dois lado a lado, sem cruzar as linhas (ocupa a caixa do ícone na largura).
function IconeCombo() {
  return (
    <span className="flex items-end gap-px" aria-hidden="true">
      <Hamburger className="w-4 h-4" />
      <CupSoda className="w-4 h-4" />
    </span>
  );
}

function ProductCategorySearch({ categories, value, onChange, onCategoryCreated }) {
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filtered = categories.filter(cat =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );
  const exactMatch = categories.some(cat => cat.name.toLowerCase() === search.trim().toLowerCase());

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleCreate = async () => {
    const trimmed = search.trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API}/categories`, { name: trimmed }, getAuthHeader());
      if (onCategoryCreated) await onCategoryCreated();
      onChange(trimmed);
      setSearch("");
      setDropdownOpen(false);
      toast.success(`Categoria "${trimmed}" criada!`);
    } catch (err) {
      toast.error("Erro ao criar categoria");
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        placeholder="Buscar ou criar categoria..."
        value={value ? value : search}
        onChange={(e) => {
          setSearch(e.target.value);
          if (value) onChange("");
          setDropdownOpen(true);
        }}
        onFocus={() => {
          setDropdownOpen(true);
          if (value) { setSearch(value); onChange(""); }
        }}
      />
      {value && (
        <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => { onChange(""); setSearch(""); inputRef.current?.focus(); }}>
          <X className="w-4 h-4" />
        </button>
      )}
      {dropdownOpen && (
        <div ref={dropdownRef} className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(cat => (
            <button
              key={cat.id || cat.name}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
              onClick={() => { onChange(cat.name); setSearch(""); setDropdownOpen(false); }}
            >
              {cat.name}
            </button>
          ))}
          {search.trim() && !exactMatch && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm text-primary font-medium hover:bg-primary/5 transition-colors flex items-center gap-2 border-t"
              onClick={handleCreate}
            >
              <Plus className="w-4 h-4" />
              Criar "{search.trim()}"
            </button>
          )}
          {filtered.length === 0 && !search.trim() && (
            <p className="px-3 py-2 text-sm text-muted-foreground">Nenhuma categoria</p>
          )}
        </div>
      )}
    </div>
  );
}

// Popup para cadastro completo de ingrediente — igual ao ESTOQUE → NOVO ITEM
function QuickIngredientDialog({ open, onOpenChange, initialName, onSuccess, categories, onCategoriesChanged }) {
  const [name, setName] = useState(initialName || "");
  const [unit, setUnit] = useState("");
  const [category, setCategory] = useState("");
  const [unitsPerPackage, setUnitsPerPackage] = useState("");
  const [unitWeight, setUnitWeight] = useState("");
  const [stockMin, setStockMin] = useState("");
  const [stockMax, setStockMax] = useState("");
  const [isRecipe, setIsRecipe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);

  useEffect(() => {
    setName(initialName || "");
  }, [initialName]);

  const resetForm = () => {
    setName("");
    setUnit("");
    setCategory("");
    setUnitsPerPackage("");
    setUnitWeight("");
    setStockMin("");
    setStockMax("");
    setIsRecipe(false);
  };

  // Verifica se o formulário tem dados preenchidos
  const hasUnsavedData = () => {
    return !!(name || unit || category || unitsPerPackage || unitWeight || stockMin || stockMax);
  };

  // Intercepta o fechamento do dialog
  const handleOpenChange = (isOpen) => {
    if (!isOpen && hasUnsavedData()) {
      // Usuário tentou fechar (clicou fora ou ESC) — perguntar se quer descartar
      setShowDiscardAlert(true);
    } else if (!isOpen) {
      onOpenChange(false);
      resetForm();
    }
  };

  const handleDiscard = () => {
    setShowDiscardAlert(false);
    onOpenChange(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !unit) {
      toast.error("Nome e unidade são obrigatórios");
      return;
    }
    if (!minMaxValido(stockMin, stockMax)) { toast.error("O estoque mínimo precisa ser menor que o máximo."); return; }

    setLoading(true);
    try {
      const payload = {
        name,
        unit: unit === "unidade" ? "un" : unit,
        category: category || null,
        stock_min: stockMin ? parseFloat(stockMin) : 0,
        stock_max: stockMax ? parseFloat(stockMax) : 0,
        is_recipe: isRecipe
      };

      Object.assign(payload, paraPayload({ unit, unitsPerPackage, unitWeight }));

      const response = await axios.post(`${API}/ingredients`, payload, getAuthHeader());
      toast.success(`Item "${name}" criado com sucesso!`);
      onSuccess(response.data);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao criar item"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => {
          if (hasUnsavedData()) {
            e.preventDefault();
            setShowDiscardAlert(true);
          }
        }} onEscapeKeyDown={(e) => {
          if (hasUnsavedData()) {
            e.preventDefault();
            setShowDiscardAlert(true);
          }
        }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                <Package className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />
              </div>
              Novo Item de Estoque
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">Preencha as informações do item. O código será gerado automaticamente.</p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <Secao numero={1} titulo="Item" ajuda="O código é gerado automaticamente." testId="secao-item">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="quick-name" className="text-xs">Nome <span className="text-red-500">*</span></Label>
                  <Input
                id="quick-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Carne Bovina, Copo Descartável..."
                required
                className="h-10 h-11"
                autoFocus
              />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Categoria</Label>
                  <CategoryCombobox
                    categories={categories}
                    value={category}
                    onChange={setCategory}
                    onCategoryCreated={(newCat) => {
                      if (onCategoriesChanged) onCategoriesChanged(newCat);
                      setCategory(newCat.name);
                    }}
                  />
                </div>
              </div>
            </Secao>

            <SecoesUnidade numero={2}
              idPrefix="quick-"
              value={{ unit, unitsPerPackage, unitWeight }}
              onChange={(v) => {
                setUnit(v.unit); setUnitsPerPackage(v.unitsPerPackage); setUnitWeight(v.unitWeight);
                if (v.unit === "un") { const r = (x) => (x === "" ? "" : String(Math.round(Number(x) || 0))); setStockMin(r(stockMin)); setStockMax(r(stockMax)); }
              }}
            />

            <SecaoEstoque numero={4} idPrefix="quick-" unidadeEstoque={unit === "kg" ? "kg" : "un"}
              stockMin={stockMin} setStockMin={setStockMin} stockMax={stockMax} setStockMax={setStockMax} />

            <Secao numero={5} titulo="Origem" ajuda="Marque se este item é produzido aqui (receita)." testId="secao-origem">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                    <ChefHat className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                  </div>
                  <p className="text-sm font-medium">É receita?</p>
                </div>
                <Switch checked={isRecipe} onCheckedChange={setIsRecipe} />
              </div>
            </Secao>

            <ResumoUnidade value={{ unit, unitsPerPackage, unitWeight }} />

            {/* Botão Salvar */}
            <Button
              type="submit"
              disabled={loading || !name || !unit}
              className="w-full h-12 font-semibold text-base shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Criando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Criar Item de Estoque
                </span>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog para confirmar descarte */}
      <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar edições?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem informações preenchidas que não foram salvas. Deseja realmente descartar as edições?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDiscardAlert(false)}>
              Continuar Editando
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard} className="bg-red-600 hover:bg-red-700 text-white">
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Componente de imagem do produto com gerenciamento de erro via estado React
function ProductThumbnail({ photoUrl, name }) {
  const [imageError, setImageError] = useState(false);

  if (!photoUrl || imageError) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full bg-muted">
        <ImageOff className="w-6 h-6 text-muted-foreground mb-1" strokeWidth={1.5} />
        <span className="text-xs text-muted-foreground">{imageError ? "Erro" : "Sem foto"}</span>
      </div>
    );
  }

  // data: URIs não devem receber prefixo /api; paths /uploads/... sim
  const src = (photoUrl && photoUrl.startsWith("data:")) ? photoUrl : `/api${photoUrl}`;
  return (
    <img
      src={src}
      alt={name}
      className="w-full h-full object-contain"
      loading="lazy"
      decoding="async"
      onError={() => setImageError(true)}
    />
  );
}

// Componente do indicador circular de progresso (fora do Products para evitar re-renderização)
// Controle financeiro e fiscal (dono, 24/10/2026): conta só o que é vendido.
// Insumo e receita não vão para a nota nem são vendidos com ficha própria.
const vendavel = (p) => !p.is_insumo && p.product_type !== "receita";
const temFicha = (p) => !!(p.recipe && p.recipe.length > 0 && p.recipe.some((r) => r.ingredient_id));
const fiscalPronto = (p) => produtoPronto(parseFiscalConfig(p.fiscal_config));
const GRUPOS_FILTRO = criarGrupos({ vendavel, temFicha, fiscalPronto });

const CORES_PENDENCIA = {
  blue: { icone: "bg-blue-500", barra: "bg-blue-500", botao: "border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/30" },
  violet: { icone: "bg-violet-500", barra: "bg-violet-500", botao: "border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-900/30" },
};

/** Ficha técnica / dados fiscais: quantos estão prontos, barra e o atalho para filtrar quem falta. */
function IndicadorPendencia({ icone: Icone, cor, titulo, feitos, total, rotuloFeitos, porque, rotuloBotao, okTexto, onVer, e2e }) {
  const c = CORES_PENDENCIA[cor];
  const faltam = Math.max(0, total - feitos);
  const pct = total ? Math.round((feitos / total) * 100) : 100;
  const tom = pct === 100 ? "text-green-600 dark:text-green-400" : pct >= 50 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
  return (
    <div className="flex flex-col p-4 bg-card rounded-2xl border" data-e2e={e2e}>
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl text-white shrink-0 ${c.icone}`}><Icone className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-tight">{titulo}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{porque}</p>
        </div>
        <span className={`text-2xl font-bold tabular-nums shrink-0 ${tom}`}>{pct}%</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${c.barra}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-muted-foreground mt-1.5"><b className="text-foreground tabular-nums">{feitos}</b> de {total} produtos {rotuloFeitos}</p>
      <div className="mt-3">
        {faltam > 0 ? (
          <Button variant="outline" size="sm" className={`w-full rounded-xl ${c.botao}`} onClick={onVer}>
            Ver {faltam} {faltam === 1 ? "produto" : "produtos"} {rotuloBotao}
          </Button>
        ) : (
          <div className="text-center text-xs text-green-600 font-medium py-1.5">✓ {okTexto}</div>
        )}
      </div>
    </div>
  );
}

function CircularProgress({ percent, size = 140, strokeWidth = 8 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;
  
  // Cor baseada na porcentagem
  const getColor = (pct) => {
    if (pct >= 80) return "#22c55e"; // verde
    if (pct >= 50) return "#eab308"; // amarelo
    return "#ef4444"; // vermelho
  };
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Círculo de fundo */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-muted/20"
      />
      {/* Círculo de progresso */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={getColor(percent)}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-500 ease-out"
      />
    </svg>
  );
}

export default function Products({ sidebarOpen, setSidebarOpen, setHeaderHidden }) {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [comboStats, setComboStats] = useState({});
  // filteredProducts agora é calculado via useMemo (linha 711) - removido estado desnecessário
  const [ingredients, setIngredients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [sortBy, setSortBy] = useState("alfabetica-az");
  const [productToDelete, setProductToDelete] = useState(null);
  
  const [name, setName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [barcode, setBarcode] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [productType, setProductType] = useState("produto");
  const [confirmTypeChange, setConfirmTypeChange] = useState(null); // null | 'combo' | 'receita'
  const [salePrice, setSalePrice] = useState("");
  // Preço "de" (riscado) — feature v1.5.282+. Toggle é derivado: originalPrice
  // não-vazio && > salePrice ativa o modo promocional. isPromotional é só state
  // UX pra manter switch ligado enquanto o dono digita o valor.
  const [originalPrice, setOriginalPrice] = useState("");
  const [isPromotional, setIsPromotional] = useState(false);
  const [simplePrice, setSimplePrice] = useState("");
  const [comboDescription, setComboDescription] = useState("");
  const [simpleDescription, setSimpleDescription] = useState("");
  const [comboLabel, setComboLabel] = useState("");
  const [simpleLabel, setSimpleLabel] = useState("");
  const [simplePhotoUrl, setSimplePhotoUrl] = useState("");
  const [comboPhotoUrl, setComboPhotoUrl] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  // Fotos extras e vídeo (só para o cliente: cardápio, Mesa QR, Totem) — 23/10/2026
  const [midias, setMidias] = useState(MIDIAS_VAZIAS);
  const [videoPrincipal, setVideoPrincipal] = useState(null); // % do vídeo enviado pelo quadro da foto 1
  const midiasOriginais = useRef(MIDIAS_VAZIAS);
  const midiasDe = useRef(null); // produto cujas mídias estão carregando/abertas
  const [isInsumo, setIsInsumo] = useState(false);
  const [isDivisible, setIsDivisible] = useState(false);
  // Balança (26/10/2026): produto em Quilo que puxa o peso da balança do computador
  const [usaBalanca, setUsaBalanca] = useState(false);
  const [taraGramas, setTaraGramas] = useState("");
  const [saleUnit, setSaleUnit] = useState("un"); // "un" | "kg" | "L"
  const [isAvailable, setIsAvailable] = useState(true); // Disponível para compra
  const [recipeIngredients, setRecipeIngredients] = useState([{ ingredient_id: "", quantity: "" }]);
  const [recipePackaging, setRecipePackaging] = useState([{ ingredient_id: "", quantity: "" }]);
  const [orderSteps, setOrderSteps] = useState([]);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  // Trava sincrona contra duplo/triplo clique (setLoading só atualiza no próximo render,
  // dando janela pra cliques rápidos passarem em paralelo e criarem duplicatas).
  const isSubmittingRef = useRef(false);
  const [categories, setCategories] = useState([]);
  const [setores, setSetores] = useState([]);
  const [setorDialogOpen, setSetorDialogOpen] = useState(false);
  const [setorName, setSetorName] = useState("");
  const [setorTempo, setSetorTempo] = useState(""); // tempo de produção do setor (min); "" = Preferências
  const [editSetorMode, setEditSetorMode] = useState(false);
  const [currentSetorId, setCurrentSetorId] = useState(null);
  const [deleteSetorDialogOpen, setDeleteSetorDialogOpen] = useState(false);
  const [setorToDelete, setSetorToDelete] = useState(null);
  const [categorySetorId, setCategorySetorId] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [activeFormTab, setActiveFormTab] = useState("ficha"); // "ficha" ou "etapas"
  const [activeMainTab, setActiveMainTab] = useState("produto");
  
  // Produtos arquivados
  const [archivedProducts, setArchivedProducts] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  // Carregamento com cards (25/10/2026): cada aba pisca só na 1a busca dela;
  // recarregar depois de salvar mantém a lista na tela.
  const [arquivadosCarregados, setArquivadosCarregados] = useState(false);
  const [categoriasCarregadas, setCategoriasCarregadas] = useState(false);
  const [setoresCarregados, setSetoresCarregados] = useState(false);

  // Estado para ordem dos cards (combo primeiro ou simples primeiro)
  const [cardOrder, setCardOrder] = useState("combo_first"); // "combo_first" ou "simple_first"

  // Estados para visibilidade e preços por módulo
  const [visibleModules, setVisibleModules] = useState(["Balcao","Totem","Delivery","Mesas","MesaQR","Comandas","Cardapio"]);
  const [modulePrices, setModulePrices] = useState({});

  // Estado para configuração de impressão de tickets
  const [printConfig, setPrintConfig] = useState({
    ticket_enabled: false,
    ticket_per_unit: true,
    ticket_route: "CAIXA",
    ticket_title: "",
    ticket_modules: ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"],
    // Modulos que devem GERAR cupom de PREPARO pra esse produto (default: todos)
    preparo_modules: ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"],
  });

  // Cadastro fiscal do produto (aba NOTA FISCAL) — alimenta a emissão de NFC-e.
  // Vazio por padrão: produto sem NCM/CFOP não emite, e a aba avisa disso.
  const [fiscalConfig, setFiscalConfig] = useState({});
  // Dialog Personalizacao Pizzas (tamanhos, bordas e fichas compartilhadas).
  const [pizzaPersonalizacaoOpen, setPizzaPersonalizacaoOpen] = useState(false);
  // Ficha tecnica de PIZZA: {size_id: [linhas]} do sabor, sai no Salvar Produto.
  // Massa/molho/caixa sao ingredientes normais do sabor (meio a meio divide tudo por N).
  const [pizzaRecipes, setPizzaRecipes] = useState({});

  // Estados para campos de receita (rendimento)
  const [recipeYield, setRecipeYield] = useState("");
  const [recipeYieldUnit, setRecipeYieldUnit] = useState("kg");
  const [linkedIngredientId, setLinkedIngredientId] = useState("");
  
  // Estados para busca de produtos nas etapas
  const [stepProductSearch, setStepProductSearch] = useState({});
  const [stepSearchOpen, setStepSearchOpen] = useState({});
  
  // Estado para popup de novo produto (wizard inicial)
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [newProductData, setNewProductData] = useState({ name: "", salePrice: "", category: "", productType: "produto", isInsumo: false, isDivisible: false, saleUnit: "un" });
  // Pizza: catálogo universal (sizes/borders/doughs) + matriz de preço por tamanho do produto sendo criado
  const [pizzaConfig, setPizzaConfig] = useState({ sizes: [], borders: [], doughs: [] });
  // Sizes efetivos pro atalho de preço por tamanho. Prioriza o step pizza_size
  // do orderSteps local (refletindo edição AO VIVO em Personalização, antes
  // mesmo de salvar). Fallback pro pizzaConfig.sizes (fetched do backend).
  const pizzaSizesEffective = useMemo(() => {
    const sizeStep = orderSteps.find(s => String(s?.kind || "") === "pizza_size");
    if (sizeStep && Array.isArray(sizeStep.items) && sizeStep.items.length > 0) {
      return sizeStep.items;
    }
    return pizzaConfig.sizes || [];
  }, [orderSteps, pizzaConfig.sizes]);
  // Item do produto atual no step pizza_flavor (só quando editando a pizza-mãe
  // — sabor sync não tem order_steps próprios, retorna null). Usado pra
  // sincronizar preços entre o atalho do editor de sabor e o FlavorEditor na
  // aba Personalização ao vivo (mesmo antes de salvar).
  const currentInPizzaFlavor = useMemo(() => {
    if (!currentProductId) return null;
    const fs = orderSteps.find(s => String(s?.kind || "") === "pizza_flavor");
    if (!fs || !Array.isArray(fs.items)) return null;
    return fs.items.find(it => it.product_id === currentProductId) || null;
  }, [orderSteps, currentProductId]);
  const [pizzaPrices, setPizzaPrices] = useState({}); // { size_id: "priceStr" }
  // No wizard de criar pizza, lojista pode "deletar" tamanhos que o sabor não
  // vende. Não apaga o tamanho global — só evita gravar prices_by_size dele
  // pra ESTE sabor. Set<size_id>.
  const [pizzaPricesExcluded, setPizzaPricesExcluded] = useState(new Set());
  // Resolve a pizza-mãe atual do tenant (qualquer produto pizza com step pizza_size,
  // ou que aponte pra uma via sync). Retorna null se ainda não existe nenhuma.
  const findMotherPizzaIdInProducts = useCallback(() => {
    for (const p of products) {
      if ((p.product_type || "produto") !== "pizza") continue;
      let steps = [];
      try {
        steps = typeof p.order_steps === "string" ? JSON.parse(p.order_steps || "[]") : (p.order_steps || []);
      } catch (_) { steps = []; }
      if (Array.isArray(steps) && steps.some(s => s && s.kind === "pizza_size")) {
        return p.id;
      }
      if (p.sync_order_steps_from_id) return p.sync_order_steps_from_id;
    }
    return null;
  }, [products]);
  // Sync Personalização → atalho: quando user edita preços do PRÓPRIO produto no
  // FlavorEditor (aba Personalização da pizza-mãe), `currentInPizzaFlavor.prices_by_size`
  // muda. Espelha em `pizzaPrices` pra o atalho refletir e o Save persistir.
  useEffect(() => {
    if (!currentInPizzaFlavor) return;
    const pbs = currentInPizzaFlavor.prices_by_size || {};
    const next = {};
    Object.entries(pbs).forEach(([sid, p]) => {
      next[sid] = (p != null && Number(p) > 0) ? String(p).replace(".", ",") : "";
    });
    setPizzaPrices(prev => {
      // Guard: só set se mudou de fato — evita loop com onChange do atalho
      const keys = new Set([...Object.keys(prev || {}), ...Object.keys(next)]);
      const same = [...keys].every(k => (prev?.[k] || "") === (next[k] || ""));
      return same ? prev : next;
    });
  }, [currentInPizzaFlavor]);
  // Único modal de gerenciamento da Pizzaria (Tamanhos / Bordas) com tabs
  // Estados pizzaManagerOpen/pizzaManagerTab removidos junto com PizzaManagerDialog (v1.5.208)
  // Dialog "Personalização Pizzas" (visão consolidada). Aparece no 1º card da grid quando tenant tem pizzas.
  const hasPizzaProducts = useMemo(() => (products || []).some(p => p.product_type === "pizza"), [products]);
  const loadPizzaConfig = useCallback(async (productId = null) => {
    // Product-aware único: lê do order_steps da pizza. Sem productId (1ª pizza
    // do tenant em criação), retorna config vazio — wizard mostra estado vazio
    // e o lojista configura tamanhos depois em Personalização.
    if (!productId) {
      setPizzaConfig({ sizes: [], borders: [], doughs: [] });
      return;
    }
    try {
      const r = await axios.get(`${API}/public/pizza/config?product_id=${encodeURIComponent(productId)}`);
      if (r?.data?.source === "product_steps") {
        setPizzaConfig({
          sizes: r.data.sizes || [],
          borders: r.data.borders || [],
          doughs: r.data.doughs || [],
        });
      } else {
        setPizzaConfig({ sizes: [], borders: [], doughs: [] });
      }
    } catch (e) {
      console.warn("[Pizza] config indisponível:", e?.message);
      setPizzaConfig({ sizes: [], borders: [], doughs: [] });
    }
  }, []);

  // Estados para cadastro rápido de ingrediente
  const [quickIngredientOpen, setQuickIngredientOpen] = useState(false);
  const [quickIngredientName, setQuickIngredientName] = useState("");
  const [ingredientCategories, setIngredientCategories] = useState([]);
  const [quickIngredientTarget, setQuickIngredientTarget] = useState(null); // {type: "ingredient"|"packaging", index: number}
  
  // Category management states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editCategoryMode, setEditCategoryMode] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryPrintAlert, setCategoryPrintAlert] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  // Duplicate product states
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [productToDuplicate, setProductToDuplicate] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicatingProduct, setDuplicatingProduct] = useState(false);
  
  // Filtro de performance (sem-foto, sem-descricao, todos)
  const [performanceFilter, setPerformanceFilter] = useState("todos");
  
  // Novos filtros manuais
  // Tipo, categoria, estoque, visibilidade, foto, descrição, ficha e nota (features/produtos-filtros)
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const mudarFiltro = (chave, valor) => setFiltros((f) => ({ ...f, [chave]: valor }));
  const [filterName, setFilterName] = useState(""); // Filtro de busca por nome
  const [cardapioStatsExpanded, setCardapioStatsExpanded] = useState(false);
  
  // Estado para modal de copiar combo
  const [copyComboOpen, setCopyComboOpen] = useState(false);
  const [copyStepsOpen, setCopyStepsOpen] = useState(false);
  const [copyStepsSearch, setCopyStepsSearch] = useState("");
  const [copyComboSearch, setCopyComboSearch] = useState("");
  // Sincronizar etapas com produto origem: ao copiar, fica o id; ao desmarcar, limpa
  const [syncOrderStepsFromId, setSyncOrderStepsFromId] = useState("");
  
  // Estado para edição rápida de preço
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [tempPrice, setTempPrice] = useState("");
  
  // Estado para controlar qual card está expandido (apenas um por vez)
  const [expandedProductId, setExpandedProductId] = useState(null);
  
  // Direção da ordenação (asc/desc) para campos com toggle
  const [sortDirection, setSortDirection] = useState({
    cmv: "desc",
    margem: "desc",
    preco: "desc"
  });

  // View mode e filtros
  const [viewMode, setViewMode] = useState("grid"); // "grid" ou "list"
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showStatsExpanded, setShowStatsExpanded] = useState(false);

  // AI description improvement
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null); // { versions: [...] }

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Calcular estatísticas de performance do cardápio
  const cardapioStats = useMemo(() => {
    const total = products.length;
    if (total === 0) {
      return {
        total: 0,
        withPhoto: 0,
        withDescription: 0,
        withFichaTecnica: 0,
        vendaveis: 0,
        withFiscal: 0,
        fiscalPercent: 0,
        photoPercent: 0,
        descriptionPercent: 0,
        fichaTecnicaPercent: 0,
        overallPercent: 0
      };
    }
    
    const withPhoto = products.filter(p => p.photo_url && p.photo_url.trim() !== "").length;
    const withDescription = products.filter(p => p.description && p.description.trim() !== "").length;
    // Ficha técnica e nota fiscal: só os produtos vendidos (sem insumo e receita)
    const listaVendaveis = products.filter(vendavel);
    const vendaveis = listaVendaveis.length;
    const withFichaTecnica = listaVendaveis.filter(temFicha).length;
    const withFiscal = listaVendaveis.filter(fiscalPronto).length;
    
    const photoPercent = Math.round((withPhoto / total) * 100);
    const descriptionPercent = Math.round((withDescription / total) * 100);
    const fichaTecnicaPercent = vendaveis ? Math.round((withFichaTecnica / vendaveis) * 100) : 100;
    const fiscalPercent = vendaveis ? Math.round((withFiscal / vendaveis) * 100) : 100;
    
    // Média ponderada: foto (50%) + descrição (50%) - ficha técnica NÃO afeta
    const overallPercent = Math.round((photoPercent + descriptionPercent) / 2);
    
    return {
      total,
      withPhoto,
      withDescription,
      withFichaTecnica,
      vendaveis,
      withFiscal,
      fiscalPercent,
      photoPercent,
      descriptionPercent,
      fichaTecnicaPercent,
      overallPercent
    };
  }, [products]);

  // Filtrar produtos baseado em todos os filtros (performance + manuais) - MEMOIZADO
  // Receita é do Estoque (Nova receita), não do cardápio: fica fora desta tela
  const produtosCardapio = useMemo(() => products.filter(ehDoCardapio), [products]);

  const filteredProducts = useMemo(() => {
    let filtered = produtosCardapio;
    
    // Filtro de busca por nome (prioridade alta)
    if (filterName.trim()) {
      const searchTerm = filterName.toLowerCase().trim();
      // Código de barras: bipar com o leitor na busca já acha o produto.
      const buscaCodigo = limparCodigo(filterName);
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm) ||
        (p.code && p.code.includes(searchTerm)) ||
        (p.barcode && buscaCodigo && p.barcode === buscaCodigo) ||
        (p.description && p.description.toLowerCase().includes(searchTerm))
      );
    }
    
    // Filtro de performance (dos indicadores)
    if (performanceFilter === "sem-foto") {
      filtered = filtered.filter(p => !p.photo_url || p.photo_url.trim() === "");
    } else if (performanceFilter === "sem-descricao") {
      filtered = filtered.filter(p => !p.description || p.description.trim() === "");
    }
    
    return aplicarFiltros(filtered, filtros, GRUPOS_FILTRO);
  }, [produtosCardapio, filterName, performanceFilter, filtros]);
  
  // Contagem de filtros ativos
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterName) count++;
    if (performanceFilter !== "todos") count++;
    return count + filtrosAtivos(filtros, GRUPOS_FILTRO).length;
  }, [filterName, performanceFilter, filtros]);

  // Handler para toggle de ordenação
  const handleSortToggle = (field) => {
    const newDirection = sortDirection[field] === "desc" ? "asc" : "desc";
    setSortDirection(prev => ({ ...prev, [field]: newDirection }));
    setSortBy(`${field}-${newDirection === "desc" ? "maior" : "menor"}`);
  };
  
  // Limpar todos os filtros
  const clearAllFilters = () => {
    setPerformanceFilter("todos");
    setFiltros(FILTROS_VAZIOS);
    setFilterName("");
  };

  // Botão "Ver N produtos sem ...": limpa os outros filtros (para a lista bater
  // com o número do botão), aplica o dele e rola até a lista
  const verPendentes = (aplicar) => {
    clearAllFilters();
    aplicar();
    setTimeout(() => document.getElementById("lista-produtos")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Função para salvar preço rapidamente (edita PERSONALIZAÇÃO->Simples: combo=simple_price, não-combo=sale_price)
  const saveQuickPrice = async (productId) => {
    if (!tempPrice || isNaN(parseFloat(tempPrice))) {
      setEditingPriceId(null);
      setTempPrice("");
      return;
    }
    
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;
      
      const valor = parseFloat(tempPrice);
      const isCombo = product.product_type === "combo";
      const updatedData = {
        ...product,
        recipe: product.recipe || [],
        ...(isCombo ? { simple_price: valor } : { sale_price: valor })
      };
      
      await axios.put(`${API}/products/${productId}`, updatedData, getAuthHeader());
      toast.success("Preço atualizado!");
      fetchProducts();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao atualizar preço"));
    }
    
    setEditingPriceId(null);
    setTempPrice("");
  };

  // Função para expandir/minimizar produto (apenas um por vez)
  const toggleProductExpand = (productId) => {
    setExpandedProductId(prevId => prevId === productId ? null : productId);
  };

  // Função para copiar configurações de outro combo
  const copyFromCombo = (sourceCombo) => {
    if (!sourceCombo) return;
    
    // Copiar preços e descrições
    if (sourceCombo.simple_price) setSimplePrice(sourceCombo.simple_price.toString());
    if (sourceCombo.simple_description) setSimpleDescription(sourceCombo.simple_description);
    if (sourceCombo.combo_description) setComboDescription(sourceCombo.combo_description);
    if (sourceCombo.simple_label) setSimpleLabel(sourceCombo.simple_label);
    if (sourceCombo.combo_label) setComboLabel(sourceCombo.combo_label);
    
    // Copiar fotos (se existirem)
    if (sourceCombo.simple_photo_url) setSimplePhotoUrl(sourceCombo.simple_photo_url);
    if (sourceCombo.combo_photo_url) setComboPhotoUrl(sourceCombo.combo_photo_url);
    
    // Copiar etapas (com todas as preferências)
    if (sourceCombo.order_steps && sourceCombo.order_steps.length > 0) {
      const copiedSteps = sourceCombo.order_steps.map(step => ({
        name: step.name || "",
        calculation_type: step.calculation_type || "soma",
        min_selections: step.min_selections || 0,
        max_selections: step.max_selections || 0,
        combo_only: step.combo_only || false,
        default_item_id: step.default_item_id || null,
        sync_prices: step.sync_prices !== false,
        items: (step.items || []).map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          price_override: item.price_override || 0,
          is_default: item.is_default || false
        }))
      }));
      setOrderSteps(copiedSteps);
    }
    if (sourceCombo.card_order) setCardOrder(sourceCombo.card_order);
    // Vincular: etapas ficarão sempre iguais ao produto origem (qualquer edição no origem atualiza este)
    setSyncOrderStepsFromId(sourceCombo.id);
    
    setCopyComboOpen(false);
    setCopyComboSearch("");
    toast.success(`Copiado de "${sourceCombo.name}". Etapas permanecem iguais ao produto origem.`);
  };

  const copyStepsFromProduct = (sourceProduct) => {
    if (!sourceProduct || !sourceProduct.order_steps?.length) {
      toast.error("Este produto não tem etapas");
      return;
    }
    // Cópia profunda preservando TODOS os campos (incluindo os de etapas pizza_*):
    //   step.kind (pizza_size | pizza_flavor | pizza_border)
    //   step.pricing_rule (highest | lowest | average) — meio-a-meio
    //   item.slices, item.max_flavors (tamanhos)
    //   item.prices_by_size (matriz preço por tamanho — sabores e bordas)
    //   item.id local (tamanhos e bordas — sem product_id)
    //   item.photo_url, item.description (sabores)
    // O spread em step e item garante que tudo passa sem precisar listar campos.
    const copiedSteps = sourceProduct.order_steps.map(step => ({
      ...step,
      items: (step.items || []).map(item => ({ ...item })),
    }));
    setOrderSteps(copiedSteps);
    setCopyStepsOpen(false);
    setCopyStepsSearch("");
    const pizzaCount = copiedSteps.filter(s => String(s.kind || "").startsWith("pizza_")).length;
    toast.success(
      `Etapas copiadas de "${sourceProduct.name}"!` +
      (pizzaCount > 0 ? ` (incluindo ${pizzaCount} etapa${pizzaCount > 1 ? "s" : ""} de pizza)` : "")
    );
  };

  const productsWithSteps = useMemo(() => {
    return products.filter(p =>
      p.order_steps && p.order_steps.length > 0 &&
      p.id !== currentProductId
    );
  }, [products, currentProductId]);

  // Obter lista de combos disponíveis para copiar
  const availableCombos = useMemo(() => {
    return products.filter(p => 
      p.product_type === "combo" && 
      p.id !== currentProductId &&
      p.order_steps && 
      p.order_steps.length > 0
    );
  }, [products, currentProductId]);

  // Auto-marcar insumo e divisível quando o tipo é "receita"
  // Também garantir que a aba ativa seja "ficha" (receitas não têm etapas)
  useEffect(() => {
    if (productType === "receita") {
      setIsInsumo(true);
      setIsDivisible(true);
      setActiveFormTab("ficha"); // Receitas não têm aba de etapas
      setSalePrice(""); // Limpar preço de venda
    }
    // "Marcar como promocional" só faz sentido em product_type='produto'.
    // Limpar state stale ao trocar pra combo/receita/pizza — evita payload
    // com original_price fantasma (mesmo com guard defensivo no submit).
    if (productType !== "produto") {
      setIsPromotional(false);
      setOriginalPrice("");
    }
  }, [productType]);

  useEffect(() => {
    fetchProducts();
    fetchIngredients();
    fetchCategories();
    fetchSetores();
    fetchIngredientCategories();
    loadCurrentUser();
    // Carregamento único na montagem; funções estão estáveis e de init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCurrentUser = () => {
    const user = localStorage.getItem("user");
    if (user) {
      try { setCurrentUser(JSON.parse(user)); } catch (_) {}
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      // Atualizar CMV dos produtos antes de carregar (ao entrar/recarregar a aba PRODUTOS)
      try {
        await axios.post(`${API}/products/recalculate-cmv`, {}, getAuthHeader());
      } catch (_) { /* ignora se recalc falhar (ex: permissão) */ }
      const [prodRes, statsRes] = await Promise.all([
        axios.get(`${API}/products`, getAuthHeader()),
        axios.get(`${API}/products/combo-stats`, getAuthHeader()).catch(() => ({ data: {} }))
      ]);
      setProducts(prodRes.data);
      setComboStats(statsRes.data || {});
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao carregar produtos"));
    } finally {
      setProductsLoading(false);
    }
  };

  // Buscar categorias de ingredientes para o popup de cadastro rápido
  const fetchIngredientCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, getAuthHeader());
      setIngredientCategories(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar categorias de ingredientes");
    }
  };

  // Handler para cadastro rápido de ingrediente
  const handleQuickIngredientCreate = (initialName, targetType, targetIndex) => {
    setQuickIngredientName(initialName);
    setQuickIngredientTarget({ type: targetType, index: targetIndex });
    setQuickIngredientOpen(true);
  };

  // Callback após criar ingrediente rápido - auto-preenche o campo OU adiciona direto na receita
  const handleQuickIngredientSuccess = (newIngredient) => {
    // Adicionar o novo ingrediente à lista global de ingredientes
    setIngredients(prev => [...prev, newIngredient]);

    // Auto-preencher o campo que estava sendo editado
    if (quickIngredientTarget) {
      const { type, index } = quickIngredientTarget;
      if (index === -1 && String(type).startsWith("pizza:")) {
        // Ficha de pizza: entra com qtd 1 em todos os tamanhos (ingrediente ou embalagem)
        const tipoPizza = type.split(":")[1] === "packaging" ? "packaging" : "ingredient";
        setPizzaRecipes(prev => adicionarNaFichaPizza(prev, pizzaSizesEffective, newIngredient.id, tipoPizza));
      } else if (index === -1) {
        // Novo fluxo: busca direta — adiciona como nova linha na receita com qtd 1
        const newItem = { ingredient_id: newIngredient.id, quantity: "1" };
        if (type === "ingredient") {
          setRecipeIngredients(prev => [...prev.filter(r => r.ingredient_id), newItem]);
        } else if (type === "packaging") {
          setRecipePackaging(prev => [...prev.filter(r => r.ingredient_id), newItem]);
        }
      } else if (type === "ingredient") {
        setRecipeIngredients(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], ingredient_id: newIngredient.id };
          return updated;
        });
      } else if (type === "packaging") {
        setRecipePackaging(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], ingredient_id: newIngredient.id };
          return updated;
        });
      }
    }

    // Limpar o target
    setQuickIngredientTarget(null);
  };




  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editCategoryMode) {
        const nomeAntigo = categories.find(c => c.id === currentCategoryId)?.name;
        const { data: catAtualizada } = await axios.put(`${API}/categories/${currentCategoryId}`, { name: categoryName, setor_id: (categorySetorId && categorySetorId !== "none") ? categorySetorId : null, print_alert: categoryPrintAlert }, getAuthHeader());
        // O backend renomeia a categoria em TODOS os produtos; a lista aqui na
        // tela ainda guardava o nome ANTIGO, e categoria nova + produto velho
        // = produto sumindo da categoria (parece "desassociado"). Recarrega os
        // produtos e leva junto o filtro que estava aplicado.
        const renomeados = catAtualizada?.produtos_renomeados || 0;
        if (nomeAntigo && nomeAntigo !== categoryName) {
          setFiltros(f => (f.categoria === nomeAntigo ? { ...f, categoria: categoryName } : f));
          await fetchProducts();
        }
        toast.success(renomeados > 0
          ? `Categoria atualizada! ${renomeados} produto${renomeados > 1 ? "s" : ""} acompanharam o novo nome.`
          : "Categoria atualizada!");
      } else {
        await axios.post(`${API}/categories`, { name: categoryName, setor_id: (categorySetorId && categorySetorId !== "none") ? categorySetorId : null, print_alert: categoryPrintAlert }, getAuthHeader());
        toast.success("Categoria criada!");
      }
      
      setCategoryName("");
      setCategoryDialogOpen(false);
      setEditCategoryMode(false);
      setCurrentCategoryId(null);
      fetchCategories();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao salvar categoria"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditCategoryMode(true);
    setCurrentCategoryId(category.id);
    setCategoryName(category.name);
    setCategorySetorId(category.setor_id || "");
    setCategoryPrintAlert(!!category.print_alert);
    setCategoryDialogOpen(true);
  };

  const confirmDeleteCategory = async (category) => {
    // Antes de abrir o popup, recarrega a contagem fresca da categoria —
    // evita que o usuário tente excluir achando que está vazia mas na verdade tem produtos
    // (ex: outro operador acabou de adicionar um produto a ela).
    let fresh = category;
    try {
      const res = await axios.get(`${API}/categories`, getAuthHeader());
      const updated = (res.data || []).find(c => c.id === category.id);
      if (updated) fresh = { ...category, ...updated };
      setCategories(res.data || []);
    } catch (_) {
      // se falhar, abre com o que temos
    }
    setCategoryToDelete(fresh);
  };


  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, getAuthHeader());
      setCategories(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    } finally {
      setCategoriasCarregadas(true);
    }
  };

  const fetchSetores = async () => {
    try {
      const response = await axios.get(`${API}/setores`, getAuthHeader());
      setSetores(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar setores:", error);
    } finally {
      setSetoresCarregados(true);
    }
  };

  const handleCreateSetor = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editSetorMode) {
        await axios.put(`${API}/setores/${currentSetorId}`, { name: setorName, tempo_producao: tempoParaSalvar(setorTempo) }, getAuthHeader());
        toast.success("Setor atualizado!");
      } else {
        await axios.post(`${API}/setores`, { name: setorName, tempo_producao: tempoParaSalvar(setorTempo) }, getAuthHeader());
        toast.success("Setor criado!");
      }
      setSetorName("");
      setSetorTempo("");
      setSetorDialogOpen(false);
      setEditSetorMode(false);
      setCurrentSetorId(null);
      fetchSetores();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao salvar setor"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditSetor = (setor) => {
    setEditSetorMode(true);
    setCurrentSetorId(setor.id);
    setSetorName(setor.name);
    setSetorTempo(setor.tempo_producao != null ? String(setor.tempo_producao) : "");
    setSetorDialogOpen(true);
  };

  const handleDeleteSetor = async () => {
    if (!setorToDelete) return;
    try {
      await axios.delete(`${API}/setores/${setorToDelete.id}`, getAuthHeader());
      toast.success("Setor deletado!");
      setDeleteSetorDialogOpen(false);
      setSetorToDelete(null);
      fetchSetores();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao deletar setor"));
    }
  };

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`, getAuthHeader());
      setIngredients(response.data);
    } catch (error) {
      toast.error("Erro ao carregar ingredientes");
    }
  };

  const resetForm = () => {
    setName("");
    setProductCode("");
    setBarcode("");
    setDescription("");
    setCategory("");
    setProductType("produto");
    setSalePrice("");
    setOriginalPrice("");
    setIsPromotional(false);
    setPhotoUrl("");
    setPhotoFile(null);
    setMidias(MIDIAS_VAZIAS);
    midiasOriginais.current = MIDIAS_VAZIAS;
    midiasDe.current = null;
    setIsInsumo(false);
    setIsDivisible(false);
    setUsaBalanca(false);
    setTaraGramas("");
    setSaleUnit("un");
    setIsAvailable(true);
    setRecipeIngredients([{ ingredient_id: "", quantity: "" }]);
    setRecipePackaging([{ ingredient_id: "", quantity: "" }]);
    setOrderSteps([]);
    setEditMode(false);
    setCurrentProductId(null);
    setActiveFormTab("ficha");
    setActiveMainTab("produto");
    setRecipeYield("");
    setRecipeYieldUnit("kg");
    setLinkedIngredientId("");
    setCardOrder("combo_first");
    setSimplePrice("");
    setComboDescription("");
    setSimpleDescription("");
    setComboLabel("");
    setSimpleLabel("");
    setSimplePhotoUrl("");
    setComboPhotoUrl("");
    setSyncOrderStepsFromId("");
    setVisibleModules(["Balcao","Totem","Delivery","Mesas","MesaQR","Comandas","Cardapio"]);
    setModulePrices({});
    setPrintConfig({ ticket_enabled: false, ticket_per_unit: true, ticket_route: "", ticket_title: "", ticket_modules: ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"], preparo_modules: ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"] });
    setFiscalConfig({});
    setPizzaRecipes({});
  };

  const handleEdit = (product) => {
    setEditMode(true);
    setCurrentProductId(product.id);
    setMidias(MIDIAS_VAZIAS);
    // null = ainda não sabemos o que está salvo: o salvar não mexe nas mídias
    // (se a leitura falhar, adicionar uma foto apagaria as extras que existem)
    midiasOriginais.current = null;
    midiasDe.current = product.id;
    axios.get(`${API}/products/${product.id}/midias`, getAuthHeader())
      .then((r) => {
        if (midiasDe.current !== product.id) return; // já abriram outro produto
        const m = r.data || MIDIAS_VAZIAS; setMidias(m); midiasOriginais.current = m;
      })
      .catch(() => { if (midiasDe.current === product.id) toast.error("Não consegui carregar as fotos extras deste produto. Mudanças nelas não serão salvas."); });
    setName(product.name);
    setProductCode(product.code || "");
    setBarcode(product.barcode || "");
    setDescription(product.description || "");
    setCategory(product.category || "");
    setProductType(product.product_type || "produto");
    setSalePrice(product.sale_price ? product.sale_price.toString() : "");
    // Preço "de" — só considerado se > sale_price. Toggle liga quando produto
    // vem com original_price válido.
    {
      const origNum = Number(product.original_price || 0);
      const saleNum = Number(product.sale_price || 0);
      if (origNum > 0 && origNum > saleNum) {
        setOriginalPrice(origNum.toString());
        setIsPromotional(true);
      } else {
        setOriginalPrice("");
        setIsPromotional(false);
      }
    }
    setSimplePrice(product.simple_price ? product.simple_price.toString() : "");
    setComboDescription(product.combo_description || "");
    setSimpleDescription(product.simple_description || "");
    setComboLabel(product.combo_label || "");
    setSimpleLabel(product.simple_label || "");
    setSimplePhotoUrl(product.simple_photo_url || "");
    setComboPhotoUrl(product.combo_photo_url || "");
    setPhotoUrl(product.photo_url || "");
    setIsInsumo(product.is_insumo || false);
    setIsDivisible(product.is_divisible || false);
    setUsaBalanca(!!product.usa_balanca);
    setTaraGramas(product.tara_gramas ? String(product.tara_gramas) : "");
    setSaleUnit(product.sale_unit || "un");
    setIsAvailable(product.available !== false); // Default true
    setOrderSteps(product.order_steps || []);
    setRecipeYield(product.recipe_yield ? product.recipe_yield.toString() : "");
    setRecipeYieldUnit(product.recipe_yield_unit || "kg");
    setLinkedIngredientId(product.linked_ingredient_id || "");
    setCardOrder(product.card_order || "combo_first");
    setSyncOrderStepsFromId(product.sync_order_steps_from_id || "");
    setVisibleModules(product.visible_modules || ["Balcao","Totem","Delivery","Mesas","MesaQR","Comandas","Cardapio"]);
    setModulePrices(product.module_prices || {});
    const pc = typeof product.print_config === 'string' ? JSON.parse(product.print_config || '{}') : (product.print_config || {});
    setPrintConfig({ ticket_enabled: pc.ticket_enabled || false, ticket_per_unit: pc.ticket_per_unit !== false, ticket_route: pc.ticket_route ?? "", ticket_title: pc.ticket_title || "", ticket_modules: pc.ticket_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"], preparo_modules: pc.preparo_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"] });
    const fisc = typeof product.fiscal_config === 'string' ? JSON.parse(product.fiscal_config || '{}') : (product.fiscal_config || {});
    setFiscalConfig(fisc);
    const pr = typeof product.pizza_recipes === 'string' ? JSON.parse(product.pizza_recipes || '{}') : (product.pizza_recipes || {});
    setPizzaRecipes(pr && typeof pr === "object" ? pr : {});

    // Separar ingredientes e embalagens baseado no item_type
    const recipe = product.recipe || [];
    const ingredients = recipe
      .filter((r) => r.item_type === "ingredient")
      .map((r) => ({
        ingredient_id: r.ingredient_id,
        quantity: (r.quantity != null ? r.quantity : "").toString(),
      }));
    const packaging = recipe
      .filter((r) => r.item_type === "packaging")
      .map((r) => ({
        ingredient_id: r.ingredient_id,
        quantity: (r.quantity != null ? r.quantity : "").toString(),
      }));
    
    setRecipeIngredients(ingredients.length > 0 ? ingredients : [{ ingredient_id: "", quantity: "" }]);
    setRecipePackaging(packaging.length > 0 ? packaging : [{ ingredient_id: "", quantity: "" }]);
    setActiveMainTab("produto");
    setOpen(true);

    // Pizza: carregar config product-aware (lê do order_steps da pizza-mãe via
    // sync_order_steps_from_id, mesma fonte do PizzaPopup público) + matriz de
    // preço por tamanho deste sabor
    if ((product.product_type || "produto") === "pizza") {
      loadPizzaConfig(product.id);
      (async () => {
        try {
          const res = await axios.get(`${API}/pizza/products/${product.id}/prices`, getAuthHeader());
          const map = res.data || {};
          const stringified = {};
          Object.entries(map).forEach(([sid, v]) => {
            stringified[sid] = (v != null && Number(v) > 0) ? String(v).replace(".", ",") : "";
          });
          setPizzaPrices(stringified);
        } catch (e) {
          console.error("[Pizza] erro ao buscar preços do produto:", e);
          setPizzaPrices({});
        }
      })();
    } else {
      setPizzaPrices({});
    }
  };

  const handlePhotoUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/product-photo`, formData, getAuthHeader());
      const url = response.data?.photo_url;
      return typeof url === "string" ? url : null;
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao fazer upload da foto"));
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Categoria obrigatória (dono, 26/10/2026); a receita (só ficha técnica) fica de fora
    if (!category && productType !== "receita") {
      toast.error("Escolha a categoria do produto antes de salvar");
      document.getElementById("category-main")?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    // Trava sincrona imediata (previne duplo/triplo clique antes do React re-renderizar)
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // Upload da foto se houver
      let uploadedPhotoUrl = photoUrl;
      if (photoFile) {
        uploadedPhotoUrl = await handlePhotoUpload(photoFile);
        if (!uploadedPhotoUrl) {
          isSubmittingRef.current = false;
          setLoading(false);
          return;
        }
      }

      // Combinar ingredientes e embalagens com item_type
      const allRecipe = [
        ...recipeIngredients
          .filter((r) => r.ingredient_id && r.quantity)
          .map((r) => ({
            ingredient_id: r.ingredient_id,
            quantity: parseFloat(r.quantity),
            item_type: "ingredient",
          })),
        ...recipePackaging
          .filter((r) => r.ingredient_id && r.quantity)
          .map((r) => ({
            ingredient_id: r.ingredient_id,
            quantity: parseFloat(r.quantity),
            item_type: "packaging",
          })),
      ];

      const isPizza = productType === "pizza";
      // Pra pizza, sale_price = MAIOR preço entre os tamanhos (mantém compat com componentes legados)
      let effectiveSalePrice = salePrice ? parseFloat(salePrice) : null;
      if (isPizza) {
        const numericPrices = Object.values(pizzaPrices).map(parsePizzaPrice).filter(p => p > 0);
        effectiveSalePrice = numericPrices.length > 0 ? Math.max(...numericPrices) : 0;
      }
      // Preço "de" só faz sentido se > sale_price. Nulificar caso contrário
      // (evita "de R$ 50 por R$ 60" sem sentido). SÓ pra product_type='produto'
      // — combo tem simple/combo, receita não vai pra cardápio, pizza tem preço
      // por tamanho. Guard defensivo cobre stale state (dono digitou o "de",
      // mudou tipo pra combo, o form escondeu switch mas state ainda tinha valor).
      let normalizedOriginalPrice = null;
      if (isPromotional && productType === "produto" && originalPrice) {
        const orig = parseFloat(originalPrice);
        const sale = Number(effectiveSalePrice || 0);
        if (orig > 0 && orig > sale) normalizedOriginalPrice = orig;
      }
      const productData = {
        name,
        description: description || null,
        category: category || null,
        product_type: productType || "produto",
        sale_price: effectiveSalePrice,
        original_price: normalizedOriginalPrice,
        simple_price: simplePrice ? parseFloat(simplePrice) : null,
        combo_description: comboDescription || null,
        simple_description: simpleDescription || null,
        combo_label: comboLabel || null,
        simple_label: simpleLabel || null,
        simple_photo_url: simplePhotoUrl || null,
        combo_photo_url: comboPhotoUrl || null,
        photo_url: uploadedPhotoUrl || null,
        recipe: allRecipe,
        is_insumo: isPizza ? false : isInsumo,
        is_divisible: isPizza ? false : isDivisible,
        usa_balanca: !isPizza && saleUnit === "kg" && usaBalanca,
        tara_gramas: Math.round(Number(taraGramas) || 0),
        sale_unit: isPizza ? "un" : (saleUnit || "un"),
        available: isAvailable,
        order_steps: orderSteps,
        recipe_yield: recipeYield ? parseFloat(recipeYield) : null,
        recipe_yield_unit: recipeYieldUnit || null,
        linked_ingredient_id: linkedIngredientId || null,
        card_order: cardOrder || "combo_first",
        sync_order_steps_from_id: syncOrderStepsFromId || null,
        visible_modules: visibleModules,
        module_prices: modulePrices,
        print_config: printConfig,
        fiscal_config: fiscalConfig,
        barcode: limparCodigo(barcode) || null,
        ...(isPizza ? { pizza_recipes: pizzaRecipes } : {}),
      };

      let savedProductId = currentProductId;
      if (editMode) {
        await axios.put(
          `${API}/products/${currentProductId}`,
          productData,
          getAuthHeader()
        );
        toast.success("Produto atualizado!");
      } else {
        const res = await axios.post(`${API}/products`, productData, getAuthHeader());
        savedProductId = res.data?.id || savedProductId;
        toast.success("Produto criado!");
      }
      // Fotos extras e vídeo: gravadas à parte, só quando mudaram
      if (savedProductId && midiasOriginais.current !== null && JSON.stringify(midias) !== JSON.stringify(midiasOriginais.current)) {
        try {
          await axios.put(`${API}/products/${savedProductId}/midias`, midias, getAuthHeader());
          midiasOriginais.current = midias;
        } catch {
          toast.error("Produto salvo, mas as fotos extras e o vídeo não foram salvos. Tente de novo.");
        }
      }
      if (!isPizza && productType !== "receita" && ehUnidadePeso(saleUnit) && !isDivisible) {
        toast.warning(AVISO_PESO_SEM_DIVISIVEL(saleUnit), { duration: 7000 });
      }

      // Pizza NOVA: chamar auto-link ANTES de gravar preços. Sem isso, o
      // handleSave cria produto com order_steps vazio (pizza sem etapas), e o
      // PUT /prices grava preços sem referência. O wizard rápido já fazia isso
      // (linha ~7157); pareamos aqui pra que o formulário completo tenha o
      // MESMO comportamento — cria as 3 etapas pizza_* (Tamanho/Sabores/Bordas)
      // automaticamente. Idempotente: se order_steps já tem pizza_size, o backend
      // pula (ex.: usuário editou etapas manualmente antes de salvar).
      if (isPizza && savedProductId && !editMode) {
        const sizesPayload = (pizzaConfig.sizes || [])
          .filter((s) => !pizzaPricesExcluded.has(s.id))
          .map((s) => ({
            id: s.id, name: s.name,
            slices: s.slices, max_flavors: s.max_flavors,
          }));
        const pricesPayload = {};
        Object.entries(pizzaPrices).forEach(([sid, v]) => {
          if (pizzaPricesExcluded.has(sid)) return;
          const n = parsePizzaPrice(v);
          if (n > 0) pricesPayload[sid] = n;
        });
        try {
          await axios.post(
            `${API}/pizza/products/${savedProductId}/auto-link`,
            { sizes: sizesPayload, prices_by_size: pricesPayload },
            getAuthHeader(),
          );
        } catch (e) {
          console.error("[Pizza] auto-link no handleSave falhou:", e);
          toast.error("Produto criado, mas falhou ao configurar Personalização");
        }
      }

      // Pizza: persistir/atualizar matriz de preços por tamanho.
      // - Criação (novo produto): o auto-link acima já gravou os preços do
      //   próprio sabor; este PUT é um no-op safe (idempotente).
      // - Edição: atualiza preços dos tamanhos existentes.
      if (isPizza && savedProductId) {
        const pricesPayload = {};
        Object.entries(pizzaPrices).forEach(([sid, v]) => { pricesPayload[sid] = parsePizzaPrice(v); });
        try {
          await axios.put(`${API}/pizza/products/${savedProductId}/prices`, { prices: pricesPayload }, getAuthHeader());
        } catch (e) {
          console.error("[Pizza] erro ao gravar preços:", e);
          toast.error("Produto salvo, mas erro ao atualizar preços da pizza");
        }
      }

      resetForm();
      setOpen(false);
      fetchProducts();
      fetchCategories();
    } catch (error) {
      toast.error(mensagemDeErro(error, "Erro ao salvar produto"));
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const fetchArchivedProducts = async () => {
    try {
      setArchivedLoading(true);
      const res = await axios.get(`${API}/products/archived`, getAuthHeader());
      setArchivedProducts(res.data);
    } catch (error) {
      console.error("Erro ao carregar arquivados:", error);
      toast.error(mensagemDeErro(error, "Não foi possível carregar os arquivados"));
    } finally {
      setArchivedLoading(false);
      setArquivadosCarregados(true);
    }
  };

  const handleRestoreProduct = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/restore`, {}, getAuthHeader());
      toast.success("Produto restaurado!");
      setArchivedProducts(prev => prev.filter(p => p.id !== productId));
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao restaurar produto");
    }
  };

  const confirmDelete = (id, name) => {
    setProductToDelete({ id, name });
  };

  // Alterna o flag 'hidden' do produto — esconde ele dos módulos de venda
  // (Cardápio Público, Delivery, Mesas, Balcão), mas mantém visível para admin.
  const toggleProductHidden = async (product, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    const newHidden = !(product.hidden === true);
    setProducts(prev => prev.map(p =>
      p.id === product.id ? { ...p, hidden: newHidden } : p
    ));
    try {
      await axios.put(`${API}/products/${product.id}`, {
        ...product,
        hidden: newHidden,
      }, getAuthHeader());
      toast.success(newHidden ? `${product.name} escondido dos módulos de venda` : `${product.name} visível novamente`);
    } catch (error) {
      setProducts(prev => prev.map(p =>
        p.id === product.id ? { ...p, hidden: !newHidden } : p
      ));
      toast.error("Erro ao atualizar visibilidade");
    }
  };

  // Função para alternar disponibilidade do produto rapidamente (otimista)
  const toggleProductAvailability = async (product, e) => {
    e.stopPropagation();
    const newAvailability = product.available === false ? true : false;

    // Atualização otimista - muda na tela IMEDIATAMENTE
    setProducts(prev => prev.map(p =>
      p.id === product.id ? { ...p, available: newAvailability } : p
    ));

    // Atualiza no backend em background
    try {
      await axios.put(`${API}/products/${product.id}`, {
        ...product,
        available: newAvailability
      }, getAuthHeader());
    } catch (error) {
      // Se falhar, reverte a mudança
      setProducts(prev => prev.map(p =>
        p.id === product.id ? { ...p, available: !newAvailability } : p
      ));
      toast.error("Erro ao atualizar disponibilidade");
    }
  };

  // Função para abrir o dialog de duplicação
  const openDuplicateDialog = (product) => {
    setProductToDuplicate(product);
    setDuplicateName(`${product.name} (Cópia)`);
    setDuplicateDialogOpen(true);
  };

  // Função para duplicar o produto
  const handleDuplicateProduct = async () => {
    if (!productToDuplicate || !duplicateName.trim()) {
      toast.error("Digite um nome para o novo produto");
      return;
    }

    // Verificar se já existe produto com mesmo nome na mesma categoria
    const existingProduct = products.find(
      p => p.name.toLowerCase() === duplicateName.trim().toLowerCase() && 
           p.category === productToDuplicate.category
    );

    if (existingProduct) {
      toast.error("Já existe um produto com este nome nesta categoria");
      return;
    }

    setDuplicatingProduct(true);

    try {
      const isPizza = productToDuplicate.product_type === "pizza";
      const productData = {
        name: duplicateName.trim(),
        description: productToDuplicate.description || null,
        category: productToDuplicate.category || null,
        product_type: productToDuplicate.product_type || "produto",
        sale_price: productToDuplicate.sale_price || null,
        // Copiar preço "de" também (feature v1.5.282+)
        original_price: productToDuplicate.original_price || null,
        photo_url: null, // NÃO duplicar a foto
        recipe: productToDuplicate.recipe || [],
        is_insumo: productToDuplicate.is_insumo || false,
        is_divisible: productToDuplicate.is_divisible || false,
        // Sem a unidade, a cópia de "Picanha (kg)" nascia por unidade (30/09/2026)
        sale_unit: isPizza ? "un" : (productToDuplicate.sale_unit || "un"),
        order_steps: productToDuplicate.order_steps || [],
      };

      const createRes = await axios.post(`${API}/products`, productData, getAuthHeader());
      const newProduct = createRes.data;

      // Pizza: também duplica matriz de preços por tamanho.
      // Bordas e tamanhos são UNIVERSAIS — não precisam ser duplicados (são compartilhados entre todas as pizzas)
      if (isPizza && newProduct?.id) {
        try {
          const pricesRes = await axios.get(`${API}/pizza/products/${productToDuplicate.id}/prices`, getAuthHeader());
          const sourcePrices = pricesRes.data || {};
          if (Object.keys(sourcePrices).length > 0) {
            await axios.put(`${API}/pizza/products/${newProduct.id}/prices`, { prices: sourcePrices }, getAuthHeader());
          }
        } catch (e) {
          console.error("[Pizza] erro ao copiar preços do produto duplicado:", e);
          toast.error("Produto duplicado, mas falhou ao copiar os preços por tamanho");
        }
      }

      toast.success(`Produto "${duplicateName}" criado com sucesso!`);
      setDuplicateDialogOpen(false);
      setProductToDuplicate(null);
      setDuplicateName("");
      fetchProducts();
    } catch (error) {
      console.error("Erro ao duplicar produto:", error);
      toast.error(mensagemDeErro(error, "Erro ao duplicar produto"));
    } finally {
      setDuplicatingProduct(false);
    }
  };

  // Funções para gerenciar etapas de pedido
  const addOrderStep = () => {
    setOrderSteps([...orderSteps, {
      name: "",
      calculation_type: "soma",
      min_selections: 0,
      max_selections: 0,
      items: [],
      combo_only: false,  // Se true, etapa só aparece para combo (ex: bebida)
      default_item_id: null,  // ID do item pré-selecionado (opcional)
      sync_prices: true  // Se true, sincroniza preços com PRODUTOS automaticamente
    }]);
  };

  // Mover etapa para cima ou para baixo
  const moveOrderStep = (stepIndex, direction) => {
    const newSteps = [...orderSteps];
    const newIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
    
    if (newIndex < 0 || newIndex >= newSteps.length) return;
    
    // Trocar posições
    [newSteps[stepIndex], newSteps[newIndex]] = [newSteps[newIndex], newSteps[stepIndex]];
    setOrderSteps(newSteps);
    toast.success(`Etapa movida para posição ${newIndex + 1}`);
  };

  const updateOrderStep = (index, field, value) => {
    const newSteps = [...orderSteps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setOrderSteps(newSteps);
  };

  const removeOrderStep = (index) => {
    setOrderSteps(orderSteps.filter((_, i) => i !== index));
  };

  const addItemToStep = (stepIndex, productId) => {
    if (!productId) return;
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const newSteps = [...orderSteps];
    const items = newSteps[stepIndex].items || [];
    
    // Verificar se o produto já está na lista
    if (items.some(item => item.product_id === productId)) {
      toast.error("Este produto já está na etapa");
      return;
    }
    
    newSteps[stepIndex].items = [...items, {
      product_id: productId,
      product_name: product.name,
      price_override: getDisplayPrice(product) || 0
    }];
    setOrderSteps(newSteps);
  };

  const updateStepItem = (stepIndex, itemIndex, field, value) => {
    const newSteps = [...orderSteps];
    newSteps[stepIndex].items[itemIndex] = { 
      ...newSteps[stepIndex].items[itemIndex], 
      [field]: value 
    };
    setOrderSteps(newSteps);
  };

  const removeStepItem = (stepIndex, itemIndex) => {
    const newSteps = [...orderSteps];
    newSteps[stepIndex].items = newSteps[stepIndex].items.filter((_, i) => i !== itemIndex);
    setOrderSteps(newSteps);
  };

  // Mover item para cima ou para baixo na etapa
  const moveStepItem = (stepIndex, itemIndex, direction) => {
    const newSteps = [...orderSteps];
    const items = [...newSteps[stepIndex].items];
    const newIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;
    
    if (newIndex < 0 || newIndex >= items.length) return;
    
    // Trocar posições
    [items[itemIndex], items[newIndex]] = [items[newIndex], items[itemIndex]];
    newSteps[stepIndex].items = items;
    setOrderSteps(newSteps);
  };

  // Ordenar itens da etapa alfabeticamente
  const sortStepItems = (stepIndex, order = 'asc') => {
    const newSteps = [...orderSteps];
    const items = [...(newSteps[stepIndex].items || [])];
    
    items.sort((a, b) => {
      const nameA = a.product_name.toLowerCase();
      const nameB = b.product_name.toLowerCase();
      if (order === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
    
    newSteps[stepIndex].items = items;
    setOrderSteps(newSteps);
    toast.success(`Itens ordenados ${order === 'asc' ? 'A→Z' : 'Z→A'}`);
  };

  // Ordenar itens por preço
  const sortStepItemsByPrice = (stepIndex, order = 'asc') => {
    const newSteps = [...orderSteps];
    const items = [...(newSteps[stepIndex].items || [])];
    
    items.sort((a, b) => {
      const priceA = a.price_override || 0;
      const priceB = b.price_override || 0;
      if (order === 'asc') {
        return priceA - priceB;
      } else {
        return priceB - priceA;
      }
    });
    
    newSteps[stepIndex].items = items;
    setOrderSteps(newSteps);
    toast.success(`Itens ordenados por preço ${order === 'asc' ? '↑' : '↓'}`);
  };

  const addRecipeItem = (type) => {
    if (type === "ingredient") {
      setRecipeIngredients([...recipeIngredients, { ingredient_id: "", quantity: "" }]);
    } else {
      setRecipePackaging([...recipePackaging, { ingredient_id: "", quantity: "" }]);
    }
  };

  // Adiciona ingrediente direto da busca (sem etapa "vazio + preencher")
  const addRecipeItemDirect = (ingredientId, type) => {
    if (!ingredientId) return;
    const newItem = { ingredient_id: ingredientId, quantity: "1" };
    if (type === "ingredient") {
      // Filtra qualquer "row vazia" pendente e adiciona o novo item já preenchido
      setRecipeIngredients(prev => [...prev.filter(r => r.ingredient_id), newItem]);
    } else {
      setRecipePackaging(prev => [...prev.filter(r => r.ingredient_id), newItem]);
    }
  };

  const removeRecipeItem = (index, type) => {
    if (type === "ingredient") {
      setRecipeIngredients(recipeIngredients.filter((_, i) => i !== index));
    } else {
      setRecipePackaging(recipePackaging.filter((_, i) => i !== index));
    }
  };

  const updateRecipeItem = (index, field, value, type) => {
    if (type === "ingredient") {
      const updated = [...recipeIngredients];
      updated[index][field] = value;
      setRecipeIngredients(updated);
    } else {
      const updated = [...recipePackaging];
      updated[index][field] = value;
      setRecipePackaging(updated);
    }
  };

  const getIngredientDetails = (ingredientId) => {
    const ing = ingredients.find((i) => i.id === ingredientId);
    return ing || { name: "-", unit: "", average_price: 0 };
  };

  const getIngredientUnit = (ingredient) => {
    if (ingredient.units_per_package && ingredient.units_per_package > 0) {
      return "un";
    }
    if (ingredient.unit_weight && ingredient.unit_weight > 0) {
      // kg+uw: receita em un | un+uw: receita em kg
      return ingredient.unit === 'un' ? 'kg' : 'un';
    }
    return ingredient.unit;
  };

  // Texto de fracionamento: "1 un = 0,130 kg" — mostra a relacao para qualquer unit com unit_weight
  const getFractionText = (ingredient) => {
    if (!ingredient) return "";
    const uw = parseFloat(ingredient.unit_weight) || 0;
    const baseUnit = ingredient.unit || "un";
    if (uw > 0) {
      return `1 un = ${uw.toFixed(3).replace('.', ',')} kg`;
    }
    return `1 ${baseUnit} = 1 ${baseUnit}`;
  };

  /** Preço de PERSONALIZAÇÃO->Simples: combo usa simple_price (fallback sale_price); não-combo usa sale_price. Para CMV usa-se sempre o Simples. */
  const getDisplayPrice = (product) => {
    if (!product) return 0;
    const isCombo = String(product.product_type || "").toLowerCase() === "combo";
    const raw = isCombo ? (product.simple_price ?? product.sale_price) : product.sale_price;
    const n = typeof raw === "number" ? raw : parseFloat(raw);
    return !isNaN(n) ? n : 0;
  };

  // Custos separados de ingredientes e embalagens.
  // MESMA conta do backend (api/v1/products/cmv.py) — se mudar aqui, mude lá:
  // o card da lista usa o CMV gravado pelo backend e a Ficha Técnica usa este
  // cálculo; critérios diferentes fazem a mesma comida ter dois CMVs na tela.
  const calculateRecipeCosts = (recipe) => {
    let ingredientsCost = 0;
    let packagingCost = 0;

    recipe.forEach(item => {
      const ingredient = getIngredientDetails(item.ingredient_id);
      const avgPrice = ingredient.average_price || 0;
      const unitWeight = ingredient.unit_weight || 0;
      const unit = ingredient.unit || '';
      const quantity = Number(item.quantity) || 0;
      // Fator de perda: paga pelo bruto, aproveita so (100-loss)% — custo real sobe.
      const loss = Number(ingredient.loss_percent) || 0;
      const lossFactor = (loss > 0 && loss < 100) ? 1 / (1 - loss / 100) : 1;

      // Branch por unit + unit_weight:
      // - unit=kg + uw>0: receita em UNIDADES (1 hamburguer = 0.130 kg) → avg × uw × qty
      // - unit=un + uw>0: receita em KG (0.02 kg = 20g) → (avg / uw) × qty
      // - sem uw: receita na unidade base do estoque
      let baseCost;
      if (unit === 'kg' && unitWeight > 0) {
        baseCost = avgPrice * unitWeight * quantity;
      } else if (unit === 'un' && unitWeight > 0) {
        baseCost = (avgPrice / unitWeight) * quantity;
      } else {
        baseCost = avgPrice * quantity;
      }
      const itemCost = baseCost * lossFactor;

      if (item.item_type === "ingredient") {
        ingredientsCost += itemCost;
      } else if (item.item_type === "packaging") {
        packagingCost += itemCost;
      }
    });

    return { ingredientsCost, packagingCost };
  };

  const toggleProduct = (productId) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  // Ordenar produtos - MEMOIZADO
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];
    
    switch (sortBy) {
      case "alfabetica-az":
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case "alfabetica-za":
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case "cmv-maior":
        return sorted.sort((a, b) => b.cmv - a.cmv);
      case "cmv-menor":
        return sorted.sort((a, b) => a.cmv - b.cmv);
      case "margem-maior":
        return sorted.sort((a, b) => (b.profit_margin || 0) - (a.profit_margin || 0));
      case "margem-menor":
        return sorted.sort((a, b) => (a.profit_margin || 0) - (b.profit_margin || 0));
      case "preco-maior":
        return sorted.sort((a, b) => (b.sale_price || 0) - (a.sale_price || 0));
      case "preco-menor":
        return sorted.sort((a, b) => (a.sale_price || 0) - (b.sale_price || 0));
      default:
        return sorted;
    }
  }, [filteredProducts, sortBy]);
  
  // Produtos paginados
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedProducts, currentPage, itemsPerPage]);

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, performanceFilter, filterName, filtros]);


  const etapasSynced = productType === "combo" && !!syncOrderStepsFromId;

  // Retrair sidebar e esconder header quando tela de edição está aberta
  // Guarda como o menu estava ANTES de abrir o formulário e devolve igual ao
  // fechar — antes voltava sempre aberto, mesmo com o menu recolhido (e abria
  // a gaveta no celular).
  const prevOpenRef = useRef(false);
  const sidebarOpenRef = useRef(sidebarOpen);
  sidebarOpenRef.current = sidebarOpen;
  const sidebarAntesRef = useRef(true);
  useEffect(() => {
    if (open) {
      if (!prevOpenRef.current) sidebarAntesRef.current = sidebarOpenRef.current !== false;
      if (setSidebarOpen) setSidebarOpen(false);
      if (setHeaderHidden) setHeaderHidden(true);
    } else if (prevOpenRef.current) {
      // Só restaura o sidebar se o formulário estava aberto e fechou
      if (setSidebarOpen) setSidebarOpen(sidebarAntesRef.current);
      if (setHeaderHidden) setHeaderHidden(false);
    }
    prevOpenRef.current = open;
  }, [open, setSidebarOpen, setHeaderHidden]);

  if (open) {
    return (
      <div className="bg-background flex flex-row h-[calc(100vh)] overflow-hidden" data-testid="products-page">
          {/* === LEFT SIDEBAR: hint na aba Produto / resumo nas outras abas === */}
          <div className="w-72 flex-shrink-0 bg-card border-r hidden lg:flex flex-col h-full">
            {/* Sidebar header: back */}
            <div className="flex items-center px-3 py-2 border-b bg-muted/20 flex-shrink-0">
              <button type="button" onClick={() => { setOpen(false); resetForm(); }} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all text-sm">
                <ArrowLeft className="w-3.5 h-3.5" />
                Voltar
              </button>
            </div>

            {activeMainTab === "produto" ? (
              /* === Aba PRODUTO: hint sutil (a edição está no painel principal) === */
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-3 min-h-0">
                <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center">
                  <Package className="w-7 h-7 text-orange-500" />
                </div>
                <div className="max-w-[220px]">
                  <h4 className="text-sm font-bold text-foreground">Edite seu produto</h4>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    Use a aba <strong className="text-foreground">Produto</strong> ao lado para preencher foto, nome, preço, descrição e configurações.
                  </p>
                </div>
                <div className="pt-3 border-t border-border/40 max-w-[220px]">
                  <p className="text-[10px] text-muted-foreground/70 leading-snug">
                    Em <strong>Ficha Técnica</strong>, <strong>Personalização</strong> ou <strong>Impressão</strong>, o resumo aparece aqui.
                  </p>
                </div>
              </div>
            ) : (
              /* === Outras abas: resumo (Foto + Nome + Categoria + Tipo) === */
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                <header className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-orange-500" />
                  <h4 className="text-[10px] font-black tracking-[0.2em] uppercase text-foreground">Resumo</h4>
                  <span className="h-px flex-1 bg-border/50" />
                  {editMode && productCode && (
                    <span className="text-[9px] text-muted-foreground font-mono bg-muted/60 px-1.5 py-0.5 rounded leading-none">#{productCode}</span>
                  )}
                </header>

                {/* Foto (somente leitura) */}
                <div className="w-44 mx-auto">
                  <div className="relative w-44 h-44 rounded-xl border border-border/60 overflow-hidden bg-muted/40 flex items-center justify-center">
                    {(photoUrl || photoFile) ? (
                      <img
                        src={photoFile ? URL.createObjectURL(photoFile) : `/api${photoUrl}`}
                        alt={name || "Produto"}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 opacity-40">
                        <ImageOff className="w-8 h-8 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">Sem foto</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Nome */}
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Nome</Label>
                  <p className="text-sm font-bold text-foreground leading-tight break-words">
                    {name || <span className="text-muted-foreground italic font-normal">Sem nome</span>}
                  </p>
                </div>

                {/* Categoria */}
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Categoria</Label>
                  <p className="text-xs">
                    {category ? (
                      <span className="inline-block bg-muted/60 px-2 py-0.5 rounded-md font-medium">{category}</span>
                    ) : (
                      <span className="text-muted-foreground italic">Sem categoria</span>
                    )}
                  </p>
                </div>

                {/* Tipo */}
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Tipo</Label>
                  <p className="text-xs font-medium">
                    {productType === "combo" ? "🍟 Combo" : productType === "pizza" ? "🍕 Pizza" : productType === "receita" ? "📋 Receita" : "📦 Produto"}
                    {isInsumo && <span className="ml-1.5 text-[9px] font-black px-1.5 py-0.5 rounded leading-none bg-amber-500 text-white">INSUMO</span>}
                    {isDivisible && <span className="ml-1.5 text-[9px] font-black px-1.5 py-0.5 rounded leading-none bg-blue-500 text-white">DIVISÍVEL</span>}
                  </p>
                </div>

                {/* CTA: voltar para Produto */}
                <button
                  type="button"
                  onClick={() => setActiveMainTab("produto")}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 mt-2 rounded-lg border border-border/70 hover:border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/10 text-xs font-semibold text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400 transition-all"
                >
                  <Edit className="w-3 h-3" /> Editar dados do produto
                </button>
              </div>
            )}
          </div>

          {/* === RIGHT: Header + Tabs + Content === */}
          <div className="flex-1 flex flex-col min-w-0 bg-background">
            {/* Header bar — abaixo de md as abas descem para uma linha própria:
                lado a lado com o Salvar, elas empurravam o botão para fora da
                tela e não havia como salvar no celular nem no tablet. */}
            <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-x-3 gap-y-2 px-3 sm:px-5 py-2.5 border-b bg-background flex-shrink-0">
              {/* Left: mobile back */}
              <div className="flex items-center gap-2 lg:hidden">
                <button type="button" onClick={() => { setOpen(false); resetForm(); }} aria-label="Voltar" className="flex items-center gap-1 px-2 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 text-sm">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-bold">{editMode ? "Editar" : "Novo"}</span>
              </div>

              {/* Center: Tab navigation */}
              <div className="order-last md:order-none w-full md:w-auto flex gap-1 bg-muted/50 rounded-xl p-1 md:mx-auto">
                {[
                  { key: "produto", label: "Produto", icon: Package },
                  { key: "ficha", label: "Ficha Técnica", icon: ChefHat },
                  ...(productType !== "receita" ? [{ key: "personalizacao", label: "Personalização", icon: Layers }] : []),
                  // Receita é insumo interno: não vai pro cardápio, logo não emite nota.
                  ...(productType !== "receita" ? [{ key: "fiscal", label: "Nota Fiscal", icon: FileText }] : []),
                  ...(productType !== "receita" ? [{ key: "impressao", label: "Impressão", icon: Printer }] : []),
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeMainTab === tab.key;
                  return (
                    <button key={tab.key} type="button" onClick={() => setActiveMainTab(tab.key)}
                      aria-label={tab.label} title={tab.label}
                      className={`flex flex-1 md:flex-none items-center justify-center gap-2 px-3 xl:px-5 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${
                        isActive
                          ? "bg-background text-foreground shadow-sm border border-border/50"
                          : "text-muted-foreground hover:text-foreground hover:bg-background/40"
                      }`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                      {/* Só a aba ativa mostra o nome abaixo de xl: com os cinco
                          nomes a barra passava de 700px e cortava o Salvar. */}
                      <span className={isActive ? "inline" : "hidden xl:inline"}>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Right: Save button - always visible */}
              <Button type="button" onClick={(e) => { e.preventDefault(); document.getElementById('product-form')?.requestSubmit(); }} disabled={loading} className="h-9 px-4 sm:px-6 text-sm font-semibold shadow-sm flex-shrink-0 ml-auto md:ml-0">
                {loading ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>) : editMode ? "Salvar Produto" : "Criar Produto"}
              </Button>
            </div>

            {/* Tab content - scrollable */}
            <div className="flex-1 overflow-y-auto bg-background" style={{ overscrollBehavior: 'contain' }}>
            <form id="product-form" onSubmit={handleSubmit} className="max-w-5xl mx-auto p-3 sm:p-6 space-y-6">

              {/* Tab: Produto */}
              {activeMainTab === "produto" && (
                <div className="space-y-6">
                  {/* === SECTION 1: IDENTIDADE — Foto + Nome + Categoria + Tipo === */}
                  <section className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                    <header className="px-4 sm:px-6 py-3.5 border-b bg-gradient-to-r from-orange-50/80 to-amber-50/60 dark:from-orange-950/20 dark:to-amber-950/10 flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                        <Package className="w-4 h-4 text-orange-500" />
                      </div>
                      <div className="min-w-0 shrink-0">
                        <h3 className="text-sm font-bold text-foreground">Identidade</h3>
                        <p className="text-[11px] text-muted-foreground">Foto, nome, descrição, categoria e tipo</p>
                      </div>
                      {/* Link de divulgação no topo do cartão (24/10/2026): o cliente abre esta
                          URL direto no produto (Insta/WhatsApp/anúncios). Só em edição com nome
                          válido; renomear o produto muda o link. */}
                      <div className="flex-1 min-w-0 flex justify-end">
                        {editMode && name?.trim() && makeProductSlug(name) && (
                          <div className="flex items-center gap-2" title="Renomear o produto faz o link mudar — atualize seus posts.">
                            {/* Pequeno de propósito (24/10/2026): o foco é copiar, não ler o link */}
                            <span className="hidden lg:flex items-center gap-1 text-[9px] leading-[1.15] uppercase tracking-wider font-bold text-orange-700 dark:text-orange-400 w-[72px] shrink-0">
                              <Link2 className="w-3 h-3 shrink-0" /> <span>Link de divulgação</span>
                            </span>
                            <Input readOnly value={buildProductPublicUrl(name)} onClick={(e) => e.target.select()}
                              aria-label="Link de divulgação" data-e2e="link-divulgacao"
                              className="hidden md:block h-7 w-40 px-2 text-[10px] md:text-[10px] font-mono text-muted-foreground bg-white/60 dark:bg-zinc-900/60 select-all" />
                            <Button type="button" size="sm" data-e2e="copiar-link"
                              className="h-8 shrink-0 gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold"
                              onClick={async () => {
                                const url = buildProductPublicUrl(name);
                                try {
                                  await navigator.clipboard.writeText(url);
                                  toast.success("Link copiado! Se renomear o produto, o link muda.");
                                } catch {
                                  toast.error("Não foi possível copiar — selecione manualmente");
                                }
                              }}>
                              <Copy className="w-3.5 h-3.5" /> Copiar link
                            </Button>
                          </div>
                        )}
                      </div>
                      {editMode && productCode && (
                        <span className="text-[10px] text-muted-foreground font-mono bg-muted/60 px-2 py-0.5 rounded">#{productCode}</span>
                      )}
                    </header>
                    <div className="p-4 sm:p-6 grid md:grid-cols-[240px_1fr] gap-6">
                      {/* Foto grande */}
                      <div>
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Foto do produto</Label>
                        <div className="relative w-full aspect-square rounded-2xl border-2 border-dashed border-border/60 overflow-hidden bg-muted/40 mt-1.5 group">
                          <NumeroDaMidia n={1} tipo="foto" vazio={!(photoUrl || photoFile)} />
                          {videoPrincipal !== null ? (
                            <div className="w-full h-full grid place-items-center"><MidiaVazia enviando={videoPrincipal} /></div>
                          ) : (photoUrl || photoFile) ? (
                            <>
                              <img
                                src={photoFile ? URL.createObjectURL(photoFile) : `/api${photoUrl}`}
                                alt="Preview"
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
              {/* z-20: o label "Trocar foto" abaixo é `absolute inset-0` e vem
                  depois no DOM, então cobria este botão — clicar na lixeira
                  abria o seletor de arquivo em vez de remover a foto. */}
                              <Button type="button" variant="destructive" size="icon"
                                title="Remover foto"
                                className="absolute top-2 right-2 z-20 h-9 w-9 sm:h-7 sm:w-7 shadow-md [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPhotoFile(null);
                                  setPhotoUrl("");
                                  // limpa o input pra poder re-selecionar o MESMO arquivo depois
                                  const inp = document.getElementById("photo-main");
                                  if (inp) inp.value = "";
                                }}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                              <label htmlFor="photo-main" className="absolute inset-0 z-10 cursor-pointer flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-medium gap-1.5">
                                <Camera className="w-4 h-4" /> Trocar foto
                              </label>
                            </>
                          ) : (
                            <label htmlFor="photo-main" className="cursor-pointer flex flex-col items-center justify-center w-full h-full hover:bg-muted/80 transition-colors p-4">
                              {/* O mesmo quadro das fotos 2 e 3; com vídeo já no produto (ou 2 e 3
                                  ocupadas), só foto */}
                              <MidiaVazia soFoto={(midias.extras || []).some((m) => m.tipo === "video") || (midias.extras || []).length >= 2} />
                            </label>
                          )}
                        </div>
                        <Input id="photo-main" type="file" accept={(photoUrl || photoFile) || (midias.extras || []).some((m) => m.tipo === "video") || (midias.extras || []).length >= 2 ? FOTO_ACEITA : `${FOTO_ACEITA},${VIDEO_ACEITO}`}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            e.target.value = "";
                            if (file && ehGif(file)) { toast.error(SEM_GIF); return; }
                            if (!file) return;
                            if (!ehVideo(file)) { setPhotoFile(file); return; }
                            // Vídeo no quadro da foto 1 (24/10/2026): a capa vira a foto principal
                            // (Balcão, KDS e impressão usam foto) e o vídeo entra como destaque
                            const extras = midias.extras || [];
                            if (extras.some((m) => m.tipo === "video")) { toast.error("Este produto já tem vídeo. Tire o vídeo de antes para colocar outro."); return; }
                            if (extras.length >= 2) { toast.error("As fotos 2 e 3 estão ocupadas: tire uma para colocar o vídeo."); return; }
                            try {
                              const v = await enviarVideo(file, setVideoPrincipal);
                              setPhotoFile(null);
                              setPhotoUrl(v.poster);
                              setMidias((m) => ({ ...m, extras: [{ tipo: "video", url: v.url, poster: v.poster }, ...(m.extras || [])], video_destaque: true }));
                              toast.success("Vídeo enviado: ele toca no cardápio e a capa virou a foto principal");
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || err.message || "Não foi possível enviar o vídeo");
                            } finally {
                              setVideoPrincipal(null);
                            }
                          }}
                          className="hidden" />
                        <FotosProntas nome={name} fotoAtual={photoUrl} temArquivo={!!photoFile} onEscolher={setPhotoUrl} novo={!editMode}
                          onEnviarPropria={() => document.getElementById("photo-main")?.click()} />
                        <MidiasExtras valor={midias} onChange={setMidias} temFotoPrincipal={!!(photoUrl || photoFile)} />
                      </div>

                      {/* Form (right side) */}
                      <div className="space-y-4">
                        {/* Nome */}
                        <div>
                          <Label htmlFor="name-main" className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Nome do produto</Label>
                          <Input
                            id="name-main"
                            data-testid="product-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex: X-Burger Especial"
                            required
                            className="mt-1.5 h-11 text-base font-semibold"
                          />
                        </div>

                        {/* Descrição dentro da Identidade, logo abaixo do nome (dono, 24/10/2026) */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="description-main" className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Descrição <span className="normal-case font-normal">· aparece para o cliente no cardápio</span></Label>
                            <span className="text-[10px] text-muted-foreground font-mono">{description.length}/255</span>
                          </div>
                        <Textarea
                          id="description-main"
                          data-testid="product-description-input"
                          value={description}
                          onChange={(e) => { if (e.target.value.length <= 255) setDescription(e.target.value); setAiSuggestions(null); }}
                          placeholder="Descreva o produto para seus clientes (sabor, ingredientes, diferencial)…"
                          className="text-sm resize-none min-h-0"
                          rows={3}
                          maxLength={255}
                        />
                        <button
                          type="button"
                          disabled={aiLoading || description.trim().length < 10}
                          title={description.trim().length < 10 ? "Escreva ao menos 10 caracteres para melhorar com IA" : "Gerar variações com IA"}
                          onClick={async () => {
                            if (description.trim().length < 10) return;
                            setAiLoading(true);
                            setAiSuggestions(null);
                            try {
                              const token = localStorage.getItem("token");
                              const res = await axios.post("/api/products/improve-description", { name, description }, { headers: { Authorization: `Bearer ${token}` } });
                              setAiSuggestions(res.data);
                            } catch (err) {
                              toast.error(mensagemDeErro(err, "Erro ao melhorar descrição"));
                            } finally {
                              setAiLoading(false);
                            }
                          }}
                          className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white text-sm font-semibold rounded-lg shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400"
                        >
                          {aiLoading ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Gerando…</>
                          ) : (
                            <><Sparkles className="w-4 h-4" /> Melhorar com IA</>
                          )}
                        </button>
                        {/* AI Suggestions Dialog */}
                        <Dialog open={!!aiSuggestions} onOpenChange={(open) => { if (!open) setAiSuggestions(null); }}>
                          <DialogContent className="sm:max-w-lg">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-violet-500" />
                                Sugestões de Descrição
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 mt-2">
                              <div className="bg-muted/50 rounded-lg p-3">
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Descrição atual</span>
                                <p className="text-sm mt-1 text-muted-foreground">{description}</p>
                              </div>
                              {aiSuggestions && aiSuggestions.versions && aiSuggestions.versions.map((v, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => { setDescription(v.text); setAiSuggestions(null); toast.success("Descrição aplicada!"); }}
                                  className="w-full text-left p-4 rounded-xl border-2 border-border/50 hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-all group"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                                      i === 0 ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400' :
                                      i === 1 ? 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400' :
                                      'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400'
                                    }`}>{v.style}</span>
                                    <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity font-medium">Clique para aplicar</span>
                                  </div>
                                  <p className="text-sm text-foreground leading-relaxed">{v.text}</p>
                                  <span className="text-[10px] text-muted-foreground mt-1 inline-block">{v.text.length}/255 caracteres</span>
                                </button>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                        </div>

                        {/* Categoria + Tipo lado a lado */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="category-main" className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Categoria</Label>
                            {/* Categoria obrigatória (dono, 26/10/2026): sem a opção "Sem categoria" */}
                            <Select value={category || ""} onValueChange={setCategory}>
                              <SelectTrigger id="category-main" className={`h-10 mt-1.5 ${!category && productType !== "receita" ? "border-amber-500" : ""}`}>
                                <SelectValue placeholder="Escolha a categoria" />
                              </SelectTrigger>
                              <SelectContent>
                                {/* Categoria do produto que não está mais na lista (renomeada
                                    ou apagada em outro aparelho): entra como opção mesmo assim.
                                    Sem isto o Select ficava VAZIO e o lojista salvava o produto
                                    achando que estava tudo certo — perdendo o vínculo. */}
                                {category && !categories.some((c) => c.name === category) && (
                                  <SelectItem value={category}>{category} (categoria não encontrada)</SelectItem>
                                )}
                                {categories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Tipo</Label>
                            {editMode && (productType === "combo" || productType === "pizza") ? (
                              <div className="mt-1.5 h-10 px-3 flex items-center bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-md text-sm">
                                <span className="text-orange-700 dark:text-orange-400 font-semibold">
                                  {productType === "pizza" ? "🍕 Pizza" : "🍟 Combo"}
                                </span>
                                <span className="ml-auto text-[10px] text-orange-500/70">fixo</span>
                              </div>
                            ) : (
                              <Select
                                value={productType}
                                onValueChange={(value) => {
                                  if ((value === "combo" || value === "receita" || value === "pizza") && productType !== value) {
                                    setConfirmTypeChange(value);
                                  } else {
                                    setProductType(value);
                                  }
                                }}
                                disabled={editMode && (productType === "combo" || productType === "pizza")}
                              >
                                <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="produto">📦 Produto</SelectItem>
                                  <SelectItem value="combo">🍟 Combo</SelectItem>
                                  <SelectItem value="pizza">🍕 Pizza</SelectItem>
                                  <SelectItem value="receita">📋 Receita</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>

                        <CampoCodigoBarras value={barcode} onChange={setBarcode} />
                      </div>
                    </div>
                  </section>

                  {/* Dialog: confirma mudança para Combo / Receita (irreversível) */}
                  <Dialog open={!!confirmTypeChange} onOpenChange={(open) => { if (!open) setConfirmTypeChange(null); }}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                          <AlertTriangle className="w-5 h-5" />
                          {confirmTypeChange === "combo"
                            ? "Mudar para Combo é irreversível"
                            : "Mudar para Receita é irreversível"}
                        </DialogTitle>
                        <DialogDescription className="pt-2 leading-relaxed">
                          {confirmTypeChange === "combo" ? (
                            <>Após salvar este produto como <strong className="text-foreground">🍟 Combo</strong>, o tipo <strong className="text-foreground">não poderá mais ser alterado</strong>. Combos têm estrutura própria (etapas, adicionais, preços por canal) que não podem ser desfeitas para produto simples.</>
                          ) : (
                            <>Após salvar este item como <strong className="text-foreground">📋 Receita</strong>, o tipo <strong className="text-foreground">não poderá mais ser alterado</strong>. Receitas são insumos internos (não aparecem no cardápio) e usam a ficha técnica para compor outros produtos.</>
                          )}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-xs text-orange-800 dark:text-orange-300 leading-relaxed">
                        <strong>Tem certeza?</strong> Se quiser experimentar primeiro, recomendamos duplicar este produto e mudar a cópia para {confirmTypeChange === "combo" ? "Combo" : "Receita"}.
                      </div>
                      <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => setConfirmTypeChange(null)}>
                          Cancelar
                        </Button>
                        <Button
                          type="button"
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={() => {
                            const newType = confirmTypeChange;
                            setProductType(newType);
                            setConfirmTypeChange(null);
                            toast.success(`Tipo alterado para ${newType === "combo" ? "Combo" : "Receita"}`);
                          }}
                        >
                          Sim, mudar para {confirmTypeChange === "combo" ? "Combo" : "Receita"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {/* === SECTION 2.PIZZA: ATALHO pra editar preço deste sabor por tamanho.
                      O salvar do produto chama PUT /pizza/products/:id/prices que faz
                      dual-write (matriz legada + order_steps[pizza_flavor] da pizza-mãe).
                      Editar a LISTA de tamanhos/bordas continua só na aba Personalização. */}
                  {productType === "pizza" && (
                    <section className="bg-card rounded-2xl border-2 border-orange-500/30 shadow-sm overflow-hidden">
                      <header className="px-4 sm:px-6 py-3.5 border-b bg-gradient-to-r from-orange-50/80 to-amber-50/60 dark:from-orange-950/20 dark:to-amber-950/10 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
                          <Pizza className="w-4 h-4 text-orange-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-foreground">Preço deste sabor por tamanho</h3>
                          <p className="text-[11px] text-muted-foreground">Atalho rápido — edite os preços direto aqui. Tamanhos/bordas/regra editam-se em <strong>Personalização</strong></p>
                        </div>
                      </header>
                      <div className="p-4 sm:p-6 space-y-2">
                        {pizzaSizesEffective.length === 0 ? (
                          <div className="rounded-lg border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center">
                            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-1">Cadastre tamanhos primeiro</p>
                            <p className="text-xs text-muted-foreground">
                              Vá em <strong>Personalização</strong> (aba acima) e adicione tamanhos na etapa <strong>Tamanho</strong>.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            {pizzaSizesEffective.map(s => {
                              const v = pizzaPrices[s.id];
                              const numV = parsePizzaPrice(v);
                              const has = numV > 0;
                              return (
                                <div key={s.id} className="flex items-center gap-2 rounded-lg bg-muted/40 border border-border/60 px-3 py-2 hover:border-orange-300 transition-colors">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold leading-tight truncate">{s.name}</p>
                                    <p className="text-[10px] text-muted-foreground leading-tight">
                                      {s.slices ? `${s.slices} fatias · ` : ""}até {s.max_flavors} sabor(es)
                                    </p>
                                  </div>
                                  <div className={`flex items-center h-9 rounded-lg border overflow-hidden ${has ? "border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20" : "border-border bg-background"}`}>
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${has ? "text-orange-700 dark:text-orange-400" : "text-muted-foreground"} border-r ${has ? "border-orange-300/40 dark:border-orange-800/40" : "border-border/40"}`}>
                                      R$
                                    </span>
                                    <PizzaPriceInput
                                      value={numV}
                                      onChange={(newNum) => {
                                        const v = newNum > 0 ? String(newNum).replace(".", ",") : "";
                                        setPizzaPrices(prev => ({ ...prev, [s.id]: v }));
                                        // Sync atalho → Personalização ao vivo: quando o produto
                                        // atual é a pizza-mãe (tem item dele no step pizza_flavor),
                                        // atualiza orderSteps[pizza_flavor].items[i].prices_by_size
                                        // pra o FlavorEditor refletir sem precisar trocar de aba.
                                        if (currentInPizzaFlavor) {
                                          setOrderSteps(prev => prev.map(step => {
                                            if (String(step?.kind || "") !== "pizza_flavor") return step;
                                            return {
                                              ...step,
                                              items: (step.items || []).map(it => {
                                                if (it.product_id !== currentProductId) return it;
                                                return { ...it, prices_by_size: { ...(it.prices_by_size || {}), [s.id]: newNum } };
                                              }),
                                            };
                                          }));
                                        }
                                      }}
                                      placeholder="0,00"
                                      className={`h-9 w-24 text-sm text-right tabular-nums font-semibold border-0 px-2 bg-transparent ${has ? "text-orange-700 dark:text-orange-400" : "text-muted-foreground"}`}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Preço & Margem e Canais de Venda lado a lado (dono, 24/10/2026) */}
                  {/* Os dois com a mesma altura (dono, 26/10/2026): a coluna estica o mais baixo */}
                  <div className={`grid gap-6 items-stretch ${productType !== "receita" && productType !== "pizza" ? "xl:grid-cols-2" : ""}`}>
                  {/* === SECTION 2: PREÇO & MARGEM === */}
                  {productType !== "receita" && productType !== "pizza" && (() => {
                    const currentPrice = parseFloat(productType === "combo" ? simplePrice : salePrice) || 0;
                    const combinedRecipe = [
                      ...recipeIngredients.filter(i => i.ingredient_id).map(i => ({ ...i, quantity: parseFloat(i.quantity) || 0, item_type: "ingredient" })),
                      ...recipePackaging.filter(i => i.ingredient_id).map(i => ({ ...i, quantity: parseFloat(i.quantity) || 0, item_type: "packaging" })),
                    ];
                    const { ingredientsCost, packagingCost } = calculateRecipeCosts(combinedRecipe);
                    const totalCost = ingredientsCost + packagingCost;
                    const profit = currentPrice - totalCost;
                    const cmvPct = currentPrice > 0 ? ((totalCost / currentPrice) * 100).toFixed(1) : null;
                    const cmvNum = cmvPct ? parseFloat(cmvPct) : 0;
                    const cmvColor = cmvNum <= 30 ? 'emerald' : cmvNum <= 40 ? 'yellow' : 'red';
                    return (
                      <section className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                        <header className="px-4 sm:px-6 py-3.5 border-b bg-gradient-to-r from-emerald-50/80 to-teal-50/60 dark:from-emerald-950/20 dark:to-teal-950/10 flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-emerald-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-bold text-foreground">Preço & Margem</h3>
                            <p className="text-[11px] text-muted-foreground">Quanto cobra e quanto sobra</p>
                          </div>
                        </header>
                        {/* Empilhado (24/10/2026): preço em cima, custo/lucro/margem embaixo.
                            Fontes e folgas menores (26/10/2026) para caber na altura dos Canais de Venda */}
                        <div className="p-4 sm:p-5 grid gap-4">
                          {/* Preço hero */}
                          <div className="bg-gradient-to-br from-emerald-50 to-teal-50/40 dark:from-emerald-950/20 dark:to-teal-950/10 rounded-2xl border border-emerald-200/40 dark:border-emerald-900/40 p-4 space-y-2.5">
                            {/* Pílulas UN / KG / L */}
                            <div>
                              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Unidade de venda</Label>
                              <div className="grid grid-cols-3 gap-1.5 mt-1.5 bg-white/60 dark:bg-zinc-900/40 rounded-lg p-1">
                                {[
                                  { value: "un", label: "Unidade", short: "un" },
                                  { value: "kg", label: "Quilo", short: "kg" },
                                  { value: "L", label: "Litro", short: "L" },
                                ].map(opt => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                      // Quilo/Litro liga o Divisível sozinho (dá pra desligar depois)
                                      if (ehUnidadePeso(opt.value) && opt.value !== saleUnit) setIsDivisible(true);
                                      setSaleUnit(opt.value);
                                    }}
                                    className={`h-7 rounded-md text-[11px] font-bold transition-all ${
                                      saleUnit === opt.value
                                        ? 'bg-orange-500 text-white shadow-sm'
                                        : 'text-muted-foreground hover:bg-muted/50'
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                              <ExplicacaoUnidadeVenda unidade={saleUnit} divisivel={isDivisible} className="mt-1.5" />
                            </div>

                            <div className="flex items-baseline justify-between">
                              <Label htmlFor="price-main" className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
                                {productType === "combo"
                                  ? "Preço Simples"
                                  : (saleUnit === "kg" ? "Preço por Kg" : saleUnit === "L" ? "Preço por Litro" : "Preço de Venda")}
                              </Label>
                              {currentPrice > 0 && totalCost > 0 && (
                                <span className={`text-[10px] font-black px-2 py-0.5 rounded leading-none ${
                                  cmvColor === 'emerald' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' :
                                  cmvColor === 'yellow' ? 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400' :
                                  'bg-red-500/15 text-red-700 dark:text-red-400'
                                }`}>CMV {String(cmvPct).replace('.', ',')}%</span>
                              )}
                            </div>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-muted-foreground/70 font-bold">R$</span>
                              {/* Caixa registradora (dono, 24/10/2026): começa em 0,00 e cada dígito
                                  entra pela direita — 1 → 0,01; 10 → 0,10; 100 → 1,00 */}
                              <InputDecimal
                                id="price-main"
                                data-testid={productType === "combo" ? "product-simple-price-input" : "product-sale-price-input"}
                                decimals={2}
                                value={(productType === "combo" ? simplePrice : salePrice) || "0"}
                                onChange={(v) => (productType === "combo" ? setSimplePrice(v) : setSalePrice(v))}
                                className="h-14 pl-12 text-3xl md:text-3xl text-left font-black tracking-tighter border-0 bg-white/80 dark:bg-zinc-900/40 focus-visible:ring-2 focus-visible:ring-emerald-500"
                                style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.04em" }}
                              />
                              {saleUnit !== "un" && currentPrice > 0 && (
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                  /{saleUnit}
                                </span>
                              )}
                            </div>
                            {saleUnit !== "un" && currentPrice > 0 && (
                              <p className="text-[11px] text-muted-foreground text-center">
                                Cliente verá <strong className="text-foreground">R$ {currentPrice.toFixed(2).replace('.', ',')}/{saleUnit}</strong>
                              </p>
                            )}

                            {/* Preço promocional "de/por" — bloco colapsável.
                                Diferente de promoção global (que insere badge PROMO):
                                aqui é só visual "de R$ X (riscado) por R$ Y".
                                Cliente paga sale_price normal. Só faz sentido em
                                product_type='produto' (combo tem simple/combo,
                                pizza tem preço por tamanho). */}
                            {productType === "produto" && (
                              <div className="mt-2 pt-2.5 border-t border-emerald-200/40 dark:border-emerald-900/40 space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <Label htmlFor="promo-toggle" className="text-[11px] font-bold text-foreground flex items-center gap-1.5 cursor-pointer">
                                      <Tag className="w-3.5 h-3.5 text-emerald-500" />
                                      Marcar como promocional
                                    </Label>
                                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                                      Mostra "de R$ X por R$ Y" no cardápio (sem badge de promoção)
                                    </p>
                                  </div>
                                  <Switch
                                    id="promo-toggle"
                                    checked={isPromotional}
                                    onCheckedChange={(v) => {
                                      setIsPromotional(v);
                                      if (!v) setOriginalPrice("");
                                    }}
                                  />
                                </div>
                                {isPromotional && (
                                  <div>
                                    <Label htmlFor="orig-price" className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
                                      Preço "de" (riscado)
                                    </Label>
                                    <div className="relative mt-1">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground/70 font-bold">R$</span>
                                      <InputDecimal
                                        id="orig-price"
                                        decimals={2}
                                        value={originalPrice || "0"}
                                        onChange={(v) => setOriginalPrice(v === "0" ? "" : v)}
                                        className="h-10 pl-10 text-lg md:text-lg text-left font-bold tracking-tight border-0 bg-white/80 dark:bg-zinc-900/40 focus-visible:ring-2 focus-visible:ring-emerald-500"
                                        style={{ fontVariantNumeric: "tabular-nums" }}
                                      />
                                    </div>
                                    {(() => {
                                      // Aviso UX: switch ligado + input vazio salva null silenciosamente
                                      if (!originalPrice || parseFloat(originalPrice) <= 0) {
                                        return (
                                          <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 leading-tight">
                                            💡 Digite o preço "de" pra ativar. Vazio = salva como produto normal.
                                          </p>
                                        );
                                      }
                                      const orig = parseFloat(originalPrice);
                                      if (currentPrice > 0 && orig <= currentPrice) {
                                        return (
                                          <p className="text-[10px] text-red-600 dark:text-red-400 mt-1 leading-tight">
                                            ⚠️ Precisa ser maior que o preço de venda (R$ {currentPrice.toFixed(2).replace(".", ",")}).
                                          </p>
                                        );
                                      }
                                      if (currentPrice > 0) {
                                        const desc = ((orig - currentPrice) / orig * 100).toFixed(0);
                                        return (
                                          <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1 leading-tight">
                                            Cliente vê: <span className="line-through">R$ {orig.toFixed(2).replace('.', ',')}</span> {" "}
                                            <strong>R$ {currentPrice.toFixed(2).replace('.', ',')}</strong> <span className="opacity-75">(−{desc}%)</span>
                                          </p>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Custo + Lucro + barra CMV VISUAL (direita) */}
                          <div className="space-y-3">
                            {/* Números grandes: Custo + Lucro */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="rounded-xl border border-red-200/40 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/10 p-2.5">
                                <div className="text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400 font-bold flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                                  Custo
                                </div>
                                <p className="text-base font-black text-red-700 dark:text-red-400 tabular-nums mt-0.5">R$ {totalCost.toFixed(2).replace(".", ",")}</p>
                              </div>
                              <div className={`rounded-xl border p-2.5 ${profit >= 0 ? 'border-emerald-200/40 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/10' : 'border-red-200/40 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/10'}`}>
                                <div className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full inline-block ${profit >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                  Lucro
                                </div>
                                <p className={`text-base font-black tabular-nums mt-0.5 ${profit >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>R$ {profit.toFixed(2).replace(".", ",")}</p>
                              </div>
                            </div>

                            {/* Barra segmentada da margem — 3 zonas visuais.
                                verde 0-30 / amber 30-40 / vermelho 40+.
                                Ponteiro cai na posição do CMV atual. */}
                            {cmvPct ? (() => {
                              const cmvClamped = Math.min(parseFloat(cmvPct), 100);
                              // Zonas visuais (0-30 saudável, 30-40 atenção, 40-100 alto)
                              const seg1 = 30, seg2 = 40; // limites
                              const seg1Pct = seg1, seg2Pct = seg2 - seg1, seg3Pct = 100 - seg2;
                              return (
                                <div className="rounded-xl border p-2.5 bg-card space-y-2">
                                  <div className="flex items-baseline justify-between">
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Margem (CMV)</span>
                                    <span className={`text-xl font-black tabular-nums ${cmvColor === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : cmvColor === 'yellow' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                                      {String(cmvPct).replace('.', ',')}%
                                    </span>
                                  </div>
                                  <div className="relative h-3">
                                    {/* Barra segmentada */}
                                    <div className="absolute inset-0 rounded-full overflow-hidden flex">
                                      <div className="h-full bg-emerald-500/60" style={{ width: `${seg1Pct}%` }} />
                                      <div className="h-full bg-amber-500/60" style={{ width: `${seg2Pct}%` }} />
                                      <div className="h-full bg-red-500/60" style={{ width: `${seg3Pct}%` }} />
                                    </div>
                                    {/* Ponteiro na posição atual */}
                                    <div
                                      className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-white dark:border-zinc-900 shadow-md z-10"
                                      style={{
                                        left: `${cmvClamped}%`,
                                        backgroundColor: cmvColor === 'emerald' ? '#10b981' : cmvColor === 'yellow' ? '#f59e0b' : '#ef4444',
                                      }}
                                    />
                                  </div>
                                  {/* Marcadores das zonas */}
                                  <div className="flex justify-between text-[9px] text-muted-foreground font-semibold px-0.5">
                                    <span>0%</span>
                                    <span className="opacity-70">30%</span>
                                    <span className="opacity-70">40%</span>
                                    <span>100%</span>
                                  </div>
                                </div>
                              );
                            })() : (
                              <div className="bg-muted/30 rounded-xl p-3 text-center">
                                <p className="text-[11px] text-muted-foreground">Adicione ficha técnica para ver a margem</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </section>
                    );
                  })()}
                  {/* Disponibilidade e Preços por Módulo */}
                  {productType !== "receita" && (() => {
                    const channels = [
                      // Totem e Mesa QR são canais próprios desde 24/10/2026 (antes seguiam
                      // Balcão e Mesas; a migração copiou um para o outro)
                      // Ícones iguais aos das abas do cabeçalho (dono, 26/10/2026); Mesa QR e
                      // Cardápio digital não têm aba e usam o QR Code e o celular
                      { key: "Balcao", label: "Balcão", Icon: CaixaRegistradoraIcon, desc: "Vendas no balcão (PDV)" },
                      { key: "Totem", label: "Totem", Icon: TotemIcon, desc: "Autoatendimento na loja" },
                      { key: "Delivery", label: "Delivery", Icon: MotoEntregaIcon, desc: "Pedidos para entrega" },
                      { key: "Mesas", label: "Mesas", Icon: MesaIcon, desc: "Atendimento do garçom" },
                      { key: "MesaQR", label: "Mesa QR", Icon: QrCode, desc: "Cliente pede pelo QR Code da mesa" },
                      { key: "Comandas", label: "Comandas", Icon: ClipboardList, desc: "Conta em nome do cliente, sem mesa" },
                      { key: "Cardapio", label: "Cardápio digital", Icon: Smartphone, desc: "Pedidos pelo link da loja" },
                    ];
                    const globalPriceNum = parseFloat(productType === "combo" ? salePrice : salePrice) || 0;
                    const globalSimpleNum = parseFloat(simplePrice) || 0;
                    const activeCount = channels.filter(c => visibleModules.includes(c.key)).length;
                    const overriddenCount = channels.filter(c => {
                      const p = modulePrices[c.key] || {};
                      return visibleModules.includes(c.key) && (p.sale_price || (productType === "combo" && p.simple_price));
                    }).length;
                    return (
                    <div className="rounded-2xl border bg-card shadow-sm overflow-hidden flex flex-col">
                      {/* Header compacto — cor do tema */}
                      <div className="px-5 py-3 border-b bg-gradient-to-r from-orange-50/80 to-amber-50/60 dark:from-orange-950/20 dark:to-amber-950/10 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center flex-shrink-0">
                          <Store className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-foreground">Canais de Venda</h3>
                          <p className="text-[11px] text-muted-foreground">
                            <span className="font-semibold text-foreground">{activeCount}</span>/{channels.length} ativos
                            {overriddenCount > 0 && <> · <span className="font-semibold text-orange-600 dark:text-orange-400">{overriddenCount} com preço próprio</span></>}
                          </p>
                        </div>
                      </div>

                      {/* Lista compacta — 1 linha por canal */}
                      <div className="p-3 space-y-1.5 flex-1">
                        {channels.map((mod) => {
                          const isActive = visibleModules.includes(mod.key);
                          const prices = modulePrices[mod.key] || {};
                          const hasOverride = !!(prices.sale_price || (productType === "combo" && prices.simple_price));
                          return (
                            <div
                              key={mod.key}
                              className={`flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 px-3 py-2 rounded-xl border transition-all ${
                                isActive
                                  ? hasOverride
                                    ? 'bg-card border-orange-300/70 dark:border-orange-700/70'
                                    : 'bg-card border-border'
                                  : 'bg-muted/30 border-transparent'
                              }`}
                            >
                              {/* Toggle (Radix Switch) — usa cor do tema (primary) */}
                              <Switch
                                checked={isActive}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setVisibleModules(prev => [...prev, mod.key]);
                                  } else {
                                    setVisibleModules(prev => prev.filter(m => m !== mod.key));
                                    setModulePrices(prev => { const n = {...prev}; delete n[mod.key]; return n; });
                                  }
                                }}
                                className="flex-shrink-0"
                              />

                              {/* Ícone + nome + desc */}
                              <mod.Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-foreground' : 'text-muted-foreground'}`} />
                              <div className="flex-1 min-w-0">
                                <span className={`text-sm font-semibold whitespace-nowrap ${isActive ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{mod.label}</span>
                                <p className="text-[10px] text-muted-foreground leading-tight">
                                  {isActive ? mod.desc : 'Não vende neste canal'}
                                </p>
                              </div>

                              {/* Preço (só se ativo) — compacto à direita; no celular
                                  desce para a linha de baixo, senão cobria o nome do canal */}
                              {isActive && (
                                <div className="flex items-center justify-end gap-3 w-full sm:w-auto">
                                  {productType === "combo" ? (
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      <div className="relative w-[110px]">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/70 font-semibold">R$</span>
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          placeholder={simplePrice ? parseFloat(simplePrice).toFixed(2).replace('.', ',') : "Simples"}
                                          value={prices.simple_price ?? ""}
                                          onChange={(e) => {
                                            const val = sanitizeCurrencyInput(e.target.value);
                                            setModulePrices(prev => ({
                                              ...prev,
                                              [mod.key]: { ...prev[mod.key], simple_price: val ? parseFloat(val) : null }
                                            }));
                                          }}
                                          className={`h-8 pl-7 text-xs font-semibold ${prices.simple_price ? 'border-orange-400 bg-orange-50/30 dark:bg-orange-950/10' : ''}`}
                                          title="Preço Simples"
                                        />
                                      </div>
                                      <div className="relative w-[110px]">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/70 font-semibold">R$</span>
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          placeholder={salePrice ? parseFloat(salePrice).toFixed(2).replace('.', ',') : "Combo"}
                                          value={prices.sale_price ?? ""}
                                          onChange={(e) => {
                                            const val = sanitizeCurrencyInput(e.target.value);
                                            setModulePrices(prev => ({
                                              ...prev,
                                              [mod.key]: { ...prev[mod.key], sale_price: val ? parseFloat(val) : null }
                                            }));
                                          }}
                                          className={`h-8 pl-7 text-xs font-semibold ${prices.sale_price ? 'border-orange-400 bg-orange-50/30 dark:bg-orange-950/10' : ''}`}
                                          title="Preço Combo"
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      <div className="relative w-[120px]">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/70 font-semibold">R$</span>
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          placeholder={salePrice ? parseFloat(salePrice).toFixed(2).replace('.', ',') : "0,00"}
                                          value={prices.sale_price ?? ""}
                                          onChange={(e) => {
                                            const val = sanitizeCurrencyInput(e.target.value);
                                            setModulePrices(prev => ({
                                              ...prev,
                                              [mod.key]: { ...prev[mod.key], sale_price: val ? parseFloat(val) : null }
                                            }));
                                          }}
                                          className={`h-8 pl-7 text-xs font-semibold ${prices.sale_price ? 'border-orange-400 bg-orange-50/30 dark:bg-orange-950/10' : ''}`}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {/* Status badge — compacto */}
                                  {hasOverride ? (
                                    <button
                                      type="button"
                                      onClick={() => setModulePrices(prev => { const n = {...prev}; delete n[mod.key]; return n; })}
                                      title="Voltar ao preço padrão"
                                      className="flex items-center gap-1 px-1.5 py-1 rounded-md bg-orange-500/15 hover:bg-orange-500/25 text-orange-700 dark:text-orange-400 text-[9px] font-black uppercase tracking-wider transition-colors flex-shrink-0"
                                    >
                                      <X className="w-2.5 h-2.5" /> Próprio
                                    </button>
                                  ) : (
                                    <span
                                      className="text-[9px] font-black uppercase tracking-wider px-1.5 py-1 rounded-md bg-muted text-muted-foreground leading-none flex-shrink-0"
                                      title="Usa o preço padrão (definido na sidebar)"
                                    >
                                      Padrão
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Rodapé com dica curta */}
                      <div className="px-4 py-2 border-t bg-muted/20 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                        <Info className="w-3 h-3 flex-shrink-0" />
                        <span>Em branco = usa o preço padrão. Digite para definir um preço próprio neste canal.</span>
                      </div>
                    </div>
                    );
                  })()}
                  </div>

                  {/* === SECTION 4: CONFIGURAÇÃO (Insumo + Divisível) — oculto para pizza === */}
                  {productType !== "pizza" && (
                  <section className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                    <header className="px-4 sm:px-6 py-3.5 border-b bg-gradient-to-r from-purple-50/80 to-fuchsia-50/60 dark:from-purple-950/20 dark:to-fuchsia-950/10 flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center">
                        <Settings2 className="w-4 h-4 text-purple-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-foreground">Configuração</h3>
                        <p className="text-[11px] text-muted-foreground">Como o produto se comporta no sistema</p>
                      </div>
                    </header>
                    <div className="p-4 sm:p-6 grid sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setIsInsumo(!isInsumo)}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all text-left ${
                          isInsumo
                            ? 'border-orange-500 bg-orange-500/[0.06] dark:bg-orange-500/10'
                            : 'border-border/70 hover:border-orange-300 hover:bg-muted/40'
                        }`}
                      >
                        <span className={`w-11 h-6 rounded-full flex-shrink-0 relative transition-colors ${
                          isInsumo ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}>
                          <span className={`absolute top-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${isInsumo ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${isInsumo ? 'text-orange-700 dark:text-orange-300' : 'text-foreground'}`}>Insumo</span>
                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded leading-none uppercase ${
                              isInsumo ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}>{isInsumo ? 'Ativo' : 'Inativo'}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">Não aparece no cardápio (ex: alface usado em combos)</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsDivisible(!isDivisible)}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all text-left ${
                          isDivisible
                            ? 'border-orange-500 bg-orange-500/[0.06] dark:bg-orange-500/10'
                            : 'border-border/70 hover:border-orange-300 hover:bg-muted/40'
                        }`}
                      >
                        <span className={`w-11 h-6 rounded-full flex-shrink-0 relative transition-colors ${
                          isDivisible ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}>
                          <span className={`absolute top-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${isDivisible ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${isDivisible ? 'text-orange-700 dark:text-orange-300' : 'text-foreground'}`}>Divisível</span>
                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded leading-none uppercase ${
                              isDivisible ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}>{isDivisible ? 'Ativo' : 'Inativo'}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                            {/* "250g" só faz sentido em quilo */}
                            {saleUnit === "kg" ? "Vendido por peso (ex: 250g)" : saleUnit === "L" ? "Vendido por volume (ex: 500 ml)" : "Vendido em frações (ex: ½ pizza)"}
                          </p>
                        </div>
                      </button>
                      {/* Balança (dono, 26/10/2026): só em Quilo. Ligar já liga o Divisível,
                          que é o que faz o quadro de peso aparecer na venda */}
                      {saleUnit === "kg" && (
                        <div className={`sm:col-span-2 px-4 py-3.5 rounded-xl border-2 transition-all ${usaBalanca ? 'border-orange-500 bg-orange-500/[0.06] dark:bg-orange-500/10' : 'border-border/70'}`} data-e2e="produto-balanca">
                          <div className="flex items-center gap-3">
                            <Switch id="produto-usa-balanca" checked={usaBalanca}
                              onCheckedChange={(v) => { setUsaBalanca(v); if (v) setIsDivisible(true); }} />
                            <Label htmlFor="produto-usa-balanca" className="flex-1 min-w-0 cursor-pointer">
                              <span className="flex items-center gap-2">
                                <Scale className="w-4 h-4 text-muted-foreground" />
                                <span className={`text-sm font-bold ${usaBalanca ? 'text-orange-700 dark:text-orange-300' : 'text-foreground'}`}>Pesar na balança</span>
                              </span>
                              <span className="block text-[11px] text-muted-foreground font-normal leading-tight mt-0.5">
                                O peso vem da balança ligada ao computador (Balcão, Comandas, Mesas e Delivery). Ex.: comida por quilo
                              </span>
                            </Label>
                          </div>
                          {usaBalanca && (
                            <div className="mt-3 flex flex-wrap items-center gap-3 pl-14">
                              <Label htmlFor="produto-tara" className="text-xs font-semibold">Peso do recipiente (g)</Label>
                              <InputDecimal id="produto-tara" data-e2e="produto-tara" decimals={0} value={taraGramas}
                                onChange={setTaraGramas} className="w-28 h-9 text-sm" sufixo="g" />
                              <span className="text-[11px] text-muted-foreground">Descontado do peso (prato, marmita). 0 se a balança já desconta.</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                  )}


                </div>
              )}

              {/* Tab: Ficha Técnica */}
              {activeMainTab === "ficha" && (
                <div className="space-y-6">

                {/* Pizza: uma ficha por TAMANHO. Base do tamanho e bordas sao da
                    loja (compartilhadas por todos os sabores); a parte do sabor
                    fica no produto. Meio a meio divide a ficha do sabor por N. */}
                {productType === "pizza" ? (
                  <PizzaFichaTab
                    sizes={pizzaSizesEffective}
                    pizzaRecipes={pizzaRecipes}
                    setPizzaRecipes={setPizzaRecipes}
                    ingredients={ingredients}
                    Combobox={IngredientCombobox}
                    getIngredientUnit={getIngredientUnit}
                    getFractionText={getFractionText}
                    onCreateNew={(nome, tipo) => handleQuickIngredientCreate(nome, `pizza:${tipo}`, -1)}
                    precoDe={(sz) => {
                      // Preco do sabor NESTE tamanho: atalho de precos em edicao, depois o
                      // prices_by_size do sabor, depois o preco base do tamanho. Campo vazio
                      // ("" ou 0) nao conta como preco.
                      for (const c of [pizzaPrices?.[sz.id], currentInPizzaFlavor?.prices_by_size?.[sz.id], sz.base_price, salePrice]) {
                        const n = Number(c);
                        if (c !== "" && c != null && n > 0) return n;
                      }
                      return 0;
                    }}
                  />
                ) : (<>

                {/* Resumo de Custos — 3 cards: Custo Total | CMV+Margem | Lucro; abaixo, o card Precificação */}
                {(() => {
                  const combinedItems = [
                    ...recipeIngredients.map(i => ({...i, item_type: "ingredient"})),
                    ...recipePackaging.map(i => ({...i, item_type: "packaging"}))
                  ];
                  const costs = calculateRecipeCosts(combinedItems);
                  const totalCost = costs.ingredientsCost + costs.packagingCost;
                  // Lucro/CMV/Margem sempre considera o preço SIMPLES (combo usa simple_price; outros usam sale_price)
                  const price = parseFloat(productType === "combo" ? simplePrice : salePrice) || 0;
                  const profit = price - totalCost;
                  const margin = price > 0 ? ((profit / price) * 100) : 0;
                  const cmvPercent = price > 0 ? ((totalCost / price) * 100) : 0;
                  const cmvColor = cmvPercent <= 30 ? 'emerald' : cmvPercent <= 50 ? 'amber' : 'red';
                  const cmvCls = cmvColor === 'emerald'
                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30'
                    : cmvColor === 'amber'
                      ? 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30'
                      : 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
                  return (<>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Custo Total */}
                      <div className="bg-card rounded-xl border p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-muted-foreground font-medium">Custo Total</p>
                          <p className="text-lg font-bold text-purple-600 dark:text-purple-400 tabular-nums">R$ {totalCost.toFixed(2).replace(".", ",")}</p>
                        </div>
                      </div>
                      {/* CMV % | Margem (combinado) */}
                      <div className="bg-card rounded-xl border p-4 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cmvCls.split(' ').slice(2).join(' ')}`}>
                          <TrendingUp className={`w-5 h-5 ${cmvCls.split(' ').slice(0, 2).join(' ')}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] text-muted-foreground font-medium">CMV % · Margem</p>
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <p className={`text-lg font-bold tabular-nums ${cmvCls.split(' ').slice(0, 2).join(' ')}`}>
                              {price > 0 ? `${cmvPercent.toFixed(1).replace('.', ',')}%` : '—'}
                            </p>
                            <span className="text-muted-foreground/60">·</span>
                            <p className={`text-lg font-bold tabular-nums ${
                              margin >= 50 ? 'text-emerald-600 dark:text-emerald-400' : margin >= 30 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              {price > 0 ? `${margin.toFixed(1).replace('.', ',')}%` : '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* Lucro (sempre baseado no preço SIMPLES) */}
                      <div className="bg-card rounded-xl border p-4 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          profit > 0 ? 'bg-blue-100 dark:bg-blue-900/30' : profit < 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-gray-900/30'
                        }`}>
                          <DollarSign className={`w-5 h-5 ${
                            profit > 0 ? 'text-blue-600 dark:text-blue-400' : profit < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'
                          }`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-muted-foreground font-medium">Lucro <span className="text-muted-foreground/60">(preço simples)</span></p>
                          <p className={`text-lg font-bold tabular-nums ${
                            profit > 0 ? 'text-blue-600 dark:text-blue-400' : profit < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'
                          }`}>{price > 0 ? `R$ ${profit.toFixed(2).replace(".", ",")}` : '—'}</p>
                        </div>
                      </div>
                    </div>
                    {/* Precificação: preço para 3 metas de CMV + preço/markup editáveis
                        (mesmo campo de preço da aba Produto; grava no Salvar). */}
                    {productType !== "receita" && (
                      <CardPrecificacao
                        custo={totalCost}
                        preco={productType === "combo" ? simplePrice : salePrice}
                        setPreco={productType === "combo" ? setSimplePrice : setSalePrice}
                        rotulo={productType === "combo" ? "Seu preço (simples)" : "Seu preço"}
                        sufixo={saleUnit && saleUnit !== "un" ? `/${saleUnit}` : ""}
                      />
                    )}
                  </>);
                })()}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Ingredientes */}
                  {(() => {
                    const emptyIngredients = recipeIngredients.map((item, i) => ({ item, index: i })).filter(x => !x.item.ingredient_id);
                    const filledIngredients = recipeIngredients.map((item, i) => ({ item, index: i })).filter(x => x.item.ingredient_id);
                    const ingSub = calculateRecipeCosts(recipeIngredients.map(i => ({...i, item_type: "ingredient"}))).ingredientsCost;
                    return (
                  <div className="bg-card rounded-2xl border shadow-sm flex flex-col">
                    <div className="px-4 py-3 border-b bg-gradient-to-r from-emerald-50/80 to-green-50/80 dark:from-emerald-950/30 dark:to-green-950/30 flex items-center justify-between rounded-t-2xl">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                          <span className="text-base">🥬</span>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">Ingredientes</h3>
                          <p className="text-[10px] text-muted-foreground">{filledIngredients.length} item(ns)</p>
                        </div>
                      </div>
                      {filledIngredients.length > 0 && (
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Subtotal</p>
                          <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">R$ {ingSub.toFixed(2).replace(".", ",")}</p>
                        </div>
                      )}
                    </div>
                    <div className="p-3 flex-1">
                      {/* Busca direta — digitar mostra opções, clicar adiciona com qtd 1 */}
                      <div className="mb-3">
                        <IngredientCombobox
                          ingredients={ingredients}
                          value=""
                          onChange={(id) => addRecipeItemDirect(id, "ingredient")}
                          placeholder="Buscar ingrediente para adicionar..."
                          onCreateNew={(name) => handleQuickIngredientCreate(name, "ingredient", -1)}
                          getIngredientUnit={getIngredientUnit}
                          excludeIds={[
                            ...recipeIngredients.map(r => r.ingredient_id),
                            ...recipePackaging.map(r => r.ingredient_id),
                          ]}
                        />
                      </div>

                      {/* Separador */}
                      {filledIngredients.length > 0 && (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{filledIngredients.length} adicionado{filledIngredients.length !== 1 ? 's' : ''}</span>
                            <div className="flex-1 h-px bg-border" />
                          </div>

                          {/* Itens preenchidos */}
                          <div className="space-y-2">
                            {filledIngredients.map(({ item, index }) => {
                              const selectedIng = getIngredientDetails(item.ingredient_id);
                              const displayUnit = getIngredientUnit(selectedIng);
                              const unitsPerPackage = selectedIng.units_per_package || 0;
                              const avgPrice = selectedIng.average_price || 0;
                              const unitPrice = avgPrice;
                              const _qty = parseFloat(item.quantity) || 0;
                              const _uw = selectedIng.unit_weight || 0;
                              const _u = selectedIng.unit || '';
                              const _loss = Number(selectedIng.loss_percent) || 0;
                              const _lossF = (_loss > 0 && _loss < 100) ? 1 / (1 - _loss / 100) : 1;
                              // Branch por unit + uw — receita em un (kg+uw) ou em kg (un+uw)
                              const _base = (_u === 'kg' && _uw > 0)
                                ? unitPrice * _uw * _qty
                                : (_u === 'un' && _uw > 0)
                                  ? (unitPrice / _uw) * _qty
                                  : unitPrice * _qty;
                              const itemCost = _base * _lossF;
                              // Quando consumido em kg (kg puro OU un+uw=gramatura), valor < 1
                              // mostra equivalencia em gramas pra leitura mais direta.
                              const consumedInKg = (_u === 'kg' && _uw === 0) || (_u === 'un' && _uw > 0);
                              const showGrams = consumedInKg && _qty > 0 && _qty < 1;
                              const qtyInGrams = showGrams ? Math.round(_qty * 1000) : null;

                              return (
                                <div key={index} className="flex flex-wrap sm:flex-nowrap items-center gap-2 p-2.5 rounded-xl border bg-muted/20 hover:bg-muted/40 transition-colors">
                                  <div className="flex-1 min-w-0 basis-full sm:basis-0">
                                    <p className="text-sm font-medium truncate">{selectedIng?.name || "—"}</p>
                                    <p className="text-[10px] text-muted-foreground tabular-nums">{getFractionText(selectedIng)}</p>
                                  </div>
                                  <div className="w-24 flex-shrink-0 ml-auto sm:ml-0 flex flex-col items-stretch gap-0.5">
                                    <Input
                                      data-testid={`recipe-quantity-${index}`}
                                      type="number"
                                      step="0.01"
                                      value={item.quantity}
                                      onChange={(e) => updateRecipeItem(index, "quantity", e.target.value, "ingredient")}
                                      placeholder="Qtd"
                                      className="h-8 text-sm text-center"
                                    />
                                    {qtyInGrams != null ? (
                                      <span className="text-center text-[10px] font-bold text-amber-600 dark:text-amber-400 tabular-nums leading-none">
                                        = {qtyInGrams} g
                                      </span>
                                    ) : (
                                      <span className="text-center text-[10px] text-transparent select-none leading-none">·</span>
                                    )}
                                  </div>
                                  <div className={`text-xs font-semibold px-2 py-1.5 rounded-lg whitespace-nowrap ${
                                    itemCost > 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground bg-muted/30'
                                  }`}>
                                    {itemCost > 0 ? `R$ ${itemCost.toFixed(2).replace(".", ",")}` : "R$ —"}
                                  </div>
                                  <Button type="button" onClick={() => removeRecipeItem(index, "ingredient")} variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg flex-shrink-0">
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                    );
                  })()}

                  {/* Embalagens */}
                  {(() => {
                    const emptyPackaging = recipePackaging.map((item, i) => ({ item, index: i })).filter(x => !x.item.ingredient_id);
                    const filledPackaging = recipePackaging.map((item, i) => ({ item, index: i })).filter(x => x.item.ingredient_id);
                    const pkgSub = calculateRecipeCosts(recipePackaging.map(i => ({...i, item_type: "packaging"}))).packagingCost;
                    return (
                  <div className="bg-card rounded-2xl border shadow-sm flex flex-col">
                    <div className="px-4 py-3 border-b bg-gradient-to-r from-amber-50/80 to-orange-50/80 dark:from-amber-950/30 dark:to-orange-950/30 flex items-center justify-between rounded-t-2xl">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                          <span className="text-base">📦</span>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">Embalagens</h3>
                          <p className="text-[10px] text-muted-foreground">{filledPackaging.length} item(ns)</p>
                        </div>
                      </div>
                      {filledPackaging.length > 0 && (
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Subtotal</p>
                          <p className="text-base font-bold text-amber-600 dark:text-amber-400 tabular-nums">R$ {pkgSub.toFixed(2).replace(".", ",")}</p>
                        </div>
                      )}
                    </div>
                    <div className="p-3 flex-1">
                      {/* Busca direta — digitar mostra opções, clicar adiciona com qtd 1 */}
                      <div className="mb-3">
                        <IngredientCombobox
                          ingredients={ingredients}
                          value=""
                          onChange={(id) => addRecipeItemDirect(id, "packaging")}
                          placeholder="Buscar embalagem para adicionar..."
                          onCreateNew={(name) => handleQuickIngredientCreate(name, "packaging", -1)}
                          getIngredientUnit={getIngredientUnit}
                          excludeIds={[
                            ...recipeIngredients.map(r => r.ingredient_id),
                            ...recipePackaging.map(r => r.ingredient_id),
                          ]}
                        />
                      </div>

                      {/* Separador */}
                      {filledPackaging.length > 0 && (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{filledPackaging.length} adicionado{filledPackaging.length !== 1 ? 's' : ''}</span>
                            <div className="flex-1 h-px bg-border" />
                          </div>

                          {/* Itens preenchidos */}
                          <div className="space-y-2">
                            {filledPackaging.map(({ item, index }) => {
                              const selectedIng = getIngredientDetails(item.ingredient_id);
                              const displayUnit = getIngredientUnit(selectedIng);
                              const unitsPerPackage = selectedIng.units_per_package || 0;
                              const avgPrice = selectedIng.average_price || 0;
                              const unitPrice = avgPrice;
                              const _qty = parseFloat(item.quantity) || 0;
                              const _uw = selectedIng.unit_weight || 0;
                              const _u = selectedIng.unit || '';
                              const _loss = Number(selectedIng.loss_percent) || 0;
                              const _lossF = (_loss > 0 && _loss < 100) ? 1 / (1 - _loss / 100) : 1;
                              // Branch por unit + uw — receita em un (kg+uw) ou em kg (un+uw)
                              const _base = (_u === 'kg' && _uw > 0)
                                ? unitPrice * _uw * _qty
                                : (_u === 'un' && _uw > 0)
                                  ? (unitPrice / _uw) * _qty
                                  : unitPrice * _qty;
                              const itemCost = _base * _lossF;
                              const consumedInKg = (_u === 'kg' && _uw === 0) || (_u === 'un' && _uw > 0);
                              const showGrams = consumedInKg && _qty > 0 && _qty < 1;
                              const qtyInGrams = showGrams ? Math.round(_qty * 1000) : null;

                              return (
                                <div key={index} className="flex flex-wrap sm:flex-nowrap items-center gap-2 p-2.5 rounded-xl border bg-muted/20 hover:bg-muted/40 transition-colors">
                                  <div className="flex-1 min-w-0 basis-full sm:basis-0">
                                    <p className="text-sm font-medium truncate">{selectedIng?.name || "—"}</p>
                                    <p className="text-[10px] text-muted-foreground tabular-nums">{getFractionText(selectedIng)}</p>
                                  </div>
                                  <div className="w-24 flex-shrink-0 ml-auto sm:ml-0 flex flex-col items-stretch gap-0.5">
                                    <Input
                                      data-testid={`recipe-packaging-quantity-${index}`}
                                      type="number"
                                      step="0.01"
                                      value={item.quantity}
                                      onChange={(e) => updateRecipeItem(index, "quantity", e.target.value, "packaging")}
                                      placeholder="Qtd"
                                      className="h-8 text-sm text-center"
                                    />
                                    {qtyInGrams != null ? (
                                      <span className="text-center text-[10px] font-bold text-amber-600 dark:text-amber-400 tabular-nums leading-none">
                                        = {qtyInGrams} g
                                      </span>
                                    ) : (
                                      <span className="text-center text-[10px] text-transparent select-none leading-none">·</span>
                                    )}
                                  </div>
                                  <div className={`text-xs font-semibold px-2 py-1.5 rounded-lg whitespace-nowrap ${
                                    itemCost > 0 ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' : 'text-muted-foreground bg-muted/30'
                                  }`}>
                                    {itemCost > 0 ? `R$ ${itemCost.toFixed(2).replace(".", ",")}` : "R$ —"}
                                  </div>
                                  <Button type="button" onClick={() => removeRecipeItem(index, "packaging")} variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg flex-shrink-0">
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                    );
                  })()}
                </div>

                {/* Campo de Rendimento para Receitas */}
                {productType === "receita" && (
                  <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b bg-gradient-to-r from-violet-50/80 to-purple-50/80 dark:from-violet-950/30 dark:to-purple-950/30">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                          <ChefHat className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">Configuração da Receita</h3>
                          <p className="text-[11px] text-muted-foreground">Defina o rendimento e vinculação ao estoque</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-5 space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="recipeYield" className="flex items-center gap-1.5 text-sm font-medium">
                            <Scale className="w-3.5 h-3.5 text-muted-foreground" />
                            Rendimento
                          </Label>
                          <Input
                            id="recipeYield"
                            type="number"
                            step="0.01"
                            value={recipeYield}
                            onChange={(e) => setRecipeYield(e.target.value)}
                            placeholder="Ex: 2"
                            className="h-11"
                          />
                          <p className="text-[11px] text-muted-foreground">
                            Quantidade que a receita rende
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="recipeYieldUnit" className="text-sm font-medium">Unidade do Rendimento</Label>
                          <Select value={recipeYieldUnit} onValueChange={setRecipeYieldUnit}>
                            <SelectTrigger className="h-11">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="kg">Quilograma (kg)</SelectItem>
                              <SelectItem value="un">Unidade (un)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="linkedIngredient" className="flex items-center gap-1.5 text-sm font-medium">
                          <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
                          Item de Estoque Vinculado
                        </Label>
                        <Select value={linkedIngredientId || "none"} onValueChange={(val) => setLinkedIngredientId(val === "none" ? "" : val)}>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Selecione o item no estoque" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhum</SelectItem>
                            {ingredients.map((ing) => (
                              <SelectItem key={ing.id} value={ing.id}>
                                {ing.name} ({ing.unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground">
                          O custo unitário da receita será enviado para este item no estoque
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                </>)}
                </div>
              )}

              {/* Tab: Personalização */}
              {activeMainTab === "personalizacao" && (
                <div className="space-y-6">

                  {/* SEÇÃO 1: Cards de Seleção (apenas para COMBO) */}
                  {productType === "combo" && (
                    <section className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                      {/* Header padronizado */}
                      <header className="px-4 sm:px-6 py-3.5 border-b bg-gradient-to-r from-purple-50/80 to-fuchsia-50/60 dark:from-purple-950/20 dark:to-fuchsia-950/10 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center">
                          <Layers className="w-4 h-4 text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-foreground">Opções para o cliente escolher</h3>
                          <p className="text-[11px] text-muted-foreground">Como os cards <strong>Simples</strong> e <strong>Combo</strong> aparecerão no cardápio</p>
                        </div>
                        {availableCombos.length > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setCopyComboOpen(true)}
                            className="text-purple-600 border-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30"
                          >
                            <Copy className="w-3.5 h-3.5 mr-1" />
                            Copiar de outro
                          </Button>
                        )}
                      </header>
                      <div className="p-4 sm:p-6 space-y-4">

                      {/* Modal de Copiar Combo */}
                      <Dialog open={copyComboOpen} onOpenChange={setCopyComboOpen}>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <Copy className="w-5 h-5 text-purple-500" />
                              Copiar de outro Combo
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                              Selecione um combo para copiar todas as configurações (preços, descrições, fotos e etapas).
                            </p>

                            {/* Busca */}
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                value={copyComboSearch}
                                onChange={(e) => setCopyComboSearch(e.target.value)}
                                placeholder="Buscar combo..."
                                className="pl-9"
                              />
                            </div>

                            {/* Lista de Combos */}
                            <div className="max-h-64 overflow-y-auto space-y-2">
                              {availableCombos
                                .filter(combo => {
                                  if (!copyComboSearch) return true;
                                  return combo.name.toLowerCase().includes(copyComboSearch.toLowerCase());
                                })
                                .map(combo => (
                                  <button
                                    key={combo.id}
                                    type="button"
                                    onClick={() => copyFromCombo(combo)}
                                    className="w-full flex items-center gap-3 p-3 rounded-xl border hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-left"
                                  >
                                    {/* Foto */}
                                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-700 flex-shrink-0">
                                      {combo.photo_url ? (
                                        <img
                                          src={combo.photo_url.startsWith('http') ? combo.photo_url : `${API}${combo.photo_url}`}
                                          alt={combo.name}
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                          decoding="async"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-2xl">🍟</div>
                                      )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{combo.name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {combo.order_steps?.length || 0} etapa{combo.order_steps?.length !== 1 ? 's' : ''} •
                                        R$ {combo.sale_price?.toFixed(2).replace(".", ",") || '0.00'}
                                      </p>
                                      <div className="flex gap-1 mt-1">
                                        {combo.order_steps?.slice(0, 3).map((step, i) => (
                                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded">
                                            {step.name || `Etapa ${i+1}`}
                                          </span>
                                        ))}
                                        {combo.order_steps?.length > 3 && (
                                          <span className="text-[10px] text-muted-foreground">+{combo.order_steps.length - 3}</span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Ícone */}
                                    <Copy className="w-5 h-5 text-purple-400 flex-shrink-0" />
                                  </button>
                                ))
                              }

                              {availableCombos.filter(combo => {
                                if (!copyComboSearch) return true;
                                return combo.name.toLowerCase().includes(copyComboSearch.toLowerCase());
                              }).length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                  <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                  <p className="text-sm">Nenhum combo encontrado</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      {/* Seletor de Ordem dos Cards */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gray-50 dark:bg-zinc-800 rounded-xl border">
                        <div className="flex items-center gap-2">
                          <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Ordem dos Cards:</span>
                        </div>
                        <div className="flex items-center gap-1 bg-white dark:bg-zinc-700 rounded-lg p-1 border w-full sm:w-auto">
                          <button
                            type="button"
                            onClick={() => setCardOrder("combo_first")}
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                              cardOrder === "combo_first"
                                ? "bg-green-500 text-white"
                                : "hover:bg-gray-100 dark:hover:bg-zinc-600"
                            }`}
                          >
                            🍟 Combo Primeiro
                          </button>
                          <button
                            type="button"
                            onClick={() => setCardOrder("simple_first")}
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                              cardOrder === "simple_first"
                                ? "bg-blue-500 text-white"
                                : "hover:bg-gray-100 dark:hover:bg-zinc-600"
                            }`}
                          >
                            Simples Primeiro
                          </button>
                        </div>
                      </div>

                      {/* Preview dos Cards lado a lado - com ordem dinâmica */}
                      <div className={`grid grid-cols-2 gap-2 sm:gap-4 ${cardOrder === "simple_first" ? "flex-row-reverse" : ""}`}>
                        {/* Card que aparece primeiro baseado na ordem */}
                        {(() => {
                            const renderComboCard = (isFirst) => (
                              <div key="combo" className={`flex flex-col rounded-xl border-2 ${isFirst ? 'border-green-300 dark:border-green-700' : 'border-border/60 dark:border-zinc-700'} overflow-hidden bg-card relative ${isFirst ? 'order-1' : 'order-2'}`}>
                                <div className={isFirst ? 'bg-green-500 text-white text-[10px] font-bold py-1 text-center uppercase tracking-wider' : 'h-6'}>
                                  {isFirst && '⭐ Recomendado'}
                                </div>
                                <div className={`px-3 py-2 flex items-center gap-2 ${isFirst ? 'bg-green-100/60 dark:bg-green-900/20' : 'bg-muted/40'}`}>
                                  <Input
                                    type="text"
                                    value={comboLabel}
                                    onChange={(e) => setComboLabel(e.target.value)}
                                    placeholder="🍟 Combo"
                                    className={`h-7 text-xs font-bold ${isFirst ? 'text-green-700 dark:text-green-300' : 'text-foreground'} bg-transparent border-0 px-1 focus-visible:ring-1 focus-visible:ring-green-400 uppercase tracking-wider`}
                                  />
                                </div>
                                <div className="p-3 flex flex-col gap-3 flex-1">
                                  <label className="cursor-pointer block">
                                    <div className={`aspect-square rounded-lg border-2 border-dashed border-border/50 hover:border-green-400 transition-colors overflow-hidden flex items-center justify-center ${isFirst ? 'bg-green-50/50 dark:bg-zinc-800/40' : 'bg-muted/40'}`}>
                                      {comboPhotoUrl ? (
                                        <img src={comboPhotoUrl.startsWith("data:") || comboPhotoUrl.startsWith("http") ? comboPhotoUrl : `/api${comboPhotoUrl}`} alt="Combo" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                      ) : photoUrl ? (
                                        <img src={photoUrl.startsWith("data:") || photoUrl.startsWith("http") ? photoUrl : `/api${photoUrl}`} alt="Combo" className="w-full h-full object-contain p-4 opacity-70" loading="lazy" decoding="async" />
                                      ) : (
                                        <div className="text-center">
                                          <Camera className="w-8 h-8 mx-auto text-muted-foreground/40 mb-1" />
                                          <span className="text-xs text-muted-foreground">Foto desta opção</span>
                                        </div>
                                      )}
                                    </div>
                                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const file = e.target.files?.[0]; if (file) { const reader = new FileReader(); reader.onload = (e) => setComboPhotoUrl(e.target?.result); reader.readAsDataURL(file); }}} />
                                  </label>
                                  <div className="h-5 flex items-center justify-center">
                                    {comboPhotoUrl && <button type="button" onClick={() => setComboPhotoUrl("")} className="text-xs text-red-500 hover:underline">Remover foto</button>}
                                  </div>
                                  <div>
                                    <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Preço</Label>
                                    <div className="relative mt-1">
                                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground/70 font-semibold">R$</span>
                                      <Input type="text" inputMode="decimal" value={salePrice} onChange={(e) => setSalePrice(sanitizeCurrencyInput(e.target.value))} placeholder="35,90" className={`h-10 pl-8 text-lg font-bold text-center ${isFirst ? 'text-green-600' : ''}`} />
                                    </div>
                                  </div>
                                  <div className="mt-auto">
                                    <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Descrição <span className="text-muted-foreground/60 normal-case font-normal">(opcional)</span></Label>
                                    <Input type="text" value={comboDescription} onChange={(e) => setComboDescription(e.target.value)} placeholder="Ex: + Batata + Refrigerante" className="mt-1 h-9 text-sm" />
                                  </div>
                                </div>
                              </div>
                            );
                            const renderSimpleCard = (isFirst) => (
                              <div key="simple" className={`flex flex-col rounded-xl border-2 ${isFirst ? 'border-blue-300 dark:border-blue-700' : 'border-border/60 dark:border-zinc-700'} overflow-hidden bg-card relative ${isFirst ? 'order-1' : 'order-2'}`}>
                                <div className={isFirst ? 'bg-blue-500 text-white text-[10px] font-bold py-1 text-center uppercase tracking-wider' : 'h-6'}>
                                  {isFirst && '⭐ Recomendado'}
                                </div>
                                <div className={`px-3 py-2 flex items-center gap-2 ${isFirst ? 'bg-blue-100/60 dark:bg-blue-900/20' : 'bg-muted/40'}`}>
                                  <Input
                                    type="text"
                                    value={simpleLabel}
                                    onChange={(e) => setSimpleLabel(e.target.value)}
                                    placeholder="Simples"
                                    className={`h-7 text-xs font-bold ${isFirst ? 'text-blue-700 dark:text-blue-300' : 'text-foreground'} bg-transparent border-0 px-1 focus-visible:ring-1 focus-visible:ring-blue-400 uppercase tracking-wider`}
                                  />
                                </div>
                                <div className="p-3 flex flex-col gap-3 flex-1">
                                  <label className="cursor-pointer block">
                                    <div className={`aspect-square rounded-lg border-2 border-dashed border-border/50 hover:border-blue-400 transition-colors overflow-hidden flex items-center justify-center ${isFirst ? 'bg-blue-50/50 dark:bg-zinc-800/40' : 'bg-muted/40'}`}>
                                      {simplePhotoUrl ? (
                                        <img src={simplePhotoUrl.startsWith("data:") || simplePhotoUrl.startsWith("http") ? simplePhotoUrl : `/api${simplePhotoUrl}`} alt="Simples" className="w-full h-full object-cover" />
                                      ) : photoUrl ? (
                                        <img src={photoUrl.startsWith("data:") || photoUrl.startsWith("http") ? photoUrl : `/api${photoUrl}`} alt="Simples" className="w-full h-full object-contain p-4 opacity-70" />
                                      ) : (
                                        <div className="text-center">
                                          <Camera className="w-8 h-8 mx-auto text-muted-foreground/40 mb-1" />
                                          <span className="text-xs text-muted-foreground">Foto desta opção</span>
                                        </div>
                                      )}
                                    </div>
                                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const file = e.target.files?.[0]; if (file) { const reader = new FileReader(); reader.onload = (e) => setSimplePhotoUrl(e.target?.result); reader.readAsDataURL(file); }}} />
                                  </label>
                                  <div className="h-5 flex items-center justify-center">
                                    {simplePhotoUrl && <button type="button" onClick={() => setSimplePhotoUrl("")} className="text-xs text-red-500 hover:underline">Remover foto</button>}
                                  </div>
                                  <div>
                                    <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Preço</Label>
                                    <div className="relative mt-1">
                                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground/70 font-semibold">R$</span>
                                      <Input type="text" inputMode="decimal" value={simplePrice} onChange={(e) => setSimplePrice(sanitizeCurrencyInput(e.target.value))} placeholder="25,90" className={`h-10 pl-8 text-lg font-bold text-center ${isFirst ? 'text-blue-600' : ''}`} />
                                    </div>
                                  </div>
                                  <div className="mt-auto">
                                    <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Descrição <span className="text-muted-foreground/60 normal-case font-normal">(opcional)</span></Label>
                                    <Input type="text" value={simpleDescription} onChange={(e) => setSimpleDescription(e.target.value)} placeholder="Ex: Apenas o sanduíche" className="mt-1 h-9 text-sm" />
                                  </div>
                                </div>
                              </div>
                            );
                            return cardOrder === "combo_first"
                              ? <>{renderComboCard(true)}{renderSimpleCard(false)}</>
                              : <>{renderSimpleCard(true)}{renderComboCard(false)}</>;
                          })()}
                      </div>
                      </div>
                    </section>
                  )}

                  {/* Copiado de + Manter etapas iguais (apenas combo) */}
                  {productType === "combo" && syncOrderStepsFromId && (
                    <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20">
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        Copiado de: <strong>{products.find(p => p.id === syncOrderStepsFromId)?.name || "—"}</strong>
                      </span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={(e) => { if (!e.target.checked) setSyncOrderStepsFromId(""); }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-blue-700 dark:text-blue-300">Manter etapas sempre iguais ao produto origem</span>
                      </label>
                      <span className="text-xs text-blue-600/80 dark:text-blue-400/80">Desmarque para editar as etapas aqui.</span>
                    </div>
                  )}

                  {/* Dialog Copiar Etapas de outro Produto — fora do wrapper de combo, disponível para todos os tipos */}
                  <Dialog open={copyStepsOpen} onOpenChange={setCopyStepsOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Copy className="w-5 h-5 text-orange-500" />
                          Copiar Etapas de outro Produto
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Selecione um produto para copiar todas as etapas de personalização.
                        </p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={copyStepsSearch}
                            onChange={(e) => setCopyStepsSearch(e.target.value)}
                            placeholder="Buscar produto..."
                            className="pl-9"
                          />
                        </div>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {productsWithSteps
                            .filter(p => !copyStepsSearch || p.name.toLowerCase().includes(copyStepsSearch.toLowerCase()))
                            .map(p => (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => copyStepsFromProduct(p)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl border hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all text-left"
                              >
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-700 flex-shrink-0">
                                  {p.photo_url ? (
                                    <img src={p.photo_url.startsWith('http') || p.photo_url.startsWith('data:') ? p.photo_url : `${API}${p.photo_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center"><ImageOff className="w-4 h-4 text-muted-foreground/30" /></div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{p.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {p.order_steps?.length || 0} etapa{p.order_steps?.length !== 1 ? 's' : ''}
                                    {p.category && ` • ${p.category}`}
                                  </p>
                                  <div className="flex gap-1 mt-1 flex-wrap">
                                    {p.order_steps?.slice(0, 3).map((step, i) => (
                                      <span key={i} className="text-[10px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">
                                        {step.name || `Etapa ${i+1}`}
                                      </span>
                                    ))}
                                    {p.order_steps?.length > 3 && <span className="text-[10px] text-muted-foreground">+{p.order_steps.length - 3}</span>}
                                  </div>
                                </div>
                                <Copy className="w-5 h-5 text-orange-400 flex-shrink-0" />
                              </button>
                            ))
                          }
                          {productsWithSteps.filter(p => !copyStepsSearch || p.name.toLowerCase().includes(copyStepsSearch.toLowerCase())).length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                              <Layers className="w-10 h-10 mx-auto mb-2 opacity-30" />
                              <p className="text-sm">Nenhum produto com etapas encontrado</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* SEÇÃO 2: Etapas do Pedido */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <Layers className="w-5 h-5 text-orange-500" />
                          <h3 className="font-semibold text-orange-700 dark:text-orange-300">Etapas do Pedido</h3>
                        </div>
                        <div className="grid grid-cols-2 sm:flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setCopyStepsOpen(true)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                            disabled={etapasSynced}
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copiar Etapas
                          </Button>
                          <Button
                            type="button"
                            onClick={addOrderStep}
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            disabled={etapasSynced}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Nova Etapa
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-orange-600/70 dark:text-orange-400/70">
                        {etapasSynced
                          ? "Etapas sincronizadas com o produto origem. Desmarque 'Manter etapas sempre iguais' acima para editar."
                          : productType === "combo"
                            ? "Configure as etapas que o cliente seguirá ao montar o pedido. Use 'Só Combo' para etapas exclusivas do combo (ex: Bebida)."
                            : "Configure as etapas que o cliente seguirá ao montar o pedido. Ex: Adicionais, Acompanhamentos."
                        }
                      </p>
                    </div>

                    {orderSteps.length === 0 ? (
                      <div className="bg-muted/30 rounded-xl p-12 text-center border-2 border-dashed border-muted-foreground/20">
                        <Layers className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-muted-foreground font-medium">Nenhuma etapa configurada</p>
                        <p className="text-xs text-muted-foreground mt-1 mb-4">
                          Adicione etapas para personalizar o pedido do cliente
                        </p>
                        <Button
                          type="button"
                          onClick={addOrderStep}
                          variant="outline"
                          size="sm"
                          disabled={etapasSynced}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Criar Primeira Etapa
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {orderSteps.map((step, stepIndex) => (
                          <div
                            key={stepIndex}
                            className={`rounded-xl border-2 overflow-hidden transition-all ${
                              step.combo_only
                                ? 'border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-900/10'
                                : 'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50'
                            }`}
                          >
                            {/* Header da Etapa */}
                            <div className={`px-3 sm:px-4 py-3 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 ${
                              step.combo_only
                                ? 'bg-purple-100 dark:bg-purple-900/30'
                                : 'bg-gray-50 dark:bg-zinc-800'
                            }`}>
                              <div className="flex items-center gap-3 flex-1 min-w-0 basis-full sm:basis-auto">
                                {/* Botões de Mover Etapa + Número */}
                                <div className="flex flex-col items-center">
                                  <button
                                    type="button"
                                    onClick={() => moveOrderStep(stepIndex, 'up')}
                                    disabled={stepIndex === 0 || etapasSynced}
                                    className={`p-1.5 sm:p-0.5 rounded hover:bg-white/50 dark:hover:bg-zinc-700 transition-colors ${
                                      stepIndex === 0 || etapasSynced ? 'opacity-30 cursor-not-allowed' : ''
                                    }`}
                                    title="Mover etapa para cima"
                                  >
                                    <ArrowUp className="w-3.5 h-3.5" />
                                  </button>
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                    step.combo_only ? 'bg-purple-500' : 'bg-orange-500'
                                  }`}>
                                    {stepIndex + 1}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => moveOrderStep(stepIndex, 'down')}
                                    disabled={stepIndex === orderSteps.length - 1 || etapasSynced}
                                    className={`p-1.5 sm:p-0.5 rounded hover:bg-white/50 dark:hover:bg-zinc-700 transition-colors ${
                                      stepIndex === orderSteps.length - 1 || etapasSynced ? 'opacity-30 cursor-not-allowed' : ''
                                    }`}
                                    title="Mover etapa para baixo"
                                  >
                                    <ArrowDown className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <Input
                                    value={step.name}
                                    onChange={(e) => updateOrderStep(stepIndex, "name", e.target.value)}
                                    placeholder="Nome da Etapa"
                                    className="h-8 font-semibold border-0 bg-transparent px-0 focus-visible:ring-0 text-base"
                                    disabled={etapasSynced}
                                  />
                                  {step.combo_only && (
                                    <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium flex items-center gap-1">
                                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                      Somente COMBO
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center justify-end gap-2 ml-auto">
                                {/* Switch Sincronizar Preços — não faz sentido em etapas pizza_*
                                    (sabores/bordas têm preço próprio por tamanho na matriz) */}
                                {!String(step.kind || "").startsWith("pizza_") && (
                                  <button
                                    type="button"
                                    onClick={() => updateOrderStep(stepIndex, "sync_prices", !step.sync_prices)}
                                    disabled={etapasSynced}
                                    className={`text-xs px-2 py-1 rounded-full transition-colors flex items-center gap-1 ${
                                      step.sync_prices !== false
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-200 dark:bg-zinc-600 text-gray-600 dark:text-gray-300 hover:bg-green-100 hover:text-green-600'
                                    } ${etapasSynced ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title={step.sync_prices !== false ? "Preços sincronizados com Produtos" : "Preços manuais"}
                                  >
                                    {step.sync_prices !== false ? '🔗 Auto' : '✏️ Manual'}
                                  </button>
                                )}
                                {productType === "combo" && (
                                  <button
                                    type="button"
                                    onClick={() => updateOrderStep(stepIndex, "combo_only", !step.combo_only)}
                                    disabled={etapasSynced}
                                    className={`text-xs px-2 py-1 rounded-full transition-colors ${
                                      step.combo_only
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-gray-200 dark:bg-zinc-600 text-gray-600 dark:text-gray-300 hover:bg-purple-100 hover:text-purple-600'
                                    } ${etapasSynced ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  >
                                    {step.combo_only ? '🍟 Só Combo' : '📦 Todos'}
                                  </button>
                                )}
                                {/* Etapas pizza_* (Tamanho, Sabores, Bordas) são essenciais pro
                                    cadastro — v1.5.325+ bloqueia delete pra evitar corromper a pizza. */}
                                {!String(step.kind || "").startsWith("pizza_") && (
                                  <Button
                                    type="button"
                                    onClick={() => setStepToDelete(stepIndex)}
                                    variant="ghost"
                                    size="icon"
                                    disabled={etapasSynced}
                                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Configurações da Etapa */}
                            <div className="p-3 sm:p-4 space-y-4">
                              {/* Etapas pizza_* usam editor especializado — não mostra UI genérica de combo */}
                              {String(step.kind || "").startsWith("pizza_") ? (
                                <PizzaStepEditor
                                  step={step}
                                  sizeStep={orderSteps.find(s => s.kind === "pizza_size")}
                                  flavorStep={orderSteps.find(s => s.kind === "pizza_flavor")}
                                  sizes={(orderSteps.find(s => s.kind === "pizza_size")?.items) || []}
                                  motherProductId={currentProductId}
                                  allPizzaProducts={
                                    // INCLUI o próprio produto (v1.5.383): o auto-recover do
                                    // FlavorEditor precisa achar a pizza-mãe em productById pra
                                    // re-injetar o "Sabor base" quando faltar — a exclusão antiga
                                    // (p.id !== currentProductId) matava o recover em silêncio e
                                    // pizzas criadas via "Copiar Etapas" ficavam SEM o próprio
                                    // sabor. O search de adicionar já filtra quem está na lista.
                                    products.filter(p => p.product_type === "pizza")
                                  }
                                  onChange={(updatedStep) => {
                                    const next = orderSteps.map((s, i) => (i === stepIndex ? updatedStep : s));
                                    setOrderSteps(next);
                                  }}
                                  onChangeSizeStep={(updatedSizeStep) => {
                                    const sizeIdx = orderSteps.findIndex(s => s.kind === "pizza_size");
                                    if (sizeIdx < 0) return;
                                    const next = orderSteps.map((s, i) => (i === sizeIdx ? updatedSizeStep : s));
                                    setOrderSteps(next);
                                  }}
                                  onChangeFlavorStep={(updatedFlavorStep) => {
                                    const fIdx = orderSteps.findIndex(s => s.kind === "pizza_flavor");
                                    if (fIdx < 0) return;
                                    const next = orderSteps.map((s, i) => (i === fIdx ? updatedFlavorStep : s));
                                    setOrderSteps(next);
                                  }}
                                />
                              ) : (<>
                              {/* Linha de configurações compacta */}
                              <div className="flex flex-wrap gap-3 items-center">
                                <div className={`flex items-center gap-2 bg-gray-100 dark:bg-zinc-700 rounded-lg px-3 py-2 ${etapasSynced ? 'opacity-60' : ''}`}>
                                  <span className="text-xs text-muted-foreground">Mín:</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={step.min_selections || 0}
                                    onChange={(e) => updateOrderStep(stepIndex, "min_selections", parseInt(e.target.value) || 0)}
                                    className="h-7 w-14 text-center border-0 bg-white dark:bg-zinc-600"
                                    disabled={etapasSynced}
                                  />
                                </div>
                                <div className={`flex items-center gap-2 bg-gray-100 dark:bg-zinc-700 rounded-lg px-3 py-2 ${etapasSynced ? 'opacity-60' : ''}`}>
                                  <span className="text-xs text-muted-foreground">Máx:</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={step.max_selections || 0}
                                    onChange={(e) => updateOrderStep(stepIndex, "max_selections", parseInt(e.target.value) || 0)}
                                    className="h-7 w-14 text-center border-0 bg-white dark:bg-zinc-600"
                                    disabled={etapasSynced}
                                  />
                                </div>
                                <Select
                                  value={step.calculation_type || "soma"}
                                  onValueChange={(value) => updateOrderStep(stepIndex, "calculation_type", value)}
                                >
                                  <SelectTrigger disabled={etapasSynced} className={`h-9 w-32 text-xs ${etapasSynced ? 'opacity-60 cursor-not-allowed' : ''}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="soma">➕ Soma</SelectItem>
                                    <SelectItem value="subtracao">➖ Subtração</SelectItem>
                                    <SelectItem value="maximo">⬆️ Máximo</SelectItem>
                                  </SelectContent>
                                </Select>
                                <span className="text-[10px] text-muted-foreground">
                                  {step.min_selections === 0 && step.max_selections === 0
                                    ? "Sem limite de seleção"
                                    : step.min_selections === step.max_selections && step.min_selections > 0
                                      ? `Deve selecionar exatamente ${step.min_selections}`
                                      : `Selecionar de ${step.min_selections || 0} a ${step.max_selections || '∞'}`
                                  }
                                </span>
                              </div>

                              {/* Busca de Produtos */}
                              <div className="relative">
                                <div className="flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-3 border border-green-200 dark:border-green-800">
                                  <Search className="w-4 h-4 text-green-600" />
                                  <Input
                                    value={stepProductSearch[stepIndex] || ""}
                                    onChange={(e) => {
                                      setStepProductSearch({...stepProductSearch, [stepIndex]: e.target.value});
                                      setStepSearchOpen({...stepSearchOpen, [stepIndex]: true});
                                    }}
                                    onFocus={() => setStepSearchOpen({...stepSearchOpen, [stepIndex]: true})}
                                    placeholder="🔍 Buscar produto para adicionar..."
                                    className="h-8 border-0 bg-transparent focus-visible:ring-0 text-sm"
                                    disabled={etapasSynced}
                                  />
                                  {stepProductSearch[stepIndex] && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setStepProductSearch({...stepProductSearch, [stepIndex]: ""});
                                        setStepSearchOpen({...stepSearchOpen, [stepIndex]: false});
                                      }}
                                      disabled={etapasSynced}
                                      className={`text-gray-400 hover:text-gray-600 ${etapasSynced ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>

                                {/* Dropdown de Resultados da Busca */}
                                {stepSearchOpen[stepIndex] && (
                                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-zinc-800 rounded-xl border shadow-xl max-h-64 overflow-y-auto">
                                    {products
                                      .filter(p => {
                                        if (p.id === currentProductId) return false;
                                        if ((step.items || []).some(item => item.product_id === p.id)) return false;
                                        const searchTerm = (stepProductSearch[stepIndex] || "").toLowerCase();
                                        if (!searchTerm) return true;
                                        return p.name.toLowerCase().includes(searchTerm) ||
                                               (p.code && p.code.includes(searchTerm));
                                      })
                                      .slice(0, 10)
                                      .map((prod) => (
                                        <button
                                          key={prod.id}
                                          type="button"
                                          disabled={etapasSynced}
                                          onClick={() => {
                                            addItemToStep(stepIndex, prod.id);
                                            setStepProductSearch({...stepProductSearch, [stepIndex]: ""});
                                            setStepSearchOpen({...stepSearchOpen, [stepIndex]: false});
                                          }}
                                          className={`w-full flex items-center gap-3 p-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors text-left ${etapasSynced ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                          {/* Foto do Produto */}
                                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-700 flex-shrink-0">
                                            {prod.photo_url ? (
                                              <img
                                                src={prod.photo_url.startsWith('http') ? prod.photo_url : `${API}${prod.photo_url}`}
                                                alt={prod.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                              />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-xl">
                                                🍽️
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{prod.name}</p>
                                            <p className="text-xs text-muted-foreground">
                                              {prod.code && `#${prod.code} • `}
                                              {getDisplayPrice(prod) ? `R$ ${getDisplayPrice(prod).toFixed(2).replace(".", ",")}` : 'Sem preço'}
                                            </p>
                                          </div>
                                          <Plus className="w-5 h-5 text-green-500 flex-shrink-0" />
                                        </button>
                                      ))
                                    }
                                    {products.filter(p => {
                                      if (p.id === currentProductId) return false;
                                      if ((step.items || []).some(item => item.product_id === p.id)) return false;
                                      const searchTerm = (stepProductSearch[stepIndex] || "").toLowerCase();
                                      if (!searchTerm) return true;
                                      return p.name.toLowerCase().includes(searchTerm);
                                    }).length === 0 && (
                                      <div className="p-4 text-center text-muted-foreground text-sm">
                                        Nenhum produto encontrado
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Lista de Produtos Adicionados */}
                              {(step.items || []).length > 0 ? (
                                <div className="space-y-2">
                                  {/* Header com contador e botões de ordenação */}
                                  <div className="flex items-center justify-between flex-wrap gap-2">
                                    <span className="text-xs font-medium text-muted-foreground">
                                      {step.items.length} {step.items.length === 1 ? 'produto' : 'produtos'} na etapa
                                    </span>
                                    {/* Botões de Ordenação */}
                                    <div className={`flex items-center gap-1 ${etapasSynced ? 'opacity-60' : ''}`}>
                                      <span className="text-[10px] text-muted-foreground mr-1">Ordenar:</span>
                                      <button
                                        type="button"
                                        onClick={() => sortStepItems(stepIndex, 'asc')}
                                        disabled={etapasSynced}
                                        className="px-2 py-1 text-[10px] rounded bg-gray-100 dark:bg-zinc-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Ordenar A→Z"
                                      >
                                        A→Z
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => sortStepItems(stepIndex, 'desc')}
                                        disabled={etapasSynced}
                                        className="px-2 py-1 text-[10px] rounded bg-gray-100 dark:bg-zinc-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Ordenar Z→A"
                                      >
                                        Z→A
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => sortStepItemsByPrice(stepIndex, 'asc')}
                                        disabled={etapasSynced}
                                        className="px-2 py-1 text-[10px] rounded bg-gray-100 dark:bg-zinc-700 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Ordenar por preço (menor primeiro)"
                                      >
                                        R$↑
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => sortStepItemsByPrice(stepIndex, 'desc')}
                                        disabled={etapasSynced}
                                        className="px-2 py-1 text-[10px] rounded bg-gray-100 dark:bg-zinc-700 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Ordenar por preço (maior primeiro)"
                                      >
                                        R$↓
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid gap-2">
                                    {(step.items || []).map((item, itemIndex) => {
                                      const itemProduct = products.find(p => p.id === item.product_id);
                                      const isDefaultItem = step.default_item_id === item.product_id;
                                      return (
                                        <div
                                          key={itemIndex}
                                          className={`flex flex-wrap md:flex-nowrap items-center gap-2 p-2 rounded-xl border transition-colors group ${
                                            isDefaultItem
                                              ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                                              : 'bg-white dark:bg-zinc-800 hover:border-orange-300'
                                          }`}
                                        >
                                          {/* Botões de Mover (para cima/baixo) */}
                                          <div className={`flex flex-col gap-0.5 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity ${etapasSynced ? '!opacity-50' : ''}`}>
                                            <button
                                              type="button"
                                              onClick={() => moveStepItem(stepIndex, itemIndex, 'up')}
                                              disabled={itemIndex === 0 || etapasSynced}
                                              className={`p-1.5 sm:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${itemIndex === 0 || etapasSynced ? 'opacity-30 cursor-not-allowed' : ''}`}
                                              title="Mover para cima"
                                            >
                                              <ArrowUp className="w-3 h-3" />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => moveStepItem(stepIndex, itemIndex, 'down')}
                                              disabled={itemIndex === step.items.length - 1 || etapasSynced}
                                              className={`p-1.5 sm:p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 ${itemIndex === step.items.length - 1 || etapasSynced ? 'opacity-30 cursor-not-allowed' : ''}`}
                                              title="Mover para baixo"
                                            >
                                              <ArrowDown className="w-3 h-3" />
                                            </button>
                                          </div>

                                          {/* Checkbox de Item Padrão */}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newDefaultId = isDefaultItem ? null : item.product_id;
                                              updateOrderStep(stepIndex, "default_item_id", newDefaultId);
                                            }}
                                            disabled={etapasSynced}
                                            className={`w-6 h-6 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                              isDefaultItem
                                                ? 'border-green-500 bg-green-500 text-white'
                                                : 'border-gray-300 dark:border-zinc-500 hover:border-green-400'
                                            } ${etapasSynced ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            title={isDefaultItem ? "Remover como item padrão" : "Definir como item pré-selecionado"}
                                          >
                                            {isDefaultItem && <Check className="w-3 h-3" />}
                                          </button>

                                          {/* Número da Posição */}
                                          <span className="text-xs text-muted-foreground font-mono w-5 text-center">{itemIndex + 1}</span>

                                          {/* Foto do Produto */}
                                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-700 flex-shrink-0">
                                            {itemProduct?.photo_url ? (
                                              <img
                                                src={itemProduct.photo_url.startsWith('http') ? itemProduct.photo_url : `${API}${itemProduct.photo_url}`}
                                                alt={item.product_name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                              />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-xl">
                                                🍽️
                                              </div>
                                            )}
                                          </div>

                                          {/* Info do Produto */}
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1">
                                              <p className="font-medium text-sm truncate">{item.product_name}</p>
                                              {isDefaultItem && (
                                                <span className="text-[9px] px-1.5 py-0.5 bg-green-500 text-white rounded-full font-medium">
                                                  PADRÃO
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                              {itemProduct?.code && `#${itemProduct.code}`}
                                            </p>
                                          </div>

                                          {/* Controles do item — abaixo de md descem para a linha de
                                              baixo: na mesma linha do nome, saíam pela direita da tela
                                              e escondiam o preço e a lixeira. */}
                                          <div className="flex flex-wrap md:flex-nowrap items-center justify-end gap-2 w-full md:w-auto">
                                          {/* Visibilidade por Módulo */}
                                          <div className="flex items-center gap-1 md:gap-0.5">
                                            {[
                                              { key: "Balcao", label: "B", color: "orange" },
                                              { key: "Delivery", label: "D", color: "blue" },
                                              { key: "Mesas", label: "M", color: "purple" },
                                              { key: "Cardapio", label: "C", color: "pink" },
                                            ].map((mod) => {
                                              const itemModules = item.visible_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"];
                                              const isVisible = itemModules.includes(mod.key);
                                              return (
                                                <button
                                                  key={mod.key}
                                                  type="button"
                                                  onClick={() => {
                                                    const current = item.visible_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"];
                                                    const updated = isVisible
                                                      ? current.filter(m => m !== mod.key)
                                                      : [...current, mod.key];
                                                    updateStepItem(stepIndex, itemIndex, "visible_modules", updated);
                                                  }}
                                                  disabled={etapasSynced}
                                                  className={`w-7 h-7 md:w-5 md:h-5 rounded text-[10px] md:text-[9px] font-bold flex items-center justify-center transition-all ${
                                                    isVisible
                                                      ? mod.color === "orange" ? "bg-orange-500 text-white"
                                                      : mod.color === "blue" ? "bg-blue-500 text-white"
                                                      : mod.color === "purple" ? "bg-purple-500 text-white"
                                                      : "bg-pink-500 text-white"
                                                      : "bg-gray-200 dark:bg-zinc-700 text-gray-400 dark:text-zinc-500"
                                                  } ${etapasSynced ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                                                  title={`${isVisible ? "Ocultar" : "Mostrar"} em ${mod.key}`}
                                                >
                                                  {mod.label}
                                                </button>
                                              );
                                            })}
                                          </div>

                                          {/* Preço Override */}
                                          <div className="flex items-center gap-1">
                                            <span className="text-xs text-muted-foreground">R$</span>
                                            {step.sync_prices !== false ? (
                                              <div className="h-8 w-20 flex items-center justify-center text-sm font-semibold text-green-600 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                                                {(getDisplayPrice(itemProduct) || 0).toFixed(2).replace('.', ',')}
                                              </div>
                                            ) : (
                                              // Caixa registradora (achado 333, 27/10/2026): começa no centavo e
                                              // enche pela direita (500 → 5,00). O campo antigo apagava a vírgula
                                              // enquanto digitava e "5,00" era gravado como R$ 500,00
                                              <InputDecimal
                                                data-e2e="etapa-preco-manual"
                                                decimals={2}
                                                value={item.price_override ? String(item.price_override) : ""}
                                                onChange={(v) => updateStepItem(stepIndex, itemIndex, "price_override", v ? parseFloat(v) : 0)}
                                                className="h-8 w-20 text-sm font-semibold"
                                                disabled={etapasSynced}
                                              />
                                            )}
                                          </div>

                                          {/* Toggle "permite multiplas unidades" — só aparece se a etapa permite (max != 1) */}
                                          {(step.max_selections === 0 || (step.max_selections || 0) > 1) && (
                                            <div className="flex items-center gap-1">
                                              <button
                                                type="button"
                                                onClick={() => updateStepItem(stepIndex, itemIndex, "allow_quantity", !item.allow_quantity)}
                                                disabled={etapasSynced}
                                                title={item.allow_quantity ? "Cliente pode escolher quantidade deste item" : "Cliente seleciona apenas 1"}
                                                className={`h-8 px-2 rounded text-[10px] font-bold flex items-center gap-1 transition-all ${
                                                  item.allow_quantity
                                                    ? "bg-amber-500 text-white"
                                                    : "bg-gray-200 dark:bg-zinc-700 text-gray-500 dark:text-zinc-400 hover:bg-gray-300"
                                                } ${etapasSynced ? 'opacity-50 cursor-not-allowed' : ''}`}
                                              >
                                                {item.allow_quantity ? "QTD ✓" : "QTD"}
                                              </button>
                                              {item.allow_quantity && (
                                                <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 rounded px-1.5 py-1">
                                                  <span className="text-[9px] text-amber-700 dark:text-amber-400 font-semibold">MÁX</span>
                                                  <Input
                                                    type="number"
                                                    min="0"
                                                    value={item.max_qty || 0}
                                                    onChange={(e) => updateStepItem(stepIndex, itemIndex, "max_qty", parseInt(e.target.value) || 0)}
                                                    disabled={etapasSynced}
                                                    title="0 = sem limite individual"
                                                    className="h-6 w-12 text-center text-xs border-0 bg-white dark:bg-zinc-700 p-0"
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          )}

                                          {/* Botão Remover */}
                                          <Button
                                            type="button"
                                            onClick={() => removeStepItem(stepIndex, itemIndex)}
                                            variant="ghost"
                                            size="icon"
                                            disabled={etapasSynced}
                                            className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity"
                                            aria-label="Remover da etapa"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-6 text-muted-foreground">
                                  <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                  <p className="text-sm">Nenhum produto adicionado</p>
                                  <p className="text-xs">Use a busca acima para adicionar produtos</p>
                                </div>
                              )}
                              </>)}{/* fecha ternário pizza_* vs UI genérica de combo */}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab: Impressão */}
              {activeMainTab === "impressao" && (
                <div className="space-y-5">
                  {/* Bloco: Modulos que imprimem CUPOM DE PREPARO */}
                  <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                    <div className="px-5 py-3.5 border-b bg-muted/30 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center shrink-0">
                        <Printer className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Label className="text-sm font-semibold block">Cupom de preparo (cozinha)</Label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Selecione os módulos onde o cupom de preparo será impresso. Desmarcar um módulo significa que pedidos desse módulo <span className="font-semibold">NÃO geram impressão de preparo</span> para este produto.
                        </p>
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-2.5">
                      {[
                        { key: "Balcao", label: "Balcão", Icon: CaixaRegistradoraIcon },
                        { key: "Delivery", label: "Delivery", Icon: MotoEntregaIcon },
                        { key: "Mesas", label: "Mesas", Icon: MesaIcon },
                        { key: "Comandas", label: "Comandas", Icon: ClipboardList },
                        { key: "Cardapio", label: "Cardápio", Icon: Smartphone },
                      ].map(mod => {
                        const modules = printConfig.preparo_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"];
                        const isActive = modules.includes(mod.key);
                        return (
                          <button
                            key={mod.key}
                            type="button"
                            onClick={() => {
                              setPrintConfig(prev => {
                                const current = prev.preparo_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"];
                                const updated = isActive
                                  ? current.filter(m => m !== mod.key)
                                  : [...current, mod.key];
                                return { ...prev, preparo_modules: updated };
                              });
                            }}
                            className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl text-sm font-medium transition-all border-2 ${
                              isActive
                                ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 shadow-sm'
                                : 'bg-muted/30 border-transparent text-muted-foreground hover:border-muted-foreground/20'
                            }`}
                          >
                            <mod.Icon className="w-5 h-5 shrink-0" />
                            <span className={!isActive ? 'line-through' : ''}>{mod.label}</span>
                            {isActive && <Check className="w-4 h-4 ml-auto text-orange-500" />}
                          </button>
                        );
                      })}
                    </div>
                    {(printConfig.preparo_modules || []).length === 0 && (
                      <div className="px-4 pb-4">
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-400">
                          ⚠️ Nenhum módulo selecionado — este produto <span className="font-bold">nunca</span> imprimirá cupom de preparo.
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Toggle Habilitar Ticket Individual */}
                  <div
                    onClick={() => setPrintConfig(prev => ({ ...prev, ticket_enabled: !prev.ticket_enabled }))}
                    className={`flex items-center justify-between p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                      printConfig.ticket_enabled
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-300 dark:border-green-700 shadow-sm'
                        : 'bg-card border-dashed border-muted-foreground/20 hover:border-muted-foreground/40'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        printConfig.ticket_enabled
                          ? 'bg-green-100 dark:bg-green-900/40'
                          : 'bg-muted'
                      }`}>
                        <Printer className={`w-6 h-6 ${printConfig.ticket_enabled ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <p className="font-semibold">{printConfig.ticket_enabled ? 'Ticket Individual Ativado' : 'Ticket Individual Desativado'}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">Imprime 1 ticket por unidade vendida (ex: 10 bebidas = 10 tickets)</p>
                      </div>
                    </div>
                    <div className={`w-12 h-7 rounded-full p-0.5 transition-colors flex-shrink-0 ${printConfig.ticket_enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-zinc-600'}`}>
                      <div className={`w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${printConfig.ticket_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </div>
                  </div>

                  {/* Configurações (só aparece quando habilitado) */}
                  {printConfig.ticket_enabled && (
                    <>
                      {/* Grid: Configurações à esquerda, Preview à direita */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {/* Coluna Esquerda: Configurações */}
                        <div className="space-y-5">
                          {/* Módulos */}
                          <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                            <div className="px-5 py-3.5 border-b bg-muted/30">
                              <Label className="text-sm font-semibold block">Módulos que imprimem ticket</Label>
                              <p className="text-[11px] text-muted-foreground mt-0.5">Selecione onde o ticket será impresso</p>
                            </div>
                            <div className="p-4 grid grid-cols-2 gap-2.5">
                              {[
                                { key: "Balcao", label: "Balcão", Icon: CaixaRegistradoraIcon },
                                { key: "Delivery", label: "Delivery", Icon: MotoEntregaIcon },
                                { key: "Mesas", label: "Mesas", Icon: MesaIcon },
                                { key: "Comandas", label: "Comandas", Icon: ClipboardList },
                                { key: "Cardapio", label: "Cardápio", Icon: Smartphone },
                              ].map(mod => {
                                const modules = printConfig.ticket_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"];
                                const isActive = modules.includes(mod.key);
                                return (
                                  <button
                                    key={mod.key}
                                    type="button"
                                    onClick={() => {
                                      setPrintConfig(prev => {
                                        const current = prev.ticket_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"];
                                        const updated = isActive
                                          ? current.filter(m => m !== mod.key)
                                          : [...current, mod.key];
                                        return { ...prev, ticket_modules: updated };
                                      });
                                    }}
                                    className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl text-sm font-medium transition-all border-2 ${
                                      isActive
                                        ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 shadow-sm'
                                        : 'bg-muted/30 border-transparent text-muted-foreground hover:border-muted-foreground/20'
                                    }`}
                                  >
                                    <mod.Icon className="w-5 h-5 shrink-0" />
                                    <span className={!isActive ? 'line-through' : ''}>{mod.label}</span>
                                    {isActive && <Check className="w-4 h-4 ml-auto text-blue-500" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Impressora */}
                          <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                            <div className="px-5 py-3.5 border-b bg-muted/30">
                              <Label className="text-sm font-semibold block">Impressora (Rota)</Label>
                              <p className="text-[11px] text-muted-foreground mt-0.5">AUTO = usa a impressora do módulo</p>
                            </div>
                            <div className="p-4 flex flex-wrap gap-2">
                              {[
                                { value: "", label: "AUTO", icon: "🔄" },
                                { value: "CAIXA", label: "CAIXA", icon: "💰" },
                                { value: "COZINHA", label: "COZINHA", icon: "👨‍🍳" },
                                { value: "BAR", label: "BAR", icon: "🍺" },
                                { value: "BALCAO", label: "BALCÃO", icon: "🏪" },
                              ].map(opt => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setPrintConfig(prev => ({ ...prev, ticket_route: opt.value }))}
                                  className={`flex-1 min-w-[80px] flex flex-col items-center gap-1 px-3 py-3 rounded-xl text-xs font-semibold transition-all border-2 ${
                                    printConfig.ticket_route === opt.value
                                      ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                                      : 'bg-muted/30 border-transparent hover:border-muted-foreground/20'
                                  }`}
                                >
                                  <span className="text-base">{opt.icon}</span>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Título */}
                          <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                            <div className="px-5 py-3.5 border-b bg-muted/30">
                              <Label className="text-sm font-semibold block">Título do Ticket</Label>
                              <p className="text-[11px] text-muted-foreground mt-0.5">Se vazio, usa o nome do produto</p>
                            </div>
                            <div className="p-4">
                              <Input
                                type="text"
                                value={printConfig.ticket_title}
                                onChange={(e) => setPrintConfig(prev => ({ ...prev, ticket_title: e.target.value }))}
                                placeholder={name || "Nome do produto"}
                                className="h-11"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Coluna Direita: Preview */}
                        <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
                          <div className="px-5 py-3.5 border-b bg-muted/30">
                            <Label className="text-sm font-semibold block">Preview do Ticket</Label>
                          </div>
                          <div className="p-6 flex flex-col items-center">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-[280px] w-full font-mono text-center text-black shadow-lg relative">
                              {/* Borda decorativa superior */}
                              <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                              <p className="text-xs font-bold tracking-[0.3em] text-gray-400 mt-1">═══ TICKET ═══</p>
                              <div className="my-4 py-3 border-y border-dashed border-gray-200">
                                <p className="text-lg font-bold leading-tight">{printConfig.ticket_title || name || "PRODUTO"}</p>
                                <p className="text-xs text-gray-400 mt-1">Pedido #0001</p>
                              </div>
                              <p className="text-4xl font-black text-gray-800 my-3">1/10</p>
                              <div className="border-t border-dashed border-gray-200 pt-3 mt-3 space-y-1">
                                <p className="text-[11px] text-gray-500">
                                  {new Date().toLocaleDateString('pt-BR')} {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                <p className="text-xs text-gray-600">Cliente: João Silva</p>
                              </div>
                              <p className="text-xs font-bold tracking-[0.3em] text-gray-400 mt-4">══════════════</p>
                            </div>
                            <div className="mt-4 text-center space-y-1">
                              <p className="text-xs text-muted-foreground flex items-center gap-1.5 justify-center">
                                <Store className="w-3 h-3" />
                                Módulos: {(printConfig.ticket_modules || ["Balcao","Delivery","Mesas","Cardapio"]).join(", ") || "Nenhum"}
                              </p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1.5 justify-center">
                                <Printer className="w-3 h-3" />
                                Impressora: {printConfig.ticket_route || "AUTO (módulo)"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Tab: Nota Fiscal — adicional cobrado à parte */}
              {activeMainTab === "fiscal" && (
                <FiscalLock>
                <ProdutoNotaFiscalTab
                  value={fiscalConfig}
                  onChange={setFiscalConfig}
                  // TODO: ler o regime da configuração fiscal da loja quando ela existir.
                  // Até lá assume Simples Nacional, que é o caso da maioria dos restaurantes.
                  regimeTributario="simples"
                />
                </FiscalLock>
              )}

            </form>
            </div>
          </div>

        {/* QuickIngredientDialog - needed for ingredient popup */}
        <QuickIngredientDialog
          open={quickIngredientOpen}
          onOpenChange={setQuickIngredientOpen}
          initialName={quickIngredientName}
          onSuccess={handleQuickIngredientSuccess}
          categories={ingredientCategories}
          onCategoriesChanged={(newCat) => setIngredientCategories(prev => [...prev, newCat])}
        />

        {/* Alert Dialog: confirmar exclusão de etapa em Personalização */}
        <AlertDialog open={stepToDelete !== null} onOpenChange={(isOpen) => { if (!isOpen) setStepToDelete(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Excluir etapa?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta etapa{stepToDelete !== null && orderSteps[stepToDelete]?.name ? ` "${orderSteps[stepToDelete].name}"` : ''} e todos os seus produtos serão removidos. <span className="font-semibold text-foreground">Não dá para reverter.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setStepToDelete(null)}>
                Não, manter
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (stepToDelete !== null) {
                    removeOrderStep(stepToDelete);
                    setStepToDelete(null);
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Sim, excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* PizzaManagerDialog removido — refator pizza usa Personalização (order_steps)
            em vez de catálogo legado */}

      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6" data-testid="products-page">
      <div className="max-w-7xl mx-auto">
        {/* Sem a barra Relatório · Produtos · Estoque · Compras no topo (dono,
            24/10/2026): Produtos é do Cardápio, se chega pelo menu lateral */}
        {/* Título da Página */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold tracking-tight">
            Produtos
          </h1>
        </div>

        {/* Sub-abas - Container com posição relativa */}
        <div className="relative">
          {/* Arquivados carrega ao abrir a aba: antes ela abria dizendo "Nenhum
              produto arquivado" até alguém tocar em "Carregar arquivados". */}
          <Tabs defaultValue="products" className="w-full" onValueChange={(aba) => { if (aba === "archived") fetchArchivedProducts(); }}>
            <TabsList className="grid w-full grid-cols-3 mb-6 relative">
              <TabsTrigger value="products">Produtos</TabsTrigger>
              <TabsTrigger value="categories">Categorias</TabsTrigger>
              <TabsTrigger value="archived" className="flex items-center gap-1.5">
                <Archive className="w-3.5 h-3.5" />
                Arquivados
              </TabsTrigger>
            </TabsList>

            <TabsContent value="products" className="space-y-4">
            
            {productsLoading && products.length === 0 && (
              <div className="bg-card rounded-2xl border shadow-sm mb-6 px-4 py-3 flex items-center gap-3" aria-busy="true" aria-label="Carregando">
                <Skeleton className="w-11 h-11 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-40" /><Skeleton className="h-3 w-56 max-w-full" /></div>
                <Skeleton className="h-4 w-40 hidden sm:block" />
              </div>
            )}
            {/* Indicadores de Performance do Cardápio - SLIM por padrão, expansível */}
            {products.length > 0 && (
              <div className="bg-card rounded-2xl border shadow-sm mb-6 overflow-hidden">
                {/* Cabeçalho SLIM (sempre visível) — apenas otimização do cardápio */}
                <button
                  type="button"
                  onClick={() => setCardapioStatsExpanded(prev => !prev)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                  aria-expanded={cardapioStatsExpanded}
                >
                  {/* Anel de progresso compacto */}
                  <div className="relative flex-shrink-0">
                    <CircularProgress percent={cardapioStats.overallPercent} size={44} strokeWidth={4} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[11px] font-bold">{cardapioStats.overallPercent}%</span>
                    </div>
                  </div>
                  {/* Texto principal */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold leading-tight">Cardápio otimizado</p>
                    <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">Fotos e descrições dos seus produtos</p>
                  </div>
                  {/* Mini indicadores fotos/descrição */}
                  <div className="hidden sm:flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Camera className="w-3.5 h-3.5" />
                      <span className="font-bold text-foreground">{cardapioStats.photoPercent}%</span>
                      <span className="hidden md:inline">fotos</span>
                    </span>
                    <span className="w-px h-4 bg-border" />
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="font-bold text-foreground">{cardapioStats.descriptionPercent}%</span>
                      <span className="hidden md:inline">descrição</span>
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${cardapioStatsExpanded ? 'rotate-180' : ''}`} />
                </button>

                {/* Conteúdo EXPANDIDO (todas as métricas) */}
                {cardapioStatsExpanded && (
                  <div className="border-t bg-muted/10 p-4 sm:p-6 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Indicador Principal - Cardápio Otimizado */}
                      <div className="flex flex-col items-center justify-center p-4 bg-card rounded-2xl border">
                        <div className="relative">
                          <CircularProgress percent={cardapioStats.overallPercent} size={120} strokeWidth={9} />
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-bold">{cardapioStats.overallPercent}%</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 text-center font-medium">do cardápio</p>
                        <p className="text-xs text-muted-foreground text-center">otimizado</p>
                      </div>

                      {/* Indicador de Fotos */}
                      <div className="flex flex-col p-4 bg-card rounded-2xl border">
                        <p className="text-xs text-muted-foreground mb-2">Você tem:</p>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-background rounded-lg border">
                            <Camera className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <span className="text-2xl font-bold">{cardapioStats.photoPercent}%</span>
                            <p className="text-xs text-muted-foreground">do cardápio com fotos</p>
                          </div>
                        </div>
                        <div className="mt-auto">
                          {cardapioStats.photoPercent < 100 ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full rounded-xl"
                              onClick={() => {
                                setPerformanceFilter("todos");
                                mudarFiltro("foto", "sem");
                              }}
                            >
                              Adicione fotos agora
                            </Button>
                          ) : (
                            <div className="text-center text-xs text-green-600 font-medium py-1.5">
                              ✓ Todos os produtos têm foto!
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Indicador de Descrições */}
                      <div className="flex flex-col p-4 bg-card rounded-2xl border">
                        <p className="text-xs text-muted-foreground mb-2">Você tem:</p>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-background rounded-lg border">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <span className="text-2xl font-bold">{cardapioStats.descriptionPercent}%</span>
                            <p className="text-xs text-muted-foreground">do cardápio com descrição</p>
                          </div>
                        </div>
                        <div className="mt-auto">
                          {cardapioStats.descriptionPercent < 100 ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full rounded-xl"
                              onClick={() => {
                                setPerformanceFilter("todos");
                                mudarFiltro("descricao", "sem");
                              }}
                            >
                              Adicione descrições agora
                            </Button>
                          ) : (
                            <div className="text-center text-xs text-green-600 font-medium py-1.5">
                              ✓ Todos os produtos têm descrição!
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Controle financeiro e fiscal (24/10/2026): ficha técnica e dados
                        fiscais lado a lado, com barra e o atalho para filtrar quem falta */}
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Melhore seu controle financeiro e fiscal</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <IndicadorPendencia
                          icone={ChefHat} cor="blue" titulo="Ficha técnica"
                          feitos={cardapioStats.withFichaTecnica} total={cardapioStats.vendaveis}
                          rotuloFeitos="com ficha técnica"
                          porque="Sem ficha, o sistema não sabe o custo: CMV, margem e baixa do estoque ficam errados."
                          rotuloBotao="sem ficha" e2e="indicador-ficha"
                          okTexto="Todos os produtos têm ficha técnica!"
                          onVer={() => verPendentes(() => mudarFiltro("ficha", "sem"))}
                        />
                        <IndicadorPendencia
                          icone={Receipt} cor="violet" titulo="Dados fiscais"
                          feitos={cardapioStats.withFiscal} total={cardapioStats.vendaveis}
                          rotuloFeitos="prontos para a nota fiscal"
                          porque="Sem NCM, CFOP e tributação, a SEFAZ recusa o item e a venda sai sem nota."
                          rotuloBotao="sem dados fiscais" e2e="indicador-fiscal"
                          okTexto="Todos os produtos prontos para a nota!"
                          onVer={() => verPendentes(() => mudarFiltro("fiscal", "sem"))}
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-2">Contam só os produtos vendidos: insumos e receitas ficam de fora.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Toolbar: Busca + Filtros + View Toggle + Actions */}
            <div className="space-y-2">
              {/* Row 1: Search + Actions */}
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Search bar */}
                <div className="flex-1 relative">
                  <div className="flex items-center gap-2 bg-card rounded-lg border px-3 py-1.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20 transition-all">
                    <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <Input
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                      placeholder="Buscar produtos..."
                      className="border-0 bg-transparent focus-visible:ring-0 h-8 text-sm placeholder:text-muted-foreground/60"
                    />
                    {filterName && (
                      <button onClick={() => setFilterName("")} className="p-0.5 hover:bg-muted rounded transition-colors">
                        <X className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button
                    variant={filtersOpen ? "default" : "outline"}
                    size="sm"
                    className="h-10 px-3 relative"
                    onClick={() => setFiltersOpen(!filtersOpen)}
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1.5">Filtros</span>
                    {activeFilterCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>

                  <div className="flex bg-muted rounded-lg p-0.5">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 rounded-md transition-all ${viewMode === "grid" ? "bg-background shadow-sm" : "hover:bg-background/50"}`}
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 rounded-md transition-all ${viewMode === "list" ? "bg-background shadow-sm" : "hover:bg-background/50"}`}
                    >
                      <LayoutList className="w-4 h-4" />
                    </button>
                  </div>

                  <PlanilhaCardapio onImportado={() => { fetchProducts(); fetchCategories(); }} />

                  <Button data-testid="add-product-button" className="h-10 shadow-sm" onClick={() => { setNewProductData({ name: "", salePrice: "", category: "", productType: "produto", isInsumo: false, isDivisible: false, saleUnit: "un" }); setShowNewProductDialog(true); }}>
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1.5">Novo Produto</span>
                  </Button>
                </div>
              </div>

              {/* Filtros (dono, 26/10/2026: mais intuitivos; esgotado e escondido
                  separados; categoria com busca) — features/produtos-filtros */}
              {filtersOpen && products.length > 0 && (
                <PainelFiltros produtos={produtosCardapio} categorias={categories} grupos={GRUPOS_FILTRO}
                  filtros={filtros} aoMudar={mudarFiltro}>
                  <Ordenacao ordem={sortBy} filtrosAtivos={activeFilterCount} aoLimpar={clearAllFilters}
                    aoOrdenar={(campo) => (campo === "alfabetica"
                      ? setSortBy(sortBy === "alfabetica-az" ? "alfabetica-za" : "alfabetica-az")
                      : handleSortToggle(campo))} />
                </PainelFiltros>
              )}

              {/* Results summary + active filter tags (single row) */}
              {products.length > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{sortedProducts.length}</span> de {produtosCardapio.length} produtos
                  </span>
                  {activeFilterCount > 0 && !filtersOpen && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {filterName && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-[10px]">
                          "{filterName}" <button onClick={() => setFilterName("")}><X className="w-2.5 h-2.5" /></button>
                        </span>
                      )}
                      {filtrosAtivos(filtros, GRUPOS_FILTRO).map((a) => (
                        <span key={a.chave} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-[10px] font-medium">
                          {a.rotulo} <button onClick={() => mudarFiltro(a.chave, TODOS)}><X className="w-2.5 h-2.5" /></button>
                        </span>
                      ))}
                      <button onClick={clearAllFilters} className="text-[10px] text-red-500 hover:text-red-600">Limpar</button>
                    </div>
                  )}
                </div>
              )}
            </div>

        {/* PRODUCT LISTING */}
        <div id="lista-produtos" className="scroll-mt-4">
          {/* Fantasma só na 1ª carga: ao recarregar depois de salvar, a lista
              que já está na tela fica (não pisca) */}
          {productsLoading && products.length === 0 ? (
            <ProdutosCarregando modo={viewMode} />
          ) : products.length === 0 ? (
            <div className="bg-card rounded-xl border p-12 text-center">
              <Package className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground font-medium">Nenhum produto cadastrado</p>
              <p className="text-sm text-muted-foreground mt-1 mb-4">Clique em "Novo Produto" para comecar</p>
              <Button onClick={() => { setNewProductData({ name: "", salePrice: "", category: "", productType: "produto", isInsumo: false, isDivisible: false, saleUnit: "un" }); setShowNewProductDialog(true); }}><Plus className="w-4 h-4 mr-2" />Novo Produto</Button>
            </div>
          ) : viewMode === "grid" ? (
            /* ========== GRID VIEW ========== */
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {/* Card especial "Personalização Pizzas" — só aparece quando tenant tem
                  pizzas + na 1ª página. Clica → modal com visão consolidada de
                  tamanhos/bordas + (futuro) ações em massa */}
              {hasPizzaProducts && currentPage === 1 && (
                <button
                  type="button"
                  onClick={() => setPizzaPersonalizacaoOpen(true)}
                  className="relative bg-gradient-to-br from-orange-500/10 to-amber-500/10 dark:from-orange-500/15 dark:to-amber-500/15 rounded-xl border-2 border-orange-500/40 hover:border-orange-500 overflow-hidden transition-all group cursor-pointer text-left flex flex-col items-center justify-center p-6 min-h-[250px]"
                  data-testid="pizza-personalization-card"
                >
                  <div className="w-16 h-16 rounded-2xl bg-orange-500/15 text-orange-500 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                    <Pizza className="w-8 h-8" />
                  </div>
                  <p className="text-sm font-bold text-orange-700 dark:text-orange-300 text-center leading-tight">
                    Personalização Pizzas
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1 text-center leading-snug">
                    Tamanhos, bordas e valores consolidados
                  </p>
                  <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange-500 text-white">
                    Geral
                  </span>
                </button>
              )}
              {paginatedProducts.map((product) => {
                const price = getDisplayPrice(product);
                const cmvPct = price > 0 ? ((product.cmv / price) * 100).toFixed(1) : null;
                const isEsgotado = product.available === false;
                const isEscondido = product.hidden === true;
                const hasPhoto = !!(product.photo_url && product.photo_url.trim());
                const hasDescription = !!(product.description && product.description.trim());
                const hasFicha = !!(product.recipe && product.recipe.length > 0 && product.recipe.some(r => r.ingredient_id));
                // Nota fiscal: verde = campos obrigatórios da NFC-e preenchidos,
                // vermelho = falta (a SEFAZ rejeita o item e a venda sai sem cupom).
                // Mesma checagem da aba Nota Fiscal — regime Simples enquanto a
                // configuração fiscal da loja não expõe o regime (vide TODO na aba).
                const fiscalOk = produtoPronto(parseFiscalConfig(product.fiscal_config));
                return (
                  <div key={product.id} data-testid={`product-card-${product.id}`}
                    className={`relative bg-card rounded-xl border overflow-hidden transition-all group cursor-pointer ${
                      isEscondido
                        ? 'border-zinc-400/60 dark:border-zinc-600/60 ring-1 ring-zinc-400/30 dark:ring-zinc-600/30 opacity-90'
                        : isEsgotado
                          ? 'border-red-300 dark:border-red-900/60 ring-1 ring-red-300/40 dark:ring-red-900/30'
                          : 'hover:shadow-lg hover:border-primary/20 hover:-translate-y-0.5'
                    }`}
                    onClick={() => handleEdit(product)}
                  >
                    {/* Image */}
                    <div className="relative aspect-square bg-muted/20 overflow-hidden">
                      {product.photo_url ? (
                        <img
                          src={product.photo_url.startsWith("data:") ? product.photo_url : `/api${product.photo_url}`}
                          alt={product.name}
                          className={`w-full h-full object-cover transition-transform duration-300 ${isEscondido ? 'grayscale opacity-50 blur-[1px]' : isEsgotado ? 'grayscale opacity-70' : 'group-hover:scale-105'}`}
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                        />
                      ) : null}
                      <div className={`${product.photo_url ? 'hidden' : 'flex'} absolute inset-0 flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-zinc-800 dark:to-zinc-900`}>
                        <ImageOff className="w-10 h-10 text-muted-foreground/20" />
                      </div>

                      {/* Banner ESCONDIDO (prioridade sobre ESGOTADO) */}
                      {isEscondido ? (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/70 via-zinc-900/30 to-transparent" />
                          <div className="relative bg-zinc-700 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-md shadow-lg rotate-[-6deg] ring-2 ring-white/30 flex items-center gap-1">
                            <EyeOff className="w-3 h-3" /> Escondido
                          </div>
                        </div>
                      ) : isEsgotado && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="absolute inset-0 bg-gradient-to-t from-red-900/70 via-red-900/20 to-transparent" />
                          <div className="relative bg-red-500 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-md shadow-lg rotate-[-6deg] ring-2 ring-white/30">
                            Esgotado
                          </div>
                        </div>
                      )}

                      {/* Type badges — top-left; no celular vão para baixo, porque o
                          cartão é estreito e elas encostavam na pílula ATIVO e no ⋮ */}
                      {(product.product_type === "combo" || product.product_type === "receita" || product.is_insumo || product.is_divisible) && (
                        <div className="absolute bottom-2 sm:bottom-auto sm:top-2 left-2 flex gap-1 flex-wrap max-w-[90%] sm:max-w-[70%]">
                          {product.product_type === "combo" && (
                            <span className="px-1.5 py-0.5 bg-orange-500 text-white text-[9px] font-bold rounded-md shadow uppercase tracking-wider">Combo</span>
                          )}
                          {product.product_type === "receita" && (
                            <span className="px-1.5 py-0.5 bg-purple-500 text-white text-[9px] font-bold rounded-md shadow uppercase tracking-wider">Receita</span>
                          )}
                          {product.is_insumo && (
                            <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-md shadow uppercase tracking-wider">Insumo</span>
                          )}
                          {product.is_divisible && (
                            <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-md shadow uppercase tracking-wider">Divisível</span>
                          )}
                        </div>
                      )}

                      {/* Top-right: availability toggle (sempre visível) + menu (hover) */}
                      <div className="absolute top-2 right-2 flex items-center gap-1.5">
                        {/* Toggle de Disponibilidade — pílula visível sempre */}
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); toggleProductAvailability(product, e); }}
                          className={`flex items-center gap-1 pl-1.5 pr-2.5 py-1.5 sm:pl-1 sm:pr-2 sm:py-0.5 rounded-full shadow-md transition-all backdrop-blur-sm ${
                            isEsgotado
                              ? 'bg-red-500/95 hover:bg-red-600 text-white'
                              : 'bg-emerald-500/95 hover:bg-emerald-600 text-white'
                          }`}
                          title={isEsgotado ? "Clique para reativar" : "Clique para esgotar"}
                        >
                          <span className={`w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center transition-transform ${isEsgotado ? '' : 'scale-100'}`}>
                            {isEsgotado
                              ? <X className="w-2.5 h-2.5 text-red-500" strokeWidth={4} />
                              : <Check className="w-2.5 h-2.5 text-emerald-500" strokeWidth={4} />}
                          </span>
                          <span className="text-[9px] font-black uppercase tracking-wider leading-none">
                            {isEsgotado ? 'Esgotado' : 'Ativo'}
                          </span>
                        </button>
                        {/* Menu (3-dot) — no mouse só aparece no hover; em tela de
                            toque não existe hover, então fica sempre visível (senão
                            Duplicar/Esconder/Excluir ficavam inalcançáveis no celular) */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <button aria-label="Mais ações" className="w-8 h-8 sm:w-6 sm:h-6 rounded-full flex items-center justify-center bg-black/50 hover:bg-black/70 text-white shadow-md transition-all [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 data-[state=open]:opacity-100">
                              <MoreVertical className="w-3.5 h-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem onClick={() => openDuplicateDialog(product)}>
                              <Copy className="w-4 h-4 mr-2" /> Duplicar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => toggleProductHidden(product, e)}>
                              {isEscondido ? (
                                <><Eye className="w-4 h-4 mr-2" /> Mostrar nas vendas</>
                              ) : (
                                <><EyeOff className="w-4 h-4 mr-2" /> Esconder das vendas</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => confirmDelete(product.id, product.name)}>
                              <Trash2 className="w-4 h-4 mr-2" /> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-3 space-y-2">
                      {/* Linha 1: nome */}
                      <div>
                        <h3 className={`font-semibold text-sm leading-tight line-clamp-2 ${isEsgotado ? 'text-muted-foreground line-through decoration-red-500/40' : ''}`}>
                          {product.name}
                        </h3>
                        {/* Categoria como chip sutil */}
                        {product.category && (
                          <div className="mt-1">
                            <span className="inline-block text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded leading-tight">
                              {product.category}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Descrição preview (se existir) */}
                      {hasDescription && (
                        <p className="text-[11px] text-muted-foreground/80 line-clamp-2 leading-snug italic">
                          {product.description}
                        </p>
                      )}

                      {/* Linha de preço + CMV */}
                      <div className="flex items-center justify-between pt-1 border-t border-dashed border-border/60">
                        {product.product_type !== "receita" ? (
                          editingPriceId === product.id ? (
                            <input
                              autoFocus
                              className="w-20 text-sm font-bold font-mono bg-background border rounded-md px-2 py-1"
                              value={String(tempPrice).replace(".", ",")}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setTempPrice(sanitizeCurrencyInput(e.target.value))}
                              onBlur={() => saveQuickPrice(product.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveQuickPrice(product.id);
                                if (e.key === 'Escape') { setEditingPriceId(null); setTempPrice(''); }
                              }}
                            />
                          ) : (
                            <span
                              className={`text-base font-bold font-mono cursor-text transition-colors ${isEsgotado ? 'text-muted-foreground' : 'hover:text-primary'}`}
                              onClick={(e) => { e.stopPropagation(); setEditingPriceId(product.id); setTempPrice(price ? price.toFixed(2) : ''); }}
                              title="Clique para editar preço"
                            >
                              R$ {price ? price.toFixed(2).replace(".", ",") : "0,00"}
                              {product.sale_unit && product.sale_unit !== "un" && (
                                <span className="text-[10px] text-muted-foreground font-normal ml-0.5">/{product.sale_unit}</span>
                              )}
                            </span>
                          )
                        ) : (
                          <span className="text-base font-bold font-mono text-purple-600 dark:text-purple-400">
                            R$ {product.cmv.toFixed(2).replace(".", ",")}
                          </span>
                        )}
                        {cmvPct && product.product_type !== "receita" && (
                          <span
                            className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              parseFloat(cmvPct) <= 30 ? 'text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/20' :
                              parseFloat(cmvPct) <= 40 ? 'text-yellow-700 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20' :
                              'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/20'
                            }`}
                            title="Custo da Mercadoria Vendida"
                          >
                            CMV {String(cmvPct).replace('.', ',')}%
                          </span>
                        )}
                      </div>

                      {/* Linha de status: foto / descrição / ficha técnica */}
                      <div className="flex items-center gap-2.5 pt-0.5">
                        <span
                          className={`inline-flex items-center gap-0.5 text-[10px] ${hasPhoto ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/50'}`}
                          title={hasPhoto ? "Tem foto" : "Sem foto"}
                        >
                          <Camera className="w-3 h-3" />
                          {hasPhoto ? <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> : <X className="w-2.5 h-2.5" strokeWidth={3.5} />}
                        </span>
                        <span
                          className={`inline-flex items-center gap-0.5 text-[10px] ${hasDescription ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/50'}`}
                          title={hasDescription ? "Tem descrição" : "Sem descrição"}
                        >
                          <FileText className="w-3 h-3" />
                          {hasDescription ? <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> : <X className="w-2.5 h-2.5" strokeWidth={3.5} />}
                        </span>
                        {product.product_type !== "receita" && (
                          <span
                            className={`inline-flex items-center gap-0.5 text-[10px] ${hasFicha ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/50'}`}
                            title={hasFicha ? "Tem ficha técnica" : "Sem ficha técnica"}
                          >
                            <ChefHat className="w-3 h-3" />
                            {hasFicha ? <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> : <X className="w-2.5 h-2.5" strokeWidth={3.5} />}
                          </span>
                        )}
                        {product.product_type !== "receita" && (
                          <span
                            className={`inline-flex items-center gap-0.5 text-[10px] ${fiscalOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
                            title={fiscalOk ? "Nota fiscal: dados completos" : "Nota fiscal: faltam campos obrigatórios (NCM, CFOP, Origem, CSOSN)"}
                          >
                            <Receipt className="w-3 h-3" />
                            {fiscalOk ? <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> : <X className="w-2.5 h-2.5" strokeWidth={3.5} />}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ========== LIST VIEW ========== */
            <div className="bg-card rounded-xl border overflow-hidden">
              {/* Table header */}
              <div className="hidden sm:flex items-center px-4 py-2.5 bg-muted/40 border-b text-[11px] font-medium text-muted-foreground uppercase tracking-wider gap-3">
                <div className="w-8" />
                <div className="w-10" />
                <div className="flex-1 min-w-0">Produto</div>
                <div className="w-24 hidden md:block">Categoria</div>
                <div className="w-24 text-right">Preço</div>
                <div className="w-20 text-right hidden lg:block">Custo</div>
                <div className="w-16 text-center hidden lg:block">CMV%</div>
                <div className="w-8" />
              </div>

              {paginatedProducts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">Nenhum produto encontrado</div>
              ) : paginatedProducts.map((product, idx) => {
                const price = getDisplayPrice(product);
                const cmvPct = price > 0 ? ((product.cmv / price) * 100).toFixed(1) : null;
                return (
                  <div
                    key={product.id}
                    data-testid={`product-card-${product.id}`}
                    className={`flex items-center px-3 sm:px-4 py-2.5 gap-2.5 sm:gap-3 hover:bg-muted/30 transition-colors cursor-pointer ${idx > 0 ? 'border-t' : ''} ${product.available === false ? 'opacity-40' : ''}`}
                    onClick={() => handleEdit(product)}
                  >
                    {/* Availability toggle */}
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleProductAvailability(product, e); }}
                      aria-label={product.available !== false ? "Esgotar produto" : "Reativar produto"}
                      className={`relative inline-flex h-6 w-10 sm:h-5 sm:w-8 items-center rounded-full flex-shrink-0 transition-colors ${product.available !== false ? 'bg-green-500' : 'bg-red-400'}`}
                    >
                      <span className={`inline-block h-[18px] w-[18px] sm:h-3.5 sm:w-3.5 rounded-full bg-white shadow transition-transform ${product.available !== false ? 'translate-x-5 sm:translate-x-4' : 'translate-x-0.5'}`} />
                    </button>

                    {/* Photo */}
                    <div className="w-10 h-10 rounded-lg border overflow-hidden flex-shrink-0 bg-muted/30">
                      <ProductThumbnail photoUrl={product.photo_url} name={product.name} />
                    </div>

                    {/* Name + type */}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm line-clamp-2 break-words sm:line-clamp-none sm:truncate">{product.name}</div>
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        {product.product_type === "combo" && <span className="text-[10px] font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 px-1.5 py-0.5 rounded">Combo</span>}
                        {product.product_type === "receita" && <span className="text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-1.5 py-0.5 rounded">Receita</span>}
                        {product.is_insumo && <span className="text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded">Insumo</span>}
                        {product.hidden === true && <span className="inline-flex items-center gap-0.5 text-[10px] font-medium bg-zinc-200 text-zinc-700 dark:bg-zinc-700/60 dark:text-zinc-300 px-1.5 py-0.5 rounded"><EyeOff className="w-2.5 h-2.5" /> Escondido</span>}
                        {product.code && <span className="text-[10px] text-muted-foreground sm:hidden">#{product.code}</span>}
                      </div>
                    </div>

                    {/* Category */}
                    <div className="w-24 hidden md:block">
                      <span className="text-xs text-muted-foreground truncate block">{product.category || '-'}</span>
                    </div>

                    {/* Price */}
                    <div className="w-auto min-w-[64px] sm:w-24 text-right flex-shrink-0">
                      {product.product_type === "receita" ? (
                        <span className="text-sm font-bold font-mono text-purple-600 dark:text-purple-400">R$ {product.cmv.toFixed(2).replace(".", ",")}</span>
                      ) : editingPriceId === product.id ? (
                        <input
                          autoFocus
                          className="w-full text-sm font-mono font-bold text-right bg-background border rounded px-1.5 py-0.5"
                          value={String(tempPrice).replace(".", ",")}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setTempPrice(sanitizeCurrencyInput(e.target.value))}
                          onBlur={() => saveQuickPrice(product.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveQuickPrice(product.id);
                            if (e.key === 'Escape') { setEditingPriceId(null); setTempPrice(''); }
                          }}
                        />
                      ) : (
                        <span
                          className="text-sm font-bold font-mono cursor-text hover:text-primary transition-colors"
                          onClick={(e) => { e.stopPropagation(); setEditingPriceId(product.id); setTempPrice(price ? price.toFixed(2) : ''); }}
                        >
                          R$ {price ? price.toFixed(2).replace(".", ",") : "0,00"}
                          {product.sale_unit && product.sale_unit !== "un" && (
                            <span className="text-[10px] text-muted-foreground font-normal ml-0.5">/{product.sale_unit}</span>
                          )}
                        </span>
                      )}
                    </div>

                    {/* CMV */}
                    <div className="w-20 text-right hidden lg:block">
                      {product.cmv > 0 && product.product_type !== "receita" && (
                        <span className="text-xs font-mono text-muted-foreground">R$ {product.cmv.toFixed(2).replace(".", ",")}</span>
                      )}
                    </div>

                    {/* CMV% */}
                    <div className="w-16 text-center hidden lg:block">
                      {cmvPct && product.product_type !== "receita" && (
                        <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${
                          parseFloat(cmvPct) <= 30 ? 'text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/20' :
                          parseFloat(cmvPct) <= 40 ? 'text-yellow-700 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20' :
                          'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/20'
                        }`}>
                          {String(cmvPct).replace('.', ',')}%
                        </span>
                      )}
                    </div>

                    {/* Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <button className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-muted transition-colors flex-shrink-0">
                          <MoreVertical className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => openDuplicateDialog(product)}>
                          <Copy className="w-4 h-4 mr-2" /> Duplicar
                        </DropdownMenuItem>
                        {/* Mesmo item do ⋮ dos cartões — faltava aqui, e esconder
                            produto não existe em nenhum outro lugar da tela. */}
                        <DropdownMenuItem onClick={(e) => toggleProductHidden(product, e)}>
                          {product.hidden === true ? (
                            <><Eye className="w-4 h-4 mr-2" /> Mostrar nas vendas</>
                          ) : (
                            <><EyeOff className="w-4 h-4 mr-2" /> Esconder das vendas</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => confirmDelete(product.id, product.name)}>
                          <Trash2 className="w-4 h-4 mr-2" /> Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination - shared by both views */}
          {sortedProducts.length > 0 && !productsLoading && (
            <div className="mt-4 bg-card rounded-xl border overflow-hidden">
              <TablePagination
                currentPage={currentPage}
                totalItems={sortedProducts.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </div>

        {/* Excluir: sem venda apaga de vez, com venda arquiva (features/excluir-cardapio) */}
        <ExcluirProdutoDialog produto={productToDelete} aoFechar={() => setProductToDelete(null)}
          aoExcluir={(id) => { setProducts(prev => prev.filter(p => p.id !== id)); fetchCategories(); }} />

        {/* Dialog de Duplicar Produto */}
        <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Duplicar Produto</AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p>
                  Você está duplicando o produto <strong>{productToDuplicate?.name}</strong>.
                </p>
                <p className="text-sm">
                  O novo produto terá a mesma ficha técnica, categoria, preço e configurações.
                </p>
                <div className="pt-2">
                  <Label htmlFor="duplicateName" className="text-foreground">Nome do novo produto</Label>
                  <Input
                    id="duplicateName"
                    value={duplicateName}
                    onChange={(e) => setDuplicateName(e.target.value)}
                    placeholder="Digite o nome do novo produto"
                    className="mt-2"
                    autoFocus
                  />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={duplicatingProduct}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDuplicateProduct}
                disabled={duplicatingProduct || !duplicateName.trim()}
                className="bg-primary"
              >
                {duplicatingProduct ? "Duplicando..." : "Duplicar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
          </TabsContent>

          {/* Aba de Categorias */}
          <TabsContent value="categories" className="space-y-4">

            {/* Turnos do Cardápio — agora no TOPO da aba Categorias */}
            <CardapioProfilesSection
              categories={categories}
              semCategoriaCount={(products || []).filter(p => !p.is_insumo && !String(p.category || '').trim()).length}
              tenantApi={API}
              authHeader={getAuthHeader}
            />

            {/* Setores — separam a comanda por praça (Bar, Cozinha, Churrasqueira)
                e são a base do filtro do KDS: o bartender só vê o que é dele.
                O bloco ficava escondido enquanto não houvesse nenhum setor, e o
                botão de criar o primeiro morava DENTRO dele — ninguém nunca
                conseguiu criar setor nenhum (zero em 18 lojas até 22/09/2026). */}
            <div className="bg-card rounded-xl border shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-base">Setores</h3>
                <Dialog open={setorDialogOpen} onOpenChange={(isOpen) => {
                  setSetorDialogOpen(isOpen);
                  if (!isOpen) { setSetorName(""); setSetorTempo(""); setEditSetorMode(false); setCurrentSetorId(null); }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      Novo Setor
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editSetorMode ? "Editar Setor" : "Novo Setor"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateSetor} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="setor-name">Nome do Setor</Label>
                        <Input
                          id="setor-name"
                          value={setorName}
                          onChange={(e) => setSetorName(e.target.value)}
                          placeholder="Ex: Cozinha, Bar, Churrasqueira..."
                          required
                          className="mt-1"
                        />
                      </div>
                      <CampoTempoProducao valor={setorTempo} onChange={setSetorTempo} />
                      <Button type="submit" disabled={loading} className="w-full">
                        {loading ? (editSetorMode ? "Atualizando..." : "Criando...") : (editSetorMode ? "Atualizar" : "Criar")}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {!setoresCarregados ? (
                <div className="flex flex-wrap gap-2" aria-busy="true" aria-label="Carregando">
                  {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-32 rounded-lg" />)}
                </div>
              ) : setores.length === 0 && (
                <p className="text-sm text-muted-foreground mb-3">
                  Nenhum setor ainda. Crie um por praça da casa (Bar, Cozinha) e
                  depois escolha o setor de cada categoria na lista abaixo — é isso
                  que deixa cada tela do KDS mostrar só os itens dela.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {setores.map(setor => (
                  <div key={setor.id} className="flex items-center gap-1 bg-primary/5 border border-primary/20 rounded-lg px-3 py-1.5">
                    <span className="text-sm font-medium">{setor.name}</span>
                    {rotuloTempoSetor(setor) && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${Number(setor.tempo_producao) === 0 ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-amber-500/15 text-amber-700 dark:text-amber-400"}`}
                        title="Tempo de produção do setor">
                        ⏱ {rotuloTempoSetor(setor)}
                      </span>
                    )}
                    <Button onClick={() => handleEditSetor(setor)} variant="ghost" size="sm" className="h-8 w-8 sm:h-6 sm:w-6 p-0 hover:bg-muted" aria-label={`Editar setor ${setor.name}`}>
                      <Edit className="w-3 h-3" strokeWidth={1.5} />
                    </Button>
                    <Button onClick={() => { setSetorToDelete(setor); setDeleteSetorDialogOpen(true); }} variant="ghost" size="sm" className="h-8 w-8 sm:h-6 sm:w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" aria-label={`Excluir setor ${setor.name}`}>
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* AlertDialog de exclusão de setor */}
            <AlertDialog open={deleteSetorDialogOpen} onOpenChange={setDeleteSetorDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir o setor <strong>{setorToDelete?.name}</strong>?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteSetor} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <div className="flex justify-end">
              <Dialog open={categoryDialogOpen} onOpenChange={(isOpen) => {
                setCategoryDialogOpen(isOpen);
                if (!isOpen) {
                  setCategoryName("");
                  setCategorySetorId("");
                  setCategoryPrintAlert(false);
                  setEditCategoryMode(false);
                  setCurrentCategoryId(null);
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="shadow-sm">
                    <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    Nova Categoria
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editCategoryMode ? "Editar Categoria" : "Nova Categoria"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="category-name">Nome da Categoria</Label>
                      <Input
                        id="category-name"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        placeholder="Ex: Sanduíches, Bebidas..."
                        required
                        className="mt-1"
                      />
                    </div>
                    <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/40 transition-colors">
                      <input
                        type="checkbox"
                        checked={categoryPrintAlert}
                        onChange={(e) => setCategoryPrintAlert(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-orange-500 cursor-pointer"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold">Destacar no cupom de entrega</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          Quando o pedido tiver item desta categoria, o cupom imprime <span className="font-mono">"Contém {categoryName || "CATEGORIA"}"</span>. Se mais de uma categoria marcada estiver no pedido, só a primeira pela ordem da lista é impressa.
                        </span>
                      </span>
                    </label>
                    <Button type="submit" disabled={loading} className="w-full">
                      {loading ? (editCategoryMode ? "Atualizando..." : "Criando...") : (editCategoryMode ? "Atualizar" : "Criar")}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
              {!categoriasCarregadas ? (
                <CarregandoLista linhas={6} avatar={false} colunas={2} className="" />
              ) : categories.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  Nenhuma categoria cadastrada. Clique em "Nova Categoria" para começar.
                </div>
              ) : (
                <CategoryList
                  categories={categories}
                  setores={setores}
                  onReorder={async (newOrder) => {
                    const categoryOrders = newOrder.map((cat, idx) => ({
                      id: cat.id,
                      display_order: idx
                    }));
                    
                    // Atualizar estado local imediatamente para evitar tela apagando
                    setCategories(newOrder);
                    
                    try {
                      await axios.put(
                        `${API}/categories/order`,
                        { category_orders: categoryOrders },
                        getAuthHeader()
                      );
                      toast.success("Ordem das categorias atualizada");
                      // Recarregar do servidor para garantir sincronização
                      fetchCategories();
                    } catch (error) {
                      // Em caso de erro, reverter para o estado anterior
                      fetchCategories();
                      toast.error(mensagemDeErro(error, "Erro ao atualizar ordem"));
                    }
                  }}
                  onMoveUp={async (categoryId) => {
                    try {
                      await axios.post(`${API}/categories/${categoryId}/move-up`, {}, getAuthHeader());
                      toast.success("Categoria movida para cima");
                      fetchCategories();
                    } catch (error) {
                      toast.error(mensagemDeErro(error, "Erro ao mover categoria"));
                    }
                  }}
                  onMoveDown={async (categoryId) => {
                    try {
                      await axios.post(`${API}/categories/${categoryId}/move-down`, {}, getAuthHeader());
                      toast.success("Categoria movida para baixo");
                      fetchCategories();
                    } catch (error) {
                      toast.error(mensagemDeErro(error, "Erro ao mover categoria"));
                    }
                  }}
                  onEdit={handleEditCategory}
                  onDelete={confirmDeleteCategory}
                  onSetorChange={async (categoryId, setorId) => {
                    try {
                      const cat = categories.find(c => c.id === categoryId);
                      if (!cat) return;
                      await axios.put(`${API}/categories/${categoryId}`, { name: cat.name, setor_id: setorId }, getAuthHeader());
                      setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, setor_id: setorId } : c));
                      toast.success("Setor atualizado!");
                    } catch (error) {
                      toast.error(mensagemDeErro(error, "Erro ao atualizar setor"));
                    }
                  }}
                  onToggleHidden={async (cat) => {
                    const newHidden = !(cat.hidden === true);
                    // Otimista
                    setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, hidden: newHidden } : c));
                    try {
                      await axios.put(`${API}/categories/${cat.id}`, {
                        name: cat.name,
                        setor_id: cat.setor_id || null,
                        print_alert: cat.print_alert || false,
                        hidden: newHidden,
                      }, getAuthHeader());
                      toast.success(newHidden
                        ? `Categoria "${cat.name}" escondida — produtos não aparecem no cardápio`
                        : `Categoria "${cat.name}" visível novamente`);
                    } catch (error) {
                      // Revert
                      setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, hidden: !newHidden } : c));
                      toast.error(mensagemDeErro(error, "Erro ao atualizar visibilidade"));
                    }
                  }}
                />
              )}
            </div>

            {/* Excluir categoria: vazia apaga; com produtos lista tudo e apaga junto
                (sem venda some de vez, com venda arquiva) — features/excluir-cardapio */}
            <ExcluirCategoriaDialog categoria={categoryToDelete} aoFechar={() => setCategoryToDelete(null)}
              aoExcluir={() => {
                if (filtros.categoria === categoryToDelete?.name) mudarFiltro("categoria", TODOS);
                fetchCategories();
                fetchProducts();
              }} />
          </TabsContent>

          {/* Aba de Arquivados */}
          <TabsContent value="archived" className="space-y-4">
            <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Archive className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <h3 className="font-semibold">Produtos Arquivados</h3>
                  {arquivadosCarregados && <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{archivedProducts.length}</span>}
                </div>
                <Button variant="outline" size="sm" onClick={fetchArchivedProducts} disabled={archivedLoading}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${archivedLoading ? 'animate-spin' : ''}`} /> Atualizar
                </Button>
              </div>
              {!arquivadosCarregados ? (
                <CarregandoLista linhas={4} colunas={0} className="" />
              ) : archivedProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Archive className="w-12 h-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground font-medium">Nenhum produto arquivado</p>
                  <p className="text-xs text-muted-foreground mt-1">Produtos arquivados pela lixeira aparecem aqui</p>
                </div>
              ) : (
                <div className="divide-y">
                  {archivedProducts.map((product) => (
                    // Celular: foto + nome/detalhes em cima e os botões numa linha
                    // própria embaixo — lado a lado, o nome virava "ZZT Mega Co..."
                    // e a data quebrava em três linhas.
                    <div key={product.id} className="flex flex-wrap sm:flex-nowrap items-center px-3 sm:px-4 py-3 gap-x-3 gap-y-2 hover:bg-muted/30">
                      <div className="w-10 h-10 rounded-lg border overflow-hidden flex-shrink-0 flex items-center justify-center bg-muted/30 opacity-60">
                        {product.photo_url ? (
                          <img src={product.photo_url.startsWith("data:") || product.photo_url.startsWith("http") ? product.photo_url : `/api${product.photo_url}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageOff className="w-4 h-4 text-muted-foreground/30" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm break-words sm:truncate">{product.name}</p>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                          {product.category && <span>{product.category}</span>}
                          {product.archived_at && <span>Arquivado em {new Date(product.archived_at).toLocaleDateString('pt-BR')}</span>}
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                      <Button variant="outline" size="sm" className="flex-1 sm:flex-none h-9 sm:h-8" onClick={() => handleRestoreProduct(product.id)}>
                        <ArchiveRestore className="w-4 h-4 mr-1" /> Restaurar
                      </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>

      {/* Popup de Cadastro Rápido de Ingrediente */}
      <QuickIngredientDialog
        open={quickIngredientOpen}
        onOpenChange={setQuickIngredientOpen}
        initialName={quickIngredientName}
        onSuccess={handleQuickIngredientSuccess}
        categories={ingredientCategories}
      />

      {/* Popup de Novo Produto - Wizard Inicial */}
      <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
        <DialogContent className="sm:max-w-lg max-h-[94vh] overflow-y-auto p-0 gap-0 border border-border/60 shadow-xl">
          {/* Header — leve, com apenas um toque de cor */}
          <div className="px-6 pt-6 pb-4 border-b border-border/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-orange-500" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-xl font-bold text-foreground leading-tight">Novo Produto</DialogTitle>
                <DialogDescription className="text-muted-foreground text-xs mt-0.5">Em poucos segundos no cardápio</DialogDescription>
              </div>
            </div>
          </div>

          <div className="px-6 py-6">
            {/* Stepper vertical com linha conectando as etapas */}
            <div className="relative">
              {/* Linha vertical de fundo (timeline) */}
              <div className="absolute left-[15px] top-8 bottom-8 w-px bg-gradient-to-b from-border via-border to-border/30" aria-hidden="true" />

              {/* ETAPA 1 — Tipo */}
              <section className="relative pl-12 pb-7">
                {/* Bolha numerada */}
                <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ring-4 ring-background ${
                  newProductData.productType
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-500/30'
                    : 'bg-orange-500/10 text-orange-600 dark:text-orange-400 ring-orange-500/20 ring-2'
                }`}>
                  {newProductData.productType ? <Check className="w-4 h-4" strokeWidth={3.5} /> : '1'}
                </div>
                <div className="-mt-1">
                  <h3 className="text-base font-bold text-foreground leading-tight">Que tipo de item?</h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Escolha o que melhor descreve</p>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {[
                    // Ícones no lugar dos emojis (dono, 26/10/2026)
                    { value: "produto", label: "Produto", Icon: CupSoda, desc: "Item simples" },
                    { value: "combo", label: "Combo", Icon: IconeCombo, desc: "Simples ou em combo" },
                    { value: "pizza", label: "Pizza", Icon: Pizza, desc: "Sabores e tamanhos", isPizza: true },
                  ].map(({ value, label, Icon, desc, isPizza }) => {
                    const selected = newProductData.productType === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setNewProductData(prev => ({
                            ...prev,
                            productType: value,
                            isDivisible: prev.isDivisible,
                          }));
                          // Pizza: carrega config da pizza-mãe atual (todos os tamanhos
                          // criados ficam disponíveis pra escolher preço). Se não há mãe,
                          // cai no catálogo legado (1ª pizza do tenant).
                          if (isPizza) {
                            setPizzaPricesExcluded(new Set());
                            loadPizzaConfig(findMotherPizzaIdInProducts());
                          }
                        }}
                        className={`group relative flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left transition-all ${
                          selected
                            ? 'border-orange-500 bg-orange-500/[0.06] dark:bg-orange-500/10 shadow-sm shadow-orange-500/10'
                            : 'border-border/70 hover:border-orange-300 hover:bg-muted/40'
                        }`}
                      >
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${selected ? 'bg-orange-500/15 text-orange-600 dark:text-orange-400' : 'bg-muted text-muted-foreground group-hover:text-foreground'}`}>
                          <Icon className="w-5 h-5" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className={`block text-sm font-semibold leading-tight ${selected ? 'text-orange-700 dark:text-orange-300' : 'text-foreground'}`}>{label}</span>
                          <span className="block text-[10px] text-muted-foreground leading-tight mt-0.5">{desc}</span>
                        </span>
                        {selected && (
                          <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* ETAPA 2 — Identificação */}
              <section className="relative pl-12 pb-7">
                <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ring-4 ring-background ${
                  newProductData.name.trim() && newProductData.category
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-500/30'
                    : newProductData.productType
                      ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 ring-orange-500/20 ring-2'
                      : 'bg-muted text-muted-foreground'
                }`}>
                  {newProductData.name.trim() && newProductData.category ? <Check className="w-4 h-4" strokeWidth={3.5} /> : '2'}
                </div>
                <div className="-mt-1">
                  <h3 className="text-base font-bold text-foreground leading-tight">Identificação</h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Nome e categoria do item — os dois são obrigatórios</p>
                </div>
                <div className="space-y-2 mt-3">
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 pointer-events-none" />
                    <Input
                      placeholder="Ex: X-Burger, Coca 350ml…"
                      className="pl-9 h-10 text-sm border-border/70 focus:border-orange-500"
                      value={newProductData.name}
                      onChange={(e) => setNewProductData(prev => ({ ...prev, name: e.target.value }))}
                      autoFocus
                    />
                  </div>
                  <ProductCategorySearch
                    categories={categories}
                    value={newProductData.category}
                    onChange={(val) => setNewProductData(prev => ({ ...prev, category: val }))}
                    onCategoryCreated={fetchCategories}
                  />
                  {/* Categoria obrigatória (dono, 26/10/2026): produto sem categoria não é criado */}
                  {newProductData.name.trim() && !newProductData.category && (
                    <p data-e2e="novo-produto-falta-categoria" className="text-[11px] text-amber-600 dark:text-amber-400 leading-tight">
                      Escolha uma categoria ou crie uma nova digitando o nome.
                    </p>
                  )}
                </div>
              </section>

              {/* ETAPA 3 — Pizza: matriz de preço por tamanho (todos os tamanhos
                  da pizza-mãe; lojista pode deletar os que este sabor não vende) */}
              {newProductData.productType === "pizza" && (() => {
                const visibleSizes = (pizzaConfig.sizes || []).filter(s => !pizzaPricesExcluded.has(s.id));
                return (
                <section className="relative pl-12 pb-7">
                  <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ring-4 ring-background ${
                    Object.entries(pizzaPrices).some(([sid, v]) => !pizzaPricesExcluded.has(sid) && parsePizzaPrice(v) > 0)
                      ? 'bg-orange-500 text-white shadow-md shadow-orange-500/30'
                      : 'bg-orange-500/10 text-orange-600 dark:text-orange-400 ring-orange-500/20 ring-2'
                  }`}>
                    {Object.entries(pizzaPrices).some(([sid, v]) => !pizzaPricesExcluded.has(sid) && parsePizzaPrice(v) > 0) ? <Check className="w-4 h-4" strokeWidth={3.5} /> : '3'}
                  </div>
                  <div className="-mt-1">
                    <h3 className="text-base font-bold text-foreground leading-tight">Preço por tamanho</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Quanto este sabor custa em cada tamanho. Use a lixeira pra remover tamanhos que este sabor não vende.
                    </p>
                  </div>
                  <div className="mt-3">
                    {visibleSizes.length === 0 ? (
                      <div className="rounded-lg border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-4 text-center">
                        {pizzaPricesExcluded.size > 0 ? (
                          <>
                            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-1">Você removeu todos os tamanhos</p>
                            <Button type="button" size="sm" variant="outline"
                              onClick={() => setPizzaPricesExcluded(new Set())}
                              className="border-amber-400 text-amber-700 hover:bg-amber-50"
                            >
                              Restaurar todos
                            </Button>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-1">Nenhum tamanho cadastrado ainda</p>
                            <p className="text-xs text-muted-foreground">Esta é a primeira pizza do tenant — depois de criar, vá em Personalização pra adicionar tamanhos.</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {visibleSizes.map(s => (
                          <div key={s.id} className="group flex items-center gap-2 rounded-lg bg-muted/40 border border-border/60 px-3 py-2 hover:border-orange-300 transition-colors">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold leading-tight truncate">{s.name}</p>
                              <p className="text-[10px] text-muted-foreground leading-tight">
                                {s.slices ? `${s.slices} fatias · ` : ""}até {s.max_flavors} sabor(es)
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className="text-xs text-muted-foreground">R$</span>
                              {/* Caixa registradora, como o valor de venda (26/10/2026). Guarda
                                  com vírgula: o parsePrice da pizza lê ponto como milhar */}
                              <div className="w-24">
                                <InputDecimal
                                  data-e2e={`novo-produto-preco-tamanho-${s.id}`}
                                  decimals={2}
                                  value={parsePizzaPrice(pizzaPrices[s.id]) > 0 ? String(parsePizzaPrice(pizzaPrices[s.id])) : ""}
                                  onChange={(v) => setPizzaPrices(prev => ({ ...prev, [s.id]: parseFloat(v) > 0 ? String(v).replace(".", ",") : "" }))}
                                  className="h-9 text-sm"
                                />
                              </div>
                            </div>
                            <Button type="button" size="icon" variant="ghost"
                              onClick={() => setPizzaPricesExcluded(prev => { const n = new Set(prev); n.add(s.id); return n; })}
                              className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              title={`Este sabor não vende no tamanho ${s.name}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        {pizzaPricesExcluded.size > 0 && (
                          <button type="button"
                            onClick={() => setPizzaPricesExcluded(new Set())}
                            className="w-full text-[11px] text-muted-foreground hover:text-foreground py-1 underline-offset-2 hover:underline"
                          >
                            Restaurar {pizzaPricesExcluded.size} tamanho{pizzaPricesExcluded.size > 1 ? "s" : ""} removido{pizzaPricesExcluded.size > 1 ? "s" : ""}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </section>
                );
              })()}

              {/* ETAPA 3 — Preço (oculto para Receita e Pizza) */}
              {newProductData.productType !== "receita" && newProductData.productType !== "pizza" && (
                <section className="relative pl-12 pb-7">
                  <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ring-4 ring-background ${
                    parseFloat(newProductData.salePrice) > 0
                      ? 'bg-orange-500 text-white shadow-md shadow-orange-500/30'
                      : newProductData.name.trim()
                        ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 ring-orange-500/20 ring-2'
                        : 'bg-muted text-muted-foreground'
                  }`}>
                    {parseFloat(newProductData.salePrice) > 0 ? <Check className="w-4 h-4" strokeWidth={3.5} /> : '3'}
                  </div>
                  <div className="-mt-1">
                    <h3 className="text-base font-bold text-foreground leading-tight">Valor de venda</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Quanto o cliente vai pagar por este item</p>
                  </div>
                  <div className="mt-3 flex gap-1.5">
                    {[
                      { value: "un", label: "Unidade" },
                      { value: "kg", label: "Kg" },
                      { value: "L", label: "Litro" }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setNewProductData(prev => ({
                          ...prev,
                          saleUnit: opt.value,
                          // Quilo/Litro liga o Divisível sozinho (dá pra desligar em "Configurações extras")
                          ...(ehUnidadePeso(opt.value) && opt.value !== prev.saleUnit ? { isDivisible: true } : {}),
                        }))}
                        className={`flex-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-all ${
                          newProductData.saleUnit === opt.value
                            ? 'bg-orange-500 text-white shadow-sm'
                            : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <ExplicacaoUnidadeVenda unidade={newProductData.saleUnit} divisivel={newProductData.isDivisible} className="mt-1.5" />
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground/70 pointer-events-none z-10">R$</span>
                    {/* Caixa registradora (dono, 26/10/2026), igual ao preço da edição:
                        começa em 0,00 e cada dígito entra pela direita (1 → 0,01; 100 → 1,00) */}
                    <InputDecimal
                      data-e2e="novo-produto-preco"
                      decimals={2}
                      value={newProductData.salePrice}
                      onChange={(v) => setNewProductData(prev => ({ ...prev, salePrice: v }))}
                      className="pl-10 pr-12 h-10 text-base text-left font-semibold border-border/70 focus:border-orange-500"
                    />
                    {newProductData.saleUnit !== "un" && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground pointer-events-none">/{newProductData.saleUnit}</span>
                    )}
                  </div>
                </section>
              )}

              {/* Pizza: "Personalização da Pizzaria" removida — tamanhos e bordas
                  agora vivem na aba Personalização do produto-mãe (order_steps pizza_*).
                  Lojista edita lá depois de criar a primeira pizza. */}

              {/* Extras — fora da timeline (oculto para Receita e Pizza) */}
              {newProductData.productType !== "receita" && newProductData.productType !== "pizza" && (
                <section className="relative pl-12">
                  <div className="absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/60 text-muted-foreground/70 ring-4 ring-background">
                    <Settings2 className="w-4 h-4" />
                  </div>
                  <div className="-mt-1">
                    <h3 className="text-sm font-semibold text-foreground leading-tight">Configurações extras</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Opcional — só ligue se precisar</p>
                  </div>
                  {/* Chavinhas no lugar dos cartões de marcar (dono, 26/10/2026) */}
                  <div className="mt-3 rounded-lg border border-border/70 divide-y divide-border/70">
                    {[
                      { chave: "isInsumo", id: "novo-produto-insumo", titulo: "Insumo", desc: "Não aparece no cardápio" },
                      { chave: "isDivisible", id: "novo-produto-divisivel", titulo: "Divisível",
                        desc: newProductData.saleUnit === "kg" ? "Vendido por peso" : newProductData.saleUnit === "L" ? "Vendido por volume" : "Vendido em frações" },
                      // Balança (26/10/2026): só em Kg; ligar já liga o Divisível
                      ...(newProductData.saleUnit === "kg" ? [{ chave: "usaBalanca", id: "novo-produto-balanca", titulo: "Pesar na balança", desc: "Peso direto da balança do computador" }] : []),
                    ].map(({ chave, id, titulo, desc }) => (
                      <div key={chave} className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <Label htmlFor={id} className="min-w-0 cursor-pointer">
                          <span className="block text-xs font-semibold leading-tight text-foreground">{titulo}</span>
                          <span className="block text-[10px] text-muted-foreground font-normal leading-tight mt-0.5">{desc}</span>
                        </Label>
                        <Switch
                          id={id}
                          checked={!!newProductData[chave]}
                          onCheckedChange={(v) => setNewProductData(prev => ({ ...prev, [chave]: v, ...(chave === "usaBalanca" && v ? { isDivisible: true } : {}) }))}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-3.5 border-t border-border/50 bg-muted/30 gap-2 sm:gap-3 flex-row sm:justify-between">
            <Button variant="ghost" onClick={() => setShowNewProductDialog(false)} className="text-muted-foreground hover:text-foreground font-semibold">
              Cancelar
            </Button>
            <Button
              data-e2e="novo-produto-criar"
              disabled={!newProductData.name.trim() || !newProductData.category}
              title={!newProductData.category ? "Escolha a categoria do produto" : undefined}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 font-semibold shadow-sm transition-all disabled:opacity-50"
              onClick={async () => {
                if (!newProductData.category) { toast.error("Escolha a categoria do produto"); return; }
                try {
                  setLoading(true);
                  const isPizza = newProductData.productType === "pizza";
                  // Pra pizza, o "preço" no products.sale_price é o MAIOR dos preços por tamanho
                  // (fallback compatível com qualquer outro componente do sistema que leia sale_price).
                  let computedSalePrice = newProductData.salePrice ? parseFloat(newProductData.salePrice) : null;
                  if (isPizza) {
                    const numericPrices = Object.values(pizzaPrices).map(parsePizzaPrice).filter(p => p > 0);
                    computedSalePrice = numericPrices.length > 0 ? Math.max(...numericPrices) : 0;
                  }
                  const productData = {
                    name: newProductData.name.trim(),
                    sale_price: computedSalePrice,
                    sale_unit: isPizza ? "un" : (newProductData.saleUnit || "un"),
                    category: newProductData.category || null,
                    product_type: newProductData.productType || "produto",
                    is_insumo: isPizza ? false : newProductData.isInsumo,
                    is_divisible: isPizza ? false : newProductData.isDivisible,
                    usa_balanca: !isPizza && newProductData.saleUnit === "kg" && !!newProductData.usaBalanca,
                    available: true,
                    recipe: [],
                    order_steps: [],
                  };
                  const res = await axios.post(`${API}/products`, productData, getAuthHeader());
                  const createdProduct = res.data;
                  // Pizza: auto-link no modelo independente. Pizza nova ganha
                  // suas 3 etapas locais: pizza_size (tamanhos usados no wizard),
                  // pizza_flavor (só ela como sabor, com prices_by_size), pizza_border (vazio).
                  // Lojista adiciona OUTROS sabores/bordas em Personalização depois.
                  if (isPizza && createdProduct?.id) {
                    const sizesPayload = (pizzaConfig.sizes || [])
                      .filter(s => !pizzaPricesExcluded.has(s.id))
                      .map(s => ({
                        id: s.id, name: s.name,
                        slices: s.slices, max_flavors: s.max_flavors,
                      }));
                    const pricesPayload = {};
                    Object.entries(pizzaPrices).forEach(([sid, v]) => {
                      if (pizzaPricesExcluded.has(sid)) return;
                      const n = parsePizzaPrice(v);
                      if (n > 0) pricesPayload[sid] = n;
                    });
                    try {
                      await axios.post(
                        `${API}/pizza/products/${createdProduct.id}/auto-link`,
                        { sizes: sizesPayload, prices_by_size: pricesPayload },
                        getAuthHeader(),
                      );
                    } catch (e) {
                      console.error("[Pizza] auto-link falhou:", e);
                      toast.error("Produto criado, mas falhou ao configurar Personalização");
                    }
                  }
                  toast.success(isPizza ? "Sabor de pizza criado!" : "Produto criado!");
                  if (!isPizza && newProductData.productType !== "receita" && ehUnidadePeso(newProductData.saleUnit) && !newProductData.isDivisible) {
                    toast.warning(AVISO_PESO_SEM_DIVISIVEL(newProductData.saleUnit), { duration: 7000 });
                  }
                  setShowNewProductDialog(false);
                  setPizzaPrices({});
                  setPizzaPricesExcluded(new Set());
                  fetchProducts();
                  fetchCategories();
                  if (createdProduct && createdProduct.id) {
                    handleEdit({ ...productData, ...createdProduct, recipe: [] });
                  }
                } catch (error) {
                  toast.error(mensagemDeErro(error, "Erro ao criar produto"));
                } finally {
                  setLoading(false);
                }
              }}
            >
              Criar Produto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog "Personalização Pizzas" — visão consolidada de tamanhos/bordas
          de todas as pizzas do tenant. Acionado pelo card especial na grid. */}
      <PizzaPersonalizationDialog
        open={pizzaPersonalizacaoOpen}
        onClose={() => setPizzaPersonalizacaoOpen(false)}
        products={products}
        onChanged={fetchProducts}
      />

    </div>
  );
}
